import { getQuestionnaireItems, LIKERT_FREQUENCY_OPTIONS as LIKERT_FREQUENCY_OPTIONS$1, QUESTIONNAIRE_URLS as QUESTIONNAIRE_URLS$1, STANDARD_QUESTIONNAIRES, getObservationCoding as getObservationCoding$1, getObservationDisplayByLoincCode, SURVEY_OBSERVATION_CATEGORY, QUERY_LIMIT_SINGLE, CARE_PLAN_CATEGORY_SYSTEM as CARE_PLAN_CATEGORY_SYSTEM$1, IDENTIFIER_SYSTEM, GP_ROLE_TYPE_EXTENSION, GP_ROLE_TYPES, COCM_CONSENT_EXTENSION_URL, VALUE_SET, isIntakeEncounterType, CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE as CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE$1, COCM_INTAKE_ENCOUNTER_TYPE_CODE as COCM_INTAKE_ENCOUNTER_TYPE_CODE$1, CARE_PLAN_ACTIVITY_SYSTEM as CARE_PLAN_ACTIVITY_SYSTEM$1, CONDITION_CATEGORY_SYSTEM, INTAKE_ENCOUNTER_CODES, ENCOUNTER_BATCH_SMALL_COUNT, FLAG_LOCAL_CODE_SYSTEM as FLAG_LOCAL_CODE_SYSTEM$1, GAS_ASSESSMENT_CODE, GOAL_CATEGORY_SYSTEM, PRACTITIONER_ROLE_CURRENT_USER_COUNT, PROGRAM_SELECTION_IDENTIFIER_SYSTEM, PROGRESS_LOG_CATEGORY_SYSTEM, getActivityAssignmentMode, getActivityAssignmentRoleCode, getActivityAssignmentGPIndex } from './constants.mjs';
import { scoreAdministeredQuestionnaire, getScoringRangeForTotal, buildScoreInterpretation } from './scoring.mjs';
import { OperationOutcomeError, isOperationOutcome, getStatus, formatHumanName, getReferenceString } from '@medplum/core';
import { localCalendarDateTodayISO } from './utils.mjs';

/**
 * Current observation LOINC codes for assessment scores
 * Used for creating new Observation resources from questionnaire responses
 *
 * IMPORTANT: These codes should ONLY be used for Observation resources that contain
 * calculated total scores. For questionnaire definitions, use QUESTIONNAIRE_LOINC_CODES
 * from questionnaire.ts which contains the panel codes.
 *
 * Validation: ✅ All codes verified against official LOINC database (2025-01-08)
 * See: packages/medplum/LOINC_CODE_MAPPINGS.md for detailed mapping documentation
 */ const OBSERVATION_LOINC_CODES = {
    /** ASRS Adult ADHD Self-Report Scale Total Score - Organizational code (no official LOINC) */ ASRS_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/asrs-total-score",
    /** AUDIT-C Total Score - LOINC 75626-2 ✅ VALIDATED */ AUDIT_C_TOTAL_SCORE: "75626-2",
    /** C-SSRS Columbia Suicide Severity Rating Scale Risk Level - Organizational code (no official LOINC) */ C_SSRS_SEVERITY: "https://benchmarkhealth.org/fhir/CodeSystem/c-ssrs-severity",
    /** GAD-7 Total Score - LOINC 70274-6 ✅ VALIDATED */ GAD7_TOTAL_SCORE: "70274-6",
    /** GAD-2 Total Score - Organizational code (the legacy LOINC 89204-7 is deprecated; the adaptive short check uses an org code) */ GAD2_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/gad-2-total-score",
    /** UCLA-3 Three-Item Loneliness Scale Total Score - Organizational code (no official LOINC) */ UCLA3_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/ucla-3-total-score",
    /** WSAS Work and Social Adjustment Scale Total Score - Organizational code (no official LOINC) */ WSAS_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/wsas-total-score",
    /** OCI-4 Obsessive-Compulsive Inventory Total Score - Organizational code (no official LOINC) */ OCI_4_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/oci-4-total-score",
    /** PC-PTSD-5 Total Score - LOINC 102017-1 ✅ VALIDATED */ PC_PTSD_5_TOTAL_SCORE: "102017-1",
    /** PSC-17 Pediatric Symptom Checklist Total Score - Organizational code (no official LOINC) */ PSC_17_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/psc-17-total-score",
    /** PHQ-9 Total Score - LOINC 44261-6 ✅ VALIDATED */ PHQ9_TOTAL_SCORE: "44261-6",
    /** PHQ-9A (Adolescent) Total Score - LOINC 89204-2 ✅ VALIDATED */ PHQ9A_TOTAL_SCORE: "89204-2",
    /** PHQ-4 Total Score - LOINC 70272-0 ✅ VALIDATED */ PHQ4_TOTAL_SCORE: "70272-0",
    /** SBIRT-ED Eating Disorder Screening Score - Organizational code (no official LOINC) */ SBIRT_ED_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/sbirt-ed-score",
    /** TAPS-1 Substance Use Screening Score - Organizational code (no official LOINC) */ TAPS_1_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/taps-1-score",
    /** TAPS-2 Substance Use Assessment Score - Organizational code (no official LOINC) */ TAPS_2_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/taps-2-score",
    /** WPAI Work Productivity and Activity Impairment Score - Organizational code (no official LOINC) */ WPAI_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/wpai-shp-score",
    /** WHO-5 Well-Being Index Total Score - Organizational code (no official LOINC) */ WHO5_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/who-5-total-score",
    /** Health Leads Social Needs Screening Toolkit Score - Organizational code (no official LOINC) */ SDOH_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/sdoh-health-leads-score",
    /** Mirah Complexity composite score - Mirah-proprietary (no official LOINC) */ MIRAH_COMPLEXITY_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-complexity-score",
    /** Mirah Severity composite score - Mirah-proprietary (no official LOINC) */ MIRAH_SEVERITY_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-severity-score",
    /** NPS 0–10 likelihood score — organizational code (no official LOINC) */ NPS_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/nps-score"
};
/**
 * Current observation types for assessment scores
 * FHIR R4-compliant observation types from LOINC
 * http://hl7.org/fhir/ValueSet/observation-codes
 * These are TOTAL SCORE codes for questionnaire observations
 * This is the UNIFIED source that replaces the previous duplicated constants
 */ const OBSERVATION_TYPES = [
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/asrs-total-score",
        display: "ASRS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "asrs",
        description: "Adult ADHD Self-Report Scale",
        minValue: 0,
        maxValue: 24
    },
    {
        code: "75626-2",
        display: "AUDIT-C",
        system: "http://loinc.org",
        fieldKey: "audit-c",
        description: "Alcohol Use Disorders Identification Test - Consumption",
        minValue: 0,
        maxValue: 12
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/gad-2-total-score",
        display: "GAD-2",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "gad-2",
        description: "Generalized Anxiety Disorder 2-item short check",
        minValue: 0,
        maxValue: 6
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/ucla-3-total-score",
        display: "UCLA-3",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "ucla-3",
        description: "Three-Item Loneliness Scale (UCLA)",
        minValue: 3,
        maxValue: 9
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/wsas-total-score",
        display: "WSAS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "wsas",
        description: "Work and Social Adjustment Scale",
        minValue: 0,
        maxValue: 40
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/c-ssrs-severity",
        display: "C-SSRS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "c-ssrs",
        description: "Columbia Suicide Severity Rating Scale",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "70274-6",
        display: "GAD-7",
        system: "http://loinc.org",
        fieldKey: "gad-7",
        description: "Generalized Anxiety Disorder-7",
        minValue: 0,
        maxValue: 21
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/oci-4-total-score",
        display: "OCI-4",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "oci-4",
        description: "Obsessive-Compulsive Inventory-4",
        minValue: 0,
        maxValue: 16
    },
    {
        code: "102017-1",
        display: "PC-PTSD-5",
        system: "http://loinc.org",
        fieldKey: "pc-ptsd-5",
        description: "Primary Care PTSD Screen for DSM-5",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/psc-17-total-score",
        display: "PSC-17",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "psc-17",
        description: "Pediatric Symptom Checklist-17",
        minValue: 0,
        maxValue: 34
    },
    {
        code: "44261-6",
        display: "PHQ-9",
        system: "http://loinc.org",
        fieldKey: "phq-9",
        description: "Patient Health Questionnaire-9",
        minValue: 0,
        maxValue: 27
    },
    {
        code: "89204-2",
        display: "PHQ-9A",
        system: "http://loinc.org",
        fieldKey: "phq-9a",
        description: "Patient Health Questionnaire-9 Modified for Teens",
        minValue: 0,
        maxValue: 27
    },
    {
        code: "70272-0",
        display: "PHQ-4",
        system: "http://loinc.org",
        fieldKey: "phq-4",
        description: "Patient Health Questionnaire-4",
        minValue: 0,
        maxValue: 12
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/prapare-risk-score",
        display: "PRAPARE",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "prapare",
        description: "Protocol for Responding to and Assessing Patients' Assets, Risks, and Experiences",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/sbirt-ed-score",
        display: "SBIRT-ED",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "sbirt-ed",
        description: "SBIRT Eating Disorder Screener",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/taps-1-score",
        display: "TAPS-1",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "taps-1",
        description: "TAPS Screener (Part 1)",
        minValue: 0,
        maxValue: 16
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/taps-2-score",
        display: "TAPS-2",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "taps-2",
        description: "TAPS Assessment (Part 2)",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/wpai-impairment-score",
        display: "WPAI",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "wpai",
        description: "Work Productivity and Activity Impairment",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/who-5-total-score",
        display: "WHO-5",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "who-5",
        description: "World Health Organization Well-Being Index",
        minValue: 0,
        maxValue: 25
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/sdoh-health-leads-score",
        display: "SDOH",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "sdoh",
        description: "Social Needs Screening Toolkit (Health Leads)",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-complexity-score",
        display: "Mirah Complexity",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "mirah-complexity",
        description: "Mirah Complexity Composite Score",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-severity-score",
        display: "Mirah Severity",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "mirah-severity",
        description: "Mirah Severity Composite Score",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/nps-score",
        display: "NPS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "nps",
        description: "Net Promoter Score (0–10)",
        minValue: 0,
        maxValue: 10
    }
];
// Select options for forms
OBSERVATION_TYPES.map((type)=>({
        value: type.code,
        label: type.display
    }));
/**
 * Get the complete observation coding for a given LOINC code
 * Returns undefined if the code is not valid for observations
 */ function getObservationCoding(code) {
    const observationType = OBSERVATION_TYPES.find((type)=>type.code === code);
    return observationType ? {
        system: observationType.system,
        code: observationType.code,
        display: observationType.display
    } : undefined;
}

/**
 * Common answer value sets for Likert-style questions
 */ const LIKERT_FREQUENCY_OPTIONS = [
    {
        value: 0,
        coding: {
            code: "LA6568-5",
            display: "Not at all"
        }
    },
    {
        value: 1,
        coding: {
            code: "LA6569-3",
            display: "Several days"
        }
    },
    {
        value: 2,
        coding: {
            code: "LA6570-1",
            display: "More than half the days"
        }
    },
    {
        value: 3,
        coding: {
            code: "LA6571-9",
            display: "Nearly every day"
        }
    }
];
 // Note: Validation type moved to questionnaire.ts to avoid circular dependency

/**
 * LOINC codes for questionnaire panels/forms (used for search/discovery and canonical URL construction)
 * These identify the questionnaire definition itself, NOT the scores/observations
 *
 * NOTE: These are used for:
 * 1. Searching/discovering questionnaires in FHIR servers
 * 2. Constructing canonical URLs for Questionnaire.url
 * 3. Initial questionnaire creation
 *
 * IMPORTANT: Once a questionnaire is fetched, prefer extracting LOINC codes
 * dynamically from the actual FHIR resource rather than using these static values.
 *
 * Only includes assessments with official LOINC questionnaire panel codes.
 * Questionnaires without official LOINC panel codes use organizational URLs.
 *
 * IMPORTANT: These codes should ONLY be used for Questionnaire and QuestionnaireResponse
 * resources. For total score observations, use OBSERVATION_LOINC_CODES from
 * observation.ts which contains the score codes.
 *
 * Validation: ✅ All codes verified against official LOINC database (2025-01-08)
 * See: packages/medplum/LOINC_CODE_MAPPINGS.md for detailed mapping documentation
 */ const QUESTIONNAIRE_LOINC_CODES = {
    /** ASRS Adult ADHD Self-Report Scale - LOINC 62750-5 ✅ VALIDATED */ ASRS: "62750-5",
    /** AUDIT-C Alcohol Screening Panel - LOINC 72109-2 ✅ VALIDATED */ AUDIT_C: "72109-2",
    /** C-SSRS Columbia Suicide Severity Rating Scale - LOINC 93245-9 ✅ VALIDATED */ C_SSRS: "93245-9",
    /** GAD-7 Anxiety Assessment Panel - LOINC 69737-5 ✅ VALIDATED */ GAD7: "69737-5",
    /** PC-PTSD-5 PTSD Screening Panel - LOINC 102010-6 ✅ VALIDATED */ PC_PTSD_5: "102010-6",
    /** PHQ-9 Depression Assessment Panel - LOINC 44249-1 ✅ VALIDATED */ PHQ9: "44249-1",
    /** PHQ-9A (Adolescent) Depression Assessment Panel - LOINC 89206-7 ✅ VALIDATED */ PHQ9A: "89206-7",
    /** PHQ-4 Combined Depression/Anxiety Panel - LOINC 69724-3 ✅ VALIDATED */ PHQ4: "69724-3",
    /** Health Leads Screening Panel (SDOH) - LOINC 99549-8 ✅ VALIDATED */ SDOH: "99549-8"
};
/**
 * Common questionnaire URLs used in FHIR Questionnaire.url
 * Uses official LOINC canonical URLs where available, organizational URLs otherwise
 */ const QUESTIONNAIRE_URLS = {
    ASRS: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.ASRS}`,
    C_SSRS: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.C_SSRS}`,
    GAD7: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.GAD7}`,
    OCI_4: "https://benchmarkhealth.org/fhir/Questionnaire/oci-4",
    PC_PTSD_5: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.PC_PTSD_5}`,
    PSC_17: "https://benchmarkhealth.org/fhir/Questionnaire/psc-17",
    PHQ9: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.PHQ9}`,
    PHQ9A: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.PHQ9A}`,
    PHQ4: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.PHQ4}`,
    SBIRT_ED: "https://benchmarkhealth.org/fhir/Questionnaire/sbirt-ed",
    SAFETY_PLAN: "https://benchmarkhealth.org/fhir/Questionnaire/safety-plan",
    TAPS_1: "https://benchmarkhealth.org/fhir/Questionnaire/taps-1",
    TAPS_2: "https://benchmarkhealth.org/fhir/Questionnaire/taps-2",
    WPAI: "https://benchmarkhealth.org/fhir/Questionnaire/wpai-functional",
    WHO5: "https://benchmarkhealth.org/fhir/Questionnaire/who-5",
    SDOH: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.SDOH}`,
    MIRAH_COMPLEXITY: "https://benchmarkhealth.org/fhir/Questionnaire/mirah-complexity",
    MIRAH_SEVERITY: "https://benchmarkhealth.org/fhir/Questionnaire/mirah-severity",
    NPS: "https://benchmarkhealth.org/fhir/Questionnaire/nps-member-feedback",
    DRUG_USE: "https://benchmarkhealth.org/fhir/Questionnaire/drug-use-screen",
    GAD2: "https://benchmarkhealth.org/fhir/Questionnaire/gad-2",
    AUDIT_C: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.AUDIT_C}`,
    UCLA3: "https://benchmarkhealth.org/fhir/Questionnaire/ucla-3",
    PGI_S: "https://benchmarkhealth.org/fhir/Questionnaire/pgi-s",
    PGI_C: "https://benchmarkhealth.org/fhir/Questionnaire/pgi-c",
    WSAS: "https://benchmarkhealth.org/fhir/Questionnaire/wsas"
};

/**
 * The full nine-section order (§8.2). Suicide-risk (3) and safety-plan (4) are
 * trigger-only and excluded from base composition; they are exported separately.
 */ const SECTION_SPECS = [
    {
        key: "patient-rating",
        order: 1,
        baseGating: "categorical",
        everyoneAsked: true,
        instruments: [
            "PGI_S"
        ]
    },
    {
        key: "depression",
        order: 2,
        baseGating: "always",
        everyoneAsked: true,
        instruments: [
            "PHQ9"
        ]
    },
    {
        key: "anxiety",
        order: 5,
        baseGating: "short-check",
        everyoneAsked: false,
        instruments: [
            "GAD2",
            "GAD7"
        ]
    },
    {
        key: "loneliness",
        order: 6,
        baseGating: "always",
        everyoneAsked: true,
        instruments: [
            "UCLA3"
        ]
    },
    {
        key: "alcohol",
        order: 7,
        baseGating: "short-check",
        everyoneAsked: false,
        instruments: [
            "AUDIT_C"
        ]
    },
    {
        key: "drug-use",
        order: 8,
        baseGating: "categorical",
        everyoneAsked: true,
        instruments: [
            "DRUG_USE"
        ]
    },
    {
        key: "functioning",
        order: 9,
        baseGating: "always",
        everyoneAsked: true,
        instruments: [
            "WSAS"
        ]
    }
];
/**
 * The trigger-added sections (PRD §8.4 / D9), in order, inserted live by the UI
 * directly after depression on a positive self-harm answer — never carried by
 * memory (suicide-risk is re-derived each visit, E6).
 */ const TRIGGER_ADDED_SECTIONS = [
    {
        key: "suicide-risk",
        order: 3,
        gating: "always",
        reasonCode: "triggered",
        reason: "Triggered now by the depression self-harm answer",
        instruments: [
            "C_SSRS"
        ]
    },
    {
        key: "safety-plan",
        order: 4,
        gating: "always",
        reasonCode: "triggered",
        reason: "Triggered now — always paired with suicide risk",
        instruments: [
            "SAFETY_PLAN"
        ]
    }
];
/**
 * Every instrument that can appear in an adaptive-screener sitting, in actual
 * administration order. The list is derived from the section definitions so
 * timeline/read-back surfaces cannot drift from screener composition. PGI-S
 * and PGI-C occupy the same final section and are mutually exclusive per visit.
 */ const ADAPTIVE_SCREENER_INSTRUMENT_ORDER = [
    ...SECTION_SPECS,
    ...TRIGGER_ADDED_SECTIONS
].sort((a, b)=>a.order - b.order).flatMap((section)=>section.key === "patient-rating" ? [
        "PGI_S",
        "PGI_C"
    ] : section.instruments);
/** Canonical URLs matching `ADAPTIVE_SCREENER_INSTRUMENT_ORDER`. */ const ADAPTIVE_SCREENER_QUESTIONNAIRE_ORDER = ADAPTIVE_SCREENER_INSTRUMENT_ORDER.map((instrument)=>QUESTIONNAIRE_URLS[instrument]);
const ADAPTIVE_SCREENER_ORDER_BY_URL = new Map(ADAPTIVE_SCREENER_QUESTIONNAIRE_ORDER.map((url, index)=>[
        url,
        index
    ]));
/**
 * Return adaptive-screener result-like items in administration order without
 * mutating the input. Unknown URLs remain visible after known instruments and
 * preserve their incoming order.
 */ const sortByAdaptiveScreenerAdministrationOrder = (items)=>items.map((item, index)=>({
            item,
            index,
            order: ADAPTIVE_SCREENER_ORDER_BY_URL.get(item.questionnaireUrl ?? "") ?? Number.MAX_SAFE_INTEGER
        })).sort((a, b)=>a.order - b.order || a.index - b.index).map(({ item })=>item);
const REASON_TEXT = {
    everyone: "Asked of everyone",
    "short-check": "Short check first — opens the full set only if positive",
    "opened-at-baseline": "Opened at an earlier visit — asked in full",
    triggered: "Triggered now"
};
/**
 * Compose the ordered adaptive-screener section list for a visit. Pure: the
 * caller supplies eligibility, baseline status, memory, and the enabled set.
 * @agentic @benchmark/core
 */ function composeAdaptiveScreener(ctx) {
    // Adults-only fail-safe (D14): never compose for an unconfirmed adult.
    if (!ctx.isConfirmedAdult) {
        return {
            eligible: false,
            sections: []
        };
    }
    const openedBefore = ctx.openedInFullBefore ?? new Set();
    const enabled = ctx.enabledSections;
    const sections = [];
    for (const spec of SECTION_SPECS){
        // Degraded composition (D15): a withheld section is omitted for everyone.
        if (enabled && !enabled.has(spec.key)) {
            continue;
        }
        // Patient rating: change (PGI-C) only once a PGI-S severity anchor exists;
        // otherwise severity (PGI-S), including when a prior PGI-S was declined.
        let instruments = spec.instruments;
        if (spec.key === "patient-rating") {
            instruments = ctx.ratingAnchorEstablished ? [
                "PGI_C"
            ] : [
                "PGI_S"
            ];
        }
        // Memory (E2/E3): a short-check section opened in full before is asked in
        // full now, regardless of what today's short check would say. Memory only
        // adds — it never removes a section (E4).
        let gating = spec.baseGating;
        let reasonCode = spec.everyoneAsked ? "everyone" : "short-check";
        if (spec.baseGating === "short-check" && openedBefore.has(spec.key)) {
            gating = "full-by-memory";
            reasonCode = "opened-at-baseline";
        }
        sections.push({
            key: spec.key,
            order: spec.order,
            gating,
            reasonCode,
            reason: REASON_TEXT[reasonCode],
            instruments
        });
    }
    return {
        eligible: true,
        sections
    };
}
/**
 * Reconstruct the original section list for draft resume/finalized edit.
 *
 * Session reason maps are broader than actual administration (they predeclare
 * GAD-7 and trigger reasons), so responses/settlements and the current PHQ-9
 * trigger decide which conditional instruments really belonged to the sitting.
 *
 * @agentic @benchmark/core
 */ function composePersistedAdaptiveSession(ctx) {
    const urlForKey = (key)=>QUESTIONNAIRE_URLS[key];
    const sections = [];
    for (const spec of SECTION_SPECS){
        let instruments = spec.instruments;
        let reasonCode;
        let gating = spec.baseGating;
        if (spec.key === "patient-rating") {
            const usePgic = ctx.reasons[QUESTIONNAIRE_URLS.PGI_C] !== undefined || ctx.administeredUrls.has(QUESTIONNAIRE_URLS.PGI_C);
            instruments = usePgic ? [
                "PGI_C"
            ] : [
                "PGI_S"
            ];
        }
        if (spec.key === "anxiety") {
            const gad7Reason = ctx.reasons[QUESTIONNAIRE_URLS.GAD7];
            const fullByMemory = gad7Reason === "opened-at-baseline";
            if (fullByMemory) {
                gating = "full-by-memory";
                instruments = [
                    "GAD7"
                ];
                reasonCode = gad7Reason;
            } else {
                instruments = [
                    "GAD2",
                    "GAD7"
                ];
                reasonCode = ctx.reasons[QUESTIONNAIRE_URLS.GAD2] ?? gad7Reason;
            }
        }
        reasonCode ??= instruments.map(urlForKey).map((url)=>ctx.reasons[url]).find((reason)=>reason !== undefined);
        const represented = reasonCode !== undefined || instruments.map(urlForKey).some((url)=>ctx.administeredUrls.has(url));
        if (!represented) continue;
        const resolvedReason = reasonCode ?? (spec.everyoneAsked ? "everyone" : gating === "full-by-memory" ? "opened-at-baseline" : "short-check");
        sections.push({
            key: spec.key,
            order: spec.order,
            gating,
            reasonCode: resolvedReason,
            reason: REASON_TEXT[resolvedReason],
            instruments
        });
    }
    const includeTriggers = ctx.selfHarmPositive || ctx.administeredUrls.has(QUESTIONNAIRE_URLS.C_SSRS) || ctx.administeredUrls.has(QUESTIONNAIRE_URLS.SAFETY_PLAN);
    if (includeTriggers) {
        const depIndex = sections.findIndex((section)=>section.key === "depression");
        const triggers = TRIGGER_ADDED_SECTIONS.map((section)=>({
                ...section,
                reasonCode: ctx.reasons[urlForKey(section.instruments[0])] ?? section.reasonCode
            }));
        sections.splice(depIndex >= 0 ? depIndex + 1 : sections.length, 0, ...triggers);
    }
    return {
        eligible: true,
        sections
    };
}
/**
 * Insert the trigger-added suicide-risk + safety-plan sections directly after
 * depression when the depression self-harm answer is positive (PRD §8.4 / D9).
 * The two always arrive together, in that order. A negative/absent answer (or a
 * list already carrying them) returns the input unchanged. Suicide risk is never
 * carried by memory — the caller re-derives `selfHarmPositive` each visit (E6).
 *
 * @agentic @benchmark/core
 */ function insertTriggeredSections(sections, opts) {
    if (!opts.selfHarmPositive) {
        return sections;
    }
    if (sections.some((s)=>s.key === "suicide-risk")) {
        return sections;
    }
    const depIndex = sections.findIndex((s)=>s.key === "depression");
    if (depIndex === -1) {
        return [
            ...sections,
            ...TRIGGER_ADDED_SECTIONS
        ];
    }
    return [
        ...sections.slice(0, depIndex + 1),
        ...TRIGGER_ADDED_SECTIONS,
        ...sections.slice(depIndex + 1)
    ];
}

const flattenItems = (items)=>items.flatMap((item)=>item.type === "group" && item.item ? [
            item,
            ...flattenItems(item.item)
        ] : [
            item
        ]);
const answered = (answer)=>answer.value !== undefined || answer.valueString !== undefined && answer.valueString.length > 0;
/**
 * Evaluate completion against the current canonical questionnaire definition.
 * Gated-out required items are excluded when their gate parent is answered 0.
 *
 * @agentic @benchmark/core
 */ const evaluateQuestionnaireCompletion = (questionnaireUrl, answers, options = {})=>{
    const items = getQuestionnaireItems(questionnaireUrl);
    if (!items) {
        return {
            supported: false,
            reason: "unknown-questionnaire"
        };
    }
    const flattened = flattenItems(items);
    const itemByLinkId = new Map(flattened.map((item)=>[
            item.linkId,
            item
        ]));
    for (const answer of answers){
        if (!itemByLinkId.has(answer.linkId)) {
            return {
                supported: false,
                reason: "unknown-link-id"
            };
        }
        if (!answered(answer) && answer.coding !== undefined) {
            return {
                supported: false,
                reason: "unsupported-answer"
            };
        }
    }
    const valueByLinkId = new Map(answers.map((answer)=>[
            answer.linkId,
            answer.value
        ]));
    const gateForByLinkId = new Map();
    for (const item of flattened){
        if (item.gateFor) {
            gateForByLinkId.set(item.linkId, item.gateFor);
        }
    }
    const hidden = new Set();
    for (const item of flattened){
        if (!item.gateFor || valueByLinkId.get(item.linkId) !== 0) {
            continue;
        }
        const queue = [
            ...item.gateFor
        ];
        while(queue.length > 0){
            const linkId = queue.shift();
            if (hidden.has(linkId)) {
                continue;
            }
            hidden.add(linkId);
            queue.push(...gateForByLinkId.get(linkId) ?? []);
        }
    }
    const answerByLinkId = new Map(answers.filter(answered).map((answer)=>[
            answer.linkId,
            answer
        ]));
    const missingRequired = flattened.filter((item)=>item.type !== "group" && item.required !== false && !hidden.has(item.linkId) && !answerByLinkId.has(item.linkId)).map((item)=>item.linkId);
    const activeRequiredCount = flattened.filter((item)=>item.type !== "group" && item.required !== false && !hidden.has(item.linkId)).length;
    return {
        supported: true,
        complete: missingRequired.length === 0 && (activeRequiredCount > 0 || options.declaredComplete === true),
        missingRequired
    };
};

/** True once a section has reached any terminal (non-pending) status. */ const isSectionSettled = (status)=>status !== "pending";
/**
 * Read-back labels for declined sections on chart-review surfaces (Assessments
 * tab, patient timeline, clipboard copy), keyed by the persisted settlement.
 * @agentic @benchmark/core
 */ const ADAPTIVE_SETTLEMENT_READBACK_LABELS = {
    "declined-section": "Declined",
    "declined-remaining": "Declined the rest",
    "not-administered-clinical": "Not clinically appropriate"
};
/**
 * Whether a status is an explicit advocate/patient decision — a patient decline
 * or the advocate's not-clinically-appropriate judgement — as opposed to the
 * `answered` status (re-derived from responses) or the transient `pending`
 * default. Only these survive the rail remounting (e.g. toggling the screener
 * off and back on) and must be restored as settlements; `answered` and `pending`
 * are always derived from the live responses, never rehydrated as a settlement,
 * so a not-yet-settled section can always still be declined after a remount.
 */ const isExplicitSettlement = (status)=>status === "declined-remaining" || status === "declined-section" || status === "not-administered-clinical";
/**
 * The patient-facing decline offered for a section (§8.6):
 * - `declined-remaining` only once at least one answer exists (answers kept, no
 *   score);
 * - `declined-section` only when nothing has been answered.
 */ const patientDeclineOption = (hasAnyAnswer)=>hasAnyAnswer ? "declined-remaining" : "declined-section";
/** Published GAD-2 two-item cut-point: ≥3 opens the full GAD-7. */ const ANXIETY_SHORT_CHECK_CUTOFF = 3;
/** Whether a GAD-2 short-check score opens the full anxiety set now. */ const shouldRevealAnxietyFullSet = (gad2Score)=>gad2Score >= ANXIETY_SHORT_CHECK_CUTOFF;
/**
 * Whether the full GAD-7 should render. A positive GAD-2 opens it live; an
 * existing GAD-7 answer re-opens it on draft resume when GAD-2 was not
 * persisted (adaptive-only short check).
 */ const shouldRevealAnxietyFullSetFromState = (input)=>shouldRevealAnxietyFullSet(input.gad2Score) || input.hasGad7Responses;
/**
 * Whether the depression self-harm answer is positive (any answer above the
 * lowest option on PHQ-9 item 9), which triggers the suicide-risk + safety-plan
 * sections (§8.4). Re-derived from today's answer each visit (E6).
 */ const isSelfHarmPositive = (phq9Responses)=>{
    const item9 = phq9Responses.find((r)=>r.linkId === "phq9-9");
    return item9?.value !== undefined && item9.value > 0;
};
/**
 * Whether the "turn the adaptive screener off" toggle must be locked so a
 * suicide-risk obligation can never be bypassed by switching to the classic
 * picker (D9/D13). A positive PHQ-9 self-harm answer triggers C-SSRS, so the
 * toggle stays locked until C-SSRS is completed; a positive C-SSRS additionally
 * requires acknowledgement. A completed, negative C-SSRS resolves it.
 *
 * @agentic @benchmark/core
 */ const isScreenerOffLocked = (input)=>input.selfHarmPositive && !input.suicideRiskCompleted || input.suicideRiskPositive && !input.suicideRiskAcknowledged;
/**
 * Whether the screening may be recorded (C6 + D13): there is at least one
 * section, every section has settled, and — if a suicide-risk screen is
 * positive — the advocate has acknowledged the escalate-now notice. A positive
 * suicide-risk screen can never be closed with the risk unacknowledged.
 *
 * @agentic @benchmark/core
 */ const canRecordScreening = (input)=>{
    if (input.statuses.length === 0) {
        return false;
    }
    if (!input.statuses.every(isSectionSettled)) {
        return false;
    }
    if (input.suicideRiskPositive && !input.suicideRiskAcknowledged) {
        return false;
    }
    return true;
};
const questionnaireUrl = (key)=>QUESTIONNAIRE_URLS[key];
const responseIsComplete = (responses, url)=>{
    const response = responses[url];
    if (!response) return false;
    const completion = evaluateQuestionnaireCompletion(url, response.responses, {
        declaredComplete: response.completed
    });
    return completion.supported && completion.complete;
};
/**
 * Recompute whether an adaptive sitting may switch to the classic flow.
 *
 * A positive PHQ-9 self-harm answer keeps the adaptive safety obligation active
 * until C-SSRS is complete or explicitly settled, and any positive C-SSRS
 * answers are durably acknowledged.
 *
 * @agentic @benchmark/core
 */ const isAdaptiveSafetyObligationSatisfied = (input)=>{
    const responses = input.questionnaireResponses ?? {};
    const selfHarmPositive = isSelfHarmPositive(responses[QUESTIONNAIRE_URLS.PHQ9]?.responses ?? []);
    const suicideRiskPositive = selfHarmPositive && (responses[QUESTIONNAIRE_URLS.C_SSRS]?.responses ?? []).some((response)=>(response.value ?? 0) > 0);
    const suicideRiskSettlement = input.settlements?.[QUESTIONNAIRE_URLS.C_SSRS];
    const suicideRiskSettled = suicideRiskSettlement !== undefined && isExplicitSettlement(suicideRiskSettlement) && input.reasons[QUESTIONNAIRE_URLS.C_SSRS] !== undefined || responseIsComplete(responses, QUESTIONNAIRE_URLS.C_SSRS);
    return !isScreenerOffLocked({
        selfHarmPositive,
        suicideRiskCompleted: suicideRiskSettled,
        suicideRiskPositive,
        suicideRiskAcknowledged: input.suicideRiskAcknowledged
    });
};
/**
 * Recompute adaptive record eligibility from clinical form state.
 *
 * The persistence boundary must call this instead of trusting the UI's cached
 * `_adaptiveCanRecord` flag. Explicit decline/judgement settlements remain
 * valid user decisions; an `answered` settlement is re-derived from canonical
 * questionnaire completion so it cannot authorize missing answers.
 *
 * @agentic @benchmark/core
 */ const canRecordAdaptiveScreener = (input)=>{
    const responses = input.questionnaireResponses ?? {};
    const settlements = input.settlements ?? {};
    const selfHarmPositive = isSelfHarmPositive(responses[QUESTIONNAIRE_URLS.PHQ9]?.responses ?? []);
    const administeredUrls = new Set([
        ...Object.keys(responses),
        ...Object.keys(settlements)
    ]);
    const sections = composePersistedAdaptiveSession({
        reasons: input.reasons,
        administeredUrls,
        selfHarmPositive
    }).sections;
    const gad2Score = (responses[QUESTIONNAIRE_URLS.GAD2]?.responses ?? []).reduce((total, response)=>total + (response.value ?? 0), 0);
    const hasGad7Responses = (responses[QUESTIONNAIRE_URLS.GAD7]?.responses ?? []).some((response)=>response.value !== undefined);
    const revealAnxietyFull = shouldRevealAnxietyFullSetFromState({
        gad2Score,
        hasGad7Responses
    });
    const administeredForSection = (section)=>{
        if (section.key !== "anxiety") {
            return [
                questionnaireUrl(section.instruments[0])
            ];
        }
        if (section.gating === "full-by-memory") {
            return [
                QUESTIONNAIRE_URLS.GAD7
            ];
        }
        return revealAnxietyFull ? [
            QUESTIONNAIRE_URLS.GAD2,
            QUESTIONNAIRE_URLS.GAD7
        ] : [
            QUESTIONNAIRE_URLS.GAD2
        ];
    };
    const ignoredQuestionnaireUrls = input.ignoredQuestionnaireUrls ?? new Set();
    const blockedQuestionnaireUrls = input.blockedQuestionnaireUrls ?? new Set();
    const statuses = sections.flatMap((section)=>{
        const administeredUrls = administeredForSection(section);
        const requiredUrls = section.key === "suicide-risk" ? administeredUrls : administeredUrls.filter((url)=>!ignoredQuestionnaireUrls.has(url));
        if (requiredUrls.length === 0) return [];
        const hasQuarantinedSuicideRisk = section.key === "suicide-risk" && requiredUrls.some((url)=>ignoredQuestionnaireUrls.has(url));
        const sectionSettled = !hasQuarantinedSuicideRisk && requiredUrls.every((url)=>{
            if (blockedQuestionnaireUrls.has(url)) return false;
            const settlement = settlements[url];
            return settlement !== undefined && isExplicitSettlement(settlement) && input.reasons[url] !== undefined || responseIsComplete(responses, url);
        });
        return [
            sectionSettled ? "answered" : "pending"
        ];
    });
    const suicideRiskPositive = selfHarmPositive && (responses[QUESTIONNAIRE_URLS.C_SSRS]?.responses ?? []).some((response)=>(response.value ?? 0) > 0);
    if (sections.length > 0 && statuses.length === 0) {
        return !suicideRiskPositive || input.suicideRiskAcknowledged;
    }
    return canRecordScreening({
        statuses,
        suicideRiskPositive,
        suicideRiskAcknowledged: input.suicideRiskAcknowledged
    });
};
/**
 * Why a screening cannot yet be recorded — a single reason for the UI to
 * surface, in priority order (unsettled sections first, then the acknowledgement
 * gate). Returns null when it may be recorded.
 *
 * @agentic @benchmark/core
 */ const recordBlockedReason = (input)=>{
    if (input.statuses.length === 0) {
        return "no-sections";
    }
    if (!input.statuses.every(isSectionSettled)) {
        return "unsettled-sections";
    }
    if (input.suicideRiskPositive && !input.suicideRiskAcknowledged) {
        return "suicide-risk-unacknowledged";
    }
    return null;
};

/** `meta.tag` system that marks a QR as part of an adaptive-screener sitting. */ const ADAPTIVE_SCREENER_SESSION_SYSTEM = "https://benchmarkhealth.org/fhir/CodeSystem/adaptive-screener-session";
/** Extension url carrying why the section was asked (F7). */ const ADAPTIVE_SCREENER_REASON_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-reason";
/** Extension url carrying whether the sitting was the patient's baseline. */ const ADAPTIVE_SCREENER_BASELINE_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-baseline";
/** Extension url carrying how the section settled — answered vs a decline kind (D8). */ const ADAPTIVE_SCREENER_SETTLEMENT_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-settlement";
/**
 * Extension url carrying the link ids the patient explicitly skipped (asked and
 * not answered — PRD §8.4). Skips are deliberately persisted as an extension,
 * never as answerless `item` entries: item presence would read as "content" to
 * cross-visit memory (flipping a later administration to follow-up against
 * D12) and would complicate every raw-item consumer, while an extension is
 * invisible to memory, completion, and scoring.
 */ const ADAPTIVE_SCREENER_SKIPPED_ITEMS_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-skipped-items";
/**
 * Extension url freezing the administration's composition inputs at the first
 * persisted write: which short-check sections were opened-in-full by memory
 * and whether the rating anchor was established. Without this, every request
 * re-derives composition from the patient's *current* prior responses, so a
 * prior sitting completing mid-administration would flip the composed section
 * list (and kind) between two saves of the same sitting. The marker makes the
 * first write's derivation authoritative for the administration's lifetime.
 */ const ADAPTIVE_SCREENER_COMPOSITION_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-composition";
/** Extension urls capturing the suicide-risk acknowledgement author + time (D13). */ const ADAPTIVE_SCREENER_ACK_AT_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-acknowledged-at";
const ADAPTIVE_SCREENER_ACK_BY_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-acknowledged-by";
/**
 * Return a copy of the QR tagged as part of an adaptive-screener sitting: a
 * `meta.tag` with the shared session id, plus extensions for the baseline flag
 * and the reason the section was asked. Existing tags/extensions are preserved.
 *
 * @agentic @benchmark/core
 */ const tagAdaptiveScreenerResponse = (qr, marker)=>{
    const existingTags = qr.meta?.tag ?? [];
    const managedExtensionUrls = new Set([
        ADAPTIVE_SCREENER_REASON_EXTENSION,
        ADAPTIVE_SCREENER_BASELINE_EXTENSION,
        ADAPTIVE_SCREENER_SETTLEMENT_EXTENSION,
        ADAPTIVE_SCREENER_SKIPPED_ITEMS_EXTENSION,
        ADAPTIVE_SCREENER_COMPOSITION_EXTENSION,
        ADAPTIVE_SCREENER_ACK_AT_EXTENSION,
        ADAPTIVE_SCREENER_ACK_BY_EXTENSION
    ]);
    const existingExtensions = (qr.extension ?? []).filter((ext)=>!managedExtensionUrls.has(ext.url));
    return {
        ...qr,
        meta: {
            ...qr.meta,
            tag: [
                ...existingTags.filter((tag)=>tag.system !== ADAPTIVE_SCREENER_SESSION_SYSTEM),
                {
                    system: ADAPTIVE_SCREENER_SESSION_SYSTEM,
                    code: marker.sessionId
                }
            ]
        },
        extension: [
            ...existingExtensions,
            {
                url: ADAPTIVE_SCREENER_BASELINE_EXTENSION,
                valueBoolean: marker.baseline
            },
            ...marker.reasonCode ? [
                {
                    url: ADAPTIVE_SCREENER_REASON_EXTENSION,
                    valueCode: marker.reasonCode
                }
            ] : [],
            ...marker.settlement ? [
                {
                    url: ADAPTIVE_SCREENER_SETTLEMENT_EXTENSION,
                    valueCode: marker.settlement
                }
            ] : [],
            ...marker.skippedLinkIds && marker.skippedLinkIds.length > 0 ? [
                {
                    url: ADAPTIVE_SCREENER_SKIPPED_ITEMS_EXTENSION,
                    // Link ids never contain whitespace, so a space-joined list keeps
                    // the extension a single human-readable valueString.
                    valueString: [
                        ...marker.skippedLinkIds
                    ].join(" ")
                }
            ] : [],
            ...marker.composition ? [
                {
                    url: ADAPTIVE_SCREENER_COMPOSITION_EXTENSION,
                    valueString: JSON.stringify({
                        openedInFullBefore: [
                            ...marker.composition.openedInFullBefore
                        ],
                        ratingAnchorEstablished: marker.composition.ratingAnchorEstablished
                    })
                }
            ] : [],
            ...marker.acknowledgedAt ? [
                {
                    url: ADAPTIVE_SCREENER_ACK_AT_EXTENSION,
                    valueDateTime: marker.acknowledgedAt
                }
            ] : [],
            ...marker.acknowledgedBy ? [
                {
                    url: ADAPTIVE_SCREENER_ACK_BY_EXTENSION,
                    valueString: marker.acknowledgedBy
                }
            ] : []
        ]
    };
};
/**
 * Read the adaptive-screener marker off a QR, or null when the QR was not part
 * of an adaptive-screener sitting.
 *
 * @agentic @benchmark/core
 */ /**
 * Parse the frozen-composition extension fail-soft: a corrupt or unexpected
 * payload yields `undefined` (the caller re-derives from prior responses)
 * rather than throwing on a persisted-value read.
 */ const parseComposition = (raw)=>{
    if (!raw) return undefined;
    try {
        const parsed = JSON.parse(raw);
        if (typeof parsed !== "object" || parsed === null) return undefined;
        const candidate = parsed;
        if (!Array.isArray(candidate.openedInFullBefore) || !candidate.openedInFullBefore.every((key)=>typeof key === "string") || typeof candidate.ratingAnchorEstablished !== "boolean") {
            return undefined;
        }
        return {
            openedInFullBefore: candidate.openedInFullBefore,
            ratingAnchorEstablished: candidate.ratingAnchorEstablished
        };
    } catch  {
        return undefined;
    }
};
const readAdaptiveScreenerMarker = (qr)=>{
    const sessionTag = (qr.meta?.tag ?? []).find((tag)=>tag.system === ADAPTIVE_SCREENER_SESSION_SYSTEM);
    if (!sessionTag?.code) {
        return null;
    }
    const findExt = (url)=>(qr.extension ?? []).find((ext)=>ext.url === url);
    const skippedRaw = findExt(ADAPTIVE_SCREENER_SKIPPED_ITEMS_EXTENSION)?.valueString;
    const skippedLinkIds = skippedRaw ? skippedRaw.split(/\s+/).filter((id)=>id.length > 0) : undefined;
    const composition = parseComposition(findExt(ADAPTIVE_SCREENER_COMPOSITION_EXTENSION)?.valueString);
    return {
        sessionId: sessionTag.code,
        baseline: findExt(ADAPTIVE_SCREENER_BASELINE_EXTENSION)?.valueBoolean === true,
        reasonCode: findExt(ADAPTIVE_SCREENER_REASON_EXTENSION)?.valueCode,
        settlement: findExt(ADAPTIVE_SCREENER_SETTLEMENT_EXTENSION)?.valueCode,
        ...skippedLinkIds && skippedLinkIds.length > 0 ? {
            skippedLinkIds
        } : {},
        ...composition ? {
            composition
        } : {},
        acknowledgedAt: findExt(ADAPTIVE_SCREENER_ACK_AT_EXTENSION)?.valueDateTime,
        acknowledgedBy: findExt(ADAPTIVE_SCREENER_ACK_BY_EXTENSION)?.valueString
    };
};
/** A persisted Encounter contains contradictory adaptive-session markers. */ class AdaptiveScreenerIntegrityError extends Error {
    constructor(message){
        super(message);
        this.name = "AdaptiveScreenerIntegrityError";
    }
}
/**
 * Restore durable adaptive form state from encounter-linked QRs.
 *
 * One encounter edit may own at most one adaptive sitting. Conflicting markers
 * fail closed rather than selecting an arbitrary session and rewriting clinical
 * provenance.
 *
 * @agentic @benchmark/core
 */ const restoreAdaptiveScreenerState = (responses)=>{
    let sessionId;
    let baseline;
    let acknowledgedAt;
    let acknowledgedBy;
    const reasons = {};
    const settlements = {};
    const seenUrls = new Set();
    for (const response of responses){
        const marker = readAdaptiveScreenerMarker(response);
        if (!marker) continue;
        const url = response.questionnaire;
        if (!url) {
            throw new AdaptiveScreenerIntegrityError("An adaptive response is missing its questionnaire canonical.");
        }
        if (seenUrls.has(url)) {
            throw new AdaptiveScreenerIntegrityError("Multiple adaptive responses exist for the same questionnaire.");
        }
        seenUrls.add(url);
        if (sessionId !== undefined && sessionId !== marker.sessionId) {
            throw new AdaptiveScreenerIntegrityError("Multiple adaptive screening sessions are linked to this encounter.");
        }
        if (baseline !== undefined && baseline !== marker.baseline) {
            throw new AdaptiveScreenerIntegrityError("Adaptive screening baseline markers conflict.");
        }
        sessionId = marker.sessionId;
        baseline = marker.baseline;
        if (marker.reasonCode) {
            reasons[url] = marker.reasonCode;
        }
        if (marker.settlement) {
            settlements[url] = marker.settlement;
        }
        if (acknowledgedAt !== undefined && marker.acknowledgedAt !== undefined && acknowledgedAt !== marker.acknowledgedAt) {
            throw new AdaptiveScreenerIntegrityError("Adaptive screening acknowledgement timestamps conflict.");
        }
        if (acknowledgedBy !== undefined && marker.acknowledgedBy !== undefined && acknowledgedBy !== marker.acknowledgedBy) {
            throw new AdaptiveScreenerIntegrityError("Adaptive screening acknowledgement authors conflict.");
        }
        acknowledgedAt ??= marker.acknowledgedAt;
        acknowledgedBy ??= marker.acknowledgedBy;
    }
    if (!sessionId || baseline === undefined) {
        return null;
    }
    return {
        session: {
            id: sessionId,
            baseline,
            reasons
        },
        settlements,
        suicideRiskAcknowledged: Boolean(acknowledgedAt || acknowledgedBy),
        suicideRiskAcknowledgedAt: acknowledgedAt,
        suicideRiskAcknowledgedBy: acknowledgedBy
    };
};
const LIKERT_CODE_TO_WEIGHT = new Map(LIKERT_FREQUENCY_OPTIONS$1.map((option)=>[
        option.coding.code,
        option.value
    ]));
/**
 * Numeric value of a QR item's first answer, decoding every shape a screener
 * answer is persisted in: an explicit `valueInteger` weight, a shared-Likert
 * `valueCoding` (LOINC LA codes), or a numeric-string option code (the
 * canonical fallback questionnaires). Clinician- and patient-authored
 * responses mix these shapes, and a `valueInteger`-only read silently treats a
 * coded answer as unanswered — corrupting memory and history derivation.
 *
 * @agentic @benchmark/core
 */ const numericAnswerValue = (item)=>{
    const answer = item?.answer?.[0];
    if (answer?.valueInteger !== undefined) {
        return answer.valueInteger;
    }
    const code = answer?.valueCoding?.code;
    if (code === undefined) return undefined;
    const weight = LIKERT_CODE_TO_WEIGHT.get(code);
    if (weight !== undefined) return weight;
    const parsed = Number(code);
    return Number.isFinite(parsed) ? parsed : undefined;
};
const answeredValue = (qr, linkId)=>numericAnswerValue((qr.item ?? []).find((item)=>item.linkId === linkId));
/**
 * Whether an AUDIT-C sitting opened the full section. A positive first item
 * (any drinking) opens items 2–3 via the instrument's internal gate — so the
 * section counts as opened even if the patient then declined the rest (E2/E3);
 * items 2–3 being present is also sufficient.
 */ const auditCOpenedFull = (qr)=>{
    const firstAnswer = answeredValue(qr, "audit-c-1");
    if (firstAnswer !== undefined && firstAnswer > 0) {
        return true;
    }
    return (qr.item ?? []).some((item)=>item.linkId === "audit-c-2" || item.linkId === "audit-c-3");
};
/**
 * Derive cross-visit memory (E1–E3) for the current visit from the patient's
 * PRIOR adaptive-screener QuestionnaireResponses:
 * - `isBaseline`: true when no prior sitting had any answered content (E1).
 * - `openedInFullBefore`: short-check sections whose full set was administered
 *   at any earlier visit — anxiety once GAD-7 was administered, alcohol once
 *   AUDIT-C opened its full set (E2/E3). Memory only adds (E4).
 * - `ratingAnchorEstablished`: a prior PGI-S severity rating exists, so the
 *   follow-up asks change (PGI-C) rather than severity (PGI-S). A declined PGI-S
 *   leaves no anchor, so PGI-S is asked again (§8.8).
 *
 * The caller passes only prior (already-persisted) responses; the current
 * in-progress sitting is excluded.
 *
 * @agentic @benchmark/core
 */ const deriveScreenerMemoryFromResponses = (priorResponses)=>{
    let anyContent = false;
    let ratingAnchorEstablished = false;
    const openedInFullBefore = new Set();
    for (const qr of priorResponses){
        if (!readAdaptiveScreenerMarker(qr)) {
            continue;
        }
        // Answered content only (E1's documented intent): an item is content when
        // it carries an answer. Item presence alone must not count — an all-skipped
        // administration does not establish a baseline (D12), and skips are
        // persisted as an extension rather than items precisely so a prior sitting
        // of skips (or a legacy answerless item) never flips kind resolution.
        if ((qr.item ?? []).some((item)=>(item.answer?.length ?? 0) > 0)) {
            anyContent = true;
        }
        if (qr.questionnaire === QUESTIONNAIRE_URLS$1.GAD7) {
            openedInFullBefore.add("anxiety");
        }
        if (qr.questionnaire === QUESTIONNAIRE_URLS$1.AUDIT_C && auditCOpenedFull(qr)) {
            openedInFullBefore.add("alcohol");
        }
        if (qr.questionnaire === QUESTIONNAIRE_URLS$1.PGI_S && (qr.item ?? []).some((item)=>numericAnswerValue(item) !== undefined)) {
            ratingAnchorEstablished = true;
        }
    }
    return {
        isBaseline: !anyContent,
        openedInFullBefore,
        ratingAnchorEstablished
    };
};

/**
 * Distinguishes the two Observations the screener writes under the same session
 * + score-code key. The completion (billing) score and the self-harm crisis
 * marker must NOT collide.
 *
 * @agentic @benchmark/core
 */ const SCREENER_OBSERVATION_ROLE_SYSTEM = "https://benchmarkhealth.com/fhir/CodeSystem/screener-observation-role";
/**
 * Role codes stamped on screener Observations.
 * @agentic @benchmark/core
 */ const SCREENER_OBSERVATION_ROLE = {
    score: "completion-score",
    risk: "self-harm-risk"
};
/**
 * Submission-source tag code for patient screener-link administrations.
 * @agentic @benchmark/core
 */ const PATIENT_SCREENER_LINK_TAG_CODE = "patient-screener-link";
/**
 * Submission-source CodeSystem for patient-facing survey/screener tags.
 * @agentic @benchmark/core
 */ const SUBMISSION_SOURCE_SYSTEM = "https://benchmarkhealth.com/fhir/CodeSystem/submission-source";
/**
 * Numeric answers keyed by link id (non-numeric / unanswered items dropped).
 * @agentic @benchmark/core
 */ const answersByLinkIdFromItems = (items)=>{
    const answers = {};
    for (const item of items){
        const value = numericAnswerValue(item);
        if (item.linkId && value !== undefined) {
            answers[item.linkId] = String(value);
        }
    }
    return answers;
};
/**
 * The screener-observation role tag on an Observation, or undefined when it
 * carries none (a legacy risk Observation written before roles existed).
 *
 * @agentic @benchmark/core
 */ const observationScreenerRole = (obs)=>obs.meta?.tag?.find((tag)=>tag.system === SCREENER_OBSERVATION_ROLE_SYSTEM)?.code;
/**
 * Build a session-tagged screener score Observation. `effectiveAt` is stamped
 * onto both `effectiveDateTime` and `issued` — callers pass administration time
 * (live: now; backfill: QuestionnaireResponse.authored).
 *
 * Status is role-derived and load-bearing for billing: the completion-score
 * role is `final` — a genuine, billing-eligible assessment. The self-harm-risk
 * role is `preliminary` — a crisis marker written the moment item 9 turns
 * positive, before (and regardless of) instrument completion. It reuses the
 * instrument's total-score LOINC so the redrive can find it by code, but CoCM
 * eligibility counts only `final` eligible-coded Observations, so `preliminary`
 * keeps an incomplete positive self-harm from unlocking assessment billing.
 *
 * @agentic @benchmark/core
 */ const buildScreenerScoreObservation = (input)=>{
    const displayName = getObservationDisplayByLoincCode(input.loincCode);
    return {
        resourceType: "Observation",
        status: input.role === SCREENER_OBSERVATION_ROLE.risk ? "preliminary" : "final",
        category: [
            SURVEY_OBSERVATION_CATEGORY
        ],
        code: {
            coding: [
                {
                    system: input.coding?.system ?? "http://loinc.org",
                    code: input.loincCode,
                    display: displayName
                }
            ],
            text: displayName
        },
        subject: {
            reference: `Patient/${input.patientId}`
        },
        ...input.total !== undefined ? {
            valueInteger: input.total
        } : {},
        ...input.interpretation && input.interpretation.length > 0 ? {
            interpretation: input.interpretation
        } : {},
        effectiveDateTime: input.effectiveAt,
        issued: input.effectiveAt,
        derivedFrom: [
            {
                reference: `QuestionnaireResponse/${input.questionnaireResponseId}`
            }
        ],
        meta: {
            source: "members",
            tag: [
                {
                    system: SUBMISSION_SOURCE_SYSTEM,
                    code: PATIENT_SCREENER_LINK_TAG_CODE,
                    display: "Patient Screener Link"
                },
                {
                    system: ADAPTIVE_SCREENER_SESSION_SYSTEM,
                    code: input.sessionId
                },
                {
                    system: SCREENER_OBSERVATION_ROLE_SYSTEM,
                    code: input.role
                }
            ]
        }
    };
};
/**
 * Conditional-write / lookup key for a session's score Observation: one stable
 * Observation per administration session + score code + role.
 *
 * @agentic @benchmark/core
 */ const screenerScoreObservationQuery = (sessionId, loincCode, coding, role)=>{
    const params = new URLSearchParams();
    params.append("_tag", `${ADAPTIVE_SCREENER_SESSION_SYSTEM}|${sessionId}`);
    params.append("_tag", `${SCREENER_OBSERVATION_ROLE_SYSTEM}|${role}`);
    params.append("code", `${coding?.system ?? "http://loinc.org"}|${loincCode}`);
    return params;
};
/**
 * Build the final, valued completion-score Observation for a completed scorable
 * instrument, or `null` when the instrument has no total-score LOINC / cannot
 * be scored from the given answers.
 *
 * @agentic @benchmark/core
 */ const buildAdaptiveCompletionScoreObservation = (input)=>{
    const config = Object.values(STANDARD_QUESTIONNAIRES).find((q)=>q.url === input.questionnaireUrl);
    const loincCode = config?.totalScoreLoincCode;
    if (!config || !loincCode) {
        return null;
    }
    const scored = scoreAdministeredQuestionnaire(input.questionnaireUrl, answersByLinkIdFromItems(input.items));
    if (!scored) {
        return null;
    }
    const total = scored.total;
    const band = config.scoring.ranges ? getScoringRangeForTotal(total, config.scoring.ranges) : undefined;
    const interpretation = band ? buildScoreInterpretation(band.interpretation, band.level.value) : undefined;
    const coding = getObservationCoding$1(loincCode);
    return buildScreenerScoreObservation({
        patientId: input.patientId,
        sessionId: input.sessionId,
        loincCode,
        coding,
        questionnaireResponseId: input.questionnaireResponseId,
        role: SCREENER_OBSERVATION_ROLE.score,
        effectiveAt: input.authored,
        total,
        interpretation
    });
};
/**
 * Idempotency / existence query string for a completion-score Observation.
 * Returns `null` when the questionnaire has no total-score LOINC.
 *
 * @agentic @benchmark/core
 */ const adaptiveCompletionScoreObservationQuery = (sessionId, questionnaireUrl)=>{
    const config = Object.values(STANDARD_QUESTIONNAIRES).find((q)=>q.url === questionnaireUrl);
    const loincCode = config?.totalScoreLoincCode;
    if (!config || !loincCode) {
        return null;
    }
    const coding = getObservationCoding$1(loincCode);
    return screenerScoreObservationQuery(sessionId, loincCode, coding, SCREENER_OBSERVATION_ROLE.score).toString();
};

/** Every valid outcome, for exhaustiveness checks/tests. */ const ADAPTIVE_SECTION_OUTCOMES = [
    "complete",
    "partial-abandoned",
    "partial-declined",
    "skipped",
    "not-reached",
    "not-clinically-appropriate"
];
/** Only a fully-answered section is scored/banded (D8, §8.9). */ const isScoredOutcome = (outcome)=>outcome === "complete";
/**
 * Whether the outcome, on its own, evidences that the full section was presented
 * — which for a gated section means its gate triggered, i.e. it is Opened
 * (§8.10). `not-reached` is Opened only when its gate separately triggered, so it
 * is excluded here and handled via the `gateTriggered` flag in memory derivation.
 * `not-clinically-appropriate` is never Opened (D15).
 */ const outcomeOpensSection = (outcome)=>outcome === "complete" || outcome === "partial-abandoned" || outcome === "partial-declined" || outcome === "skipped";
/**
 * Whether the outcome reflects any answered FULL-SECTION content (used for
 * follow-up detection, D4/§8.1). A short-check answer is captured separately by
 * `shortCheckAnswered` on a gated section.
 */ const outcomeHasAnsweredContent = (outcome)=>outcome === "complete" || outcome === "partial-abandoned" || outcome === "partial-declined";
/**
 * Map the shipped in-sitting settlement status onto the six-state outcome (D14
 * "bookkeeping"). Total over the terminal statuses; the transient `pending`
 * status is not a resolved outcome and returns `null` (the caller treats a
 * pending historical record as not-yet-resolved rather than inventing a state).
 *
 * @agentic @benchmark/core
 */ const outcomeFromSettlementStatus = (status)=>{
    switch(status){
        case "answered":
            return "complete";
        case "declined-remaining":
            return "partial-declined";
        case "declined-section":
            return "skipped";
        case "not-administered-clinical":
            return "not-clinically-appropriate";
        case "pending":
            return null;
    }
};
// ---------------------------------------------------------------------------
// Phase 2 — "opened = gate triggered" memory derivation (D1–D6, D14)
// ---------------------------------------------------------------------------
/**
 * The gated (short-check) sections — the only sections to which "opened" applies
 * (§8.3). Ungated sections are asked in full at every check-in. Kept in sync with
 * the short-check specs in `adaptive-screener.service` by the drift-guard test
 * (which asserts `composeAdaptiveScreener` gives exactly these sections a
 * `short-check` gating on a baseline).
 */ const GATED_SECTION_KEYS = new Set([
    "anxiety",
    "alcohol"
]);
/** Whether "opened" applies to this section (i.e. it is gated by a short check). */ const isGatedSection = (key)=>GATED_SECTION_KEYS.has(key);
/** Whether a gated section record opened its section (D1/§8.4). */ const recordOpensGatedSection = (record)=>Boolean(record.gateTriggered) || record.outcome !== undefined && outcomeOpensSection(record.outcome);
/**
 * Whether a section record evidences any retained answered content (D4/§8.1).
 *
 * A gated section's short check being answered (positive or negative) is retained
 * content. `gateTriggered` alone is deliberately NOT counted as content: when a
 * short check is actually answered the caller sets `shortCheckAnswered`, and a
 * section opened purely by memory carries its content from the earlier sitting
 * that actually answered it — so a bare `gateTriggered` never fabricates
 * follow-up status on its own.
 */ const recordHasAnsweredContent = (record)=>{
    if (isGatedSection(record.sectionKey) && record.shortCheckAnswered) {
        return true;
    }
    return record.outcome !== undefined && outcomeHasAnsweredContent(record.outcome);
};
/**
 * Whether a single prior sitting evidences any retained answered content — a
 * short check answered (positive or negative), any answered full-section content,
 * or a recorded severity rating (D4/§8.1).
 */ const sittingHasAnsweredContent = (sitting)=>Boolean(sitting.severityRatingRecorded) || sitting.sections.some(recordHasAnsweredContent);
/**
 * Derive the follow-up selection memory from the patient's prior sittings.
 *
 * "Opened" (D1): a gated section is opened if, at ANY prior sitting, its gate
 * triggered or the full set was presented (any state that opens it) — regardless
 * of how the patient then answered, and even across partial/abandoned sittings
 * (D4). Opening is permanent and only ever adds (D6): the result is the union
 * across all sittings, further unioned with `legacyOpenedSections` and never
 * reduced (the D14 "nothing already opened may become un-opened" invariant, made
 * explicit by `findUnOpenedRegressions`).
 *
 * @agentic @benchmark/core
 */ const deriveFollowUpSelectionMemory = (priorSittings, opts = {})=>{
    const openedInFullBefore = new Set(opts.legacyOpenedSections ?? []);
    let isFollowUp = false;
    let ratingAnchorEstablished = false;
    for (const sitting of priorSittings){
        if (sittingHasAnsweredContent(sitting)) {
            isFollowUp = true;
        }
        if (sitting.severityRatingRecorded) {
            ratingAnchorEstablished = true;
        }
        for (const section of sitting.sections){
            if (isGatedSection(section.sectionKey) && recordOpensGatedSection(section)) {
                openedInFullBefore.add(section.sectionKey);
            }
        }
    }
    return {
        isBaseline: !isFollowUp,
        isFollowUp,
        openedInFullBefore,
        ratingAnchorEstablished
    };
};
/**
 * The D14 safety invariant, made executable: assert that newly-derived memory
 * never drops a section that was opened before. Returns the offending keys (empty
 * when safe) so the caller can fail loud rather than silently un-open history.
 *
 * @agentic @benchmark/core
 */ const findUnOpenedRegressions = (previouslyOpened, nextOpened)=>[
        ...previouslyOpened
    ].filter((key)=>!nextOpened.has(key));
/**
 * Resolve the follow-up selection end to end: derive memory from history, then
 * compose the ordered section plan with the existing (unchanged) composer, and
 * run the dropped-section audit. Pure and deterministic (D9): identical history
 * yields an identical plan.
 *
 * Patient rating (§8.2): a first check-in asks the severity rating (PGI-S); every
 * follow-up asks the change rating (PGI-C) — unconditionally, even when no prior
 * severity rating was recorded. This is keyed off follow-up status, not the
 * severity anchor, per §8.2/§9.6.
 *
 * @agentic @benchmark/core
 */ const resolveFollowUpSelection = (ctx)=>{
    const memory = deriveFollowUpSelectionMemory(ctx.priorSittings, {
        legacyOpenedSections: ctx.legacyOpenedSections
    });
    const composeCtx = {
        isConfirmedAdult: ctx.isConfirmedAdult,
        isBaseline: memory.isBaseline,
        openedInFullBefore: memory.openedInFullBefore,
        // §8.2/§9.6: every follow-up asks the change rating (PGI-C), regardless of
        // whether a prior severity rating (PGI-S) was ever administered.
        ratingAnchorEstablished: memory.isFollowUp,
        enabledSections: ctx.enabledSections
    };
    const composed = composeAdaptiveScreener(composeCtx);
    return {
        ...composed,
        memory,
        droppedOpenedSections: findDroppedOpenedSections(memory.openedInFullBefore, composed.sections)
    };
};
// ---------------------------------------------------------------------------
// Phase 3 — safety signal on the answer, sticky across edits/resume (D11)
// ---------------------------------------------------------------------------
/** The one safety item that fires the escalation signal (D11). */ const SAFETY_ITEM_LINK_ID = "phq9-9";
/**
 * Whether an answer value to the safety item (PHQ-9 item 9) is positive: any
 * answer other than "Not at all" (value 0). A missing answer is not positive.
 */ const isSafetyItemPositive = (value)=>value !== undefined && value > 0;
/**
 * Whether the current PHQ-9 answers contain a positive safety item (item 9 other
 * than "Not at all"). Pure predicate over the CURRENT answers only.
 *
 * @agentic @benchmark/core
 */ const deriveSafetySignalFired = (phq9Answers)=>phq9Answers.some((answer)=>answer.linkId === SAFETY_ITEM_LINK_ID && isSafetyItemPositive(answer.value));
/**
 * The safety signal as a **sticky, idempotent** state transition (D11/§8.13).
 * Once a positive item-9 answer has fired the signal for an administration it
 * stays fired: editing or removing the answer, or resuming later, neither
 * un-fires it (`fired` stays true given `previouslyFired`) nor raises a duplicate
 * (`newlyRaised` is true only on the first transition). Fires on the answer,
 * independent of completion. Delivery to a person is out of scope (§5.14).
 *
 * @agentic @benchmark/core
 */ const deriveSafetySignalState = (input)=>{
    const positiveNow = deriveSafetySignalFired(input.phq9Answers);
    const fired = input.previouslyFired || positiveNow;
    return {
        fired,
        newlyRaised: fired && !input.previouslyFired
    };
};
/**
 * Build the per-administration selection record from the composed plan (§8.15):
 * which sections were asked, which were carried in from memory, which run a short
 * check, and — given this sitting's short-check outcomes — which the short check
 * closed (RAN and did not open). `shortCheckClosed` is a post-administration
 * outcome (§8.9), so the caller supplies both the set of gated sections whose
 * short check was ANSWERED this sitting and the set it OPENED: a short-checked
 * section is closed only when its short check was answered but did not open.
 * A short check that was never reached (answered set excludes it) is NOT closed.
 * When the answered set is omitted, `shortCheckClosed` is false (not yet known).
 *
 * @agentic @benchmark/core
 */ const buildSelectionRecord = (sections, opts = {})=>sections.map((section)=>{
        const shortChecked = section.gating === "short-check";
        const answered = opts.shortCheckAnswered?.has(section.key) ?? false;
        const opened = opts.openedThisSitting?.has(section.key) ?? false;
        return {
            sectionKey: section.key,
            asked: true,
            carriedFromMemory: section.gating === "full-by-memory" || section.reasonCode === "opened-at-baseline",
            shortChecked,
            shortCheckClosed: shortChecked && answered && !opened
        };
    });
/**
 * Fail-loud audit (§7.1/§10.4): return the previously-opened sections that are
 * ABSENT from the composed plan. A non-empty result is an alert-grade defect — a
 * previously-opened section was silently dropped — and the caller must surface it
 * loudly rather than tolerate it. Empty is the healthy case.
 *
 * There is NO exemption for release-disabled sections: §7.1 tolerates zero
 * omissions of an opened section, so a disabled-yet-opened section is reported as
 * a drop rather than silently excused (that reconciliation is the caller's).
 *
 * @agentic @benchmark/core
 */ const findDroppedOpenedSections = (openedInFullBefore, composedSections)=>{
    const composed = new Set(composedSections.map((section)=>section.key));
    return [
        ...openedInFullBefore
    ].filter((key)=>!composed.has(key));
};

/** Single-instrument, ungated sections keyed by questionnaire URL. */ const UNGATED_SECTION_BY_URL = {
    [QUESTIONNAIRE_URLS$1.PHQ9]: "depression",
    [QUESTIONNAIRE_URLS$1.UCLA3]: "loneliness",
    [QUESTIONNAIRE_URLS$1.WSAS]: "functioning",
    [QUESTIONNAIRE_URLS$1.DRUG_USE]: "drug-use"
};
const itemsOf = (qr)=>qr.item ?? [];
const answeredCount = (qr)=>itemsOf(qr).filter((item)=>(item.answer?.length ?? 0) > 0).length;
const hasAnyAnswer = (qr)=>answeredCount(qr) > 0;
const integerAnswer = (qr, linkId)=>numericAnswerValue(itemsOf(qr).find((item)=>item.linkId === linkId));
/**
 * Resolve a full-section outcome from a QR: prefer the persisted settlement
 * marker (D14 bookkeeping), else fall back to completion/answers. `pending`
 * settlement maps to `undefined` (not a resolved outcome).
 */ const deriveOutcome = (qr)=>{
    const settlement = readAdaptiveScreenerMarker(qr)?.settlement;
    if (settlement) {
        const mapped = outcomeFromSettlementStatus(settlement);
        if (mapped) {
            return mapped;
        }
    }
    // `completed` maps to `complete` only when the QR actually holds answered
    // content — a crafted or corrupted completed-but-empty response must not
    // read as a fully-answered section.
    if (qr.status === "completed" && hasAnyAnswer(qr)) {
        return "complete";
    }
    return hasAnyAnswer(qr) ? "partial-abandoned" : "not-reached";
};
const findByUrl = (qrs, url)=>qrs.find((qr)=>qr.questionnaire === url);
const answeredLinkIds = (qr)=>new Set((qr?.item ?? []).filter((item)=>item.linkId && (item.answer?.length ?? 0) > 0).map((item)=>item.linkId));
/**
 * Derive one section's recorded lifecycle state from its required-this-visit
 * instruments (PRD §8.8, exhaustive and precedence-ordered — the server-side
 * single source of truth, D18):
 *
 * 1. An explicit decline settlement always settles the section:
 *    `partial-declined` with ≥1 answer anywhere in the section, `skipped` with
 *    none (an advocate `not-administered-clinical` settlement with no answers
 *    maps to `not-clinically-appropriate`).
 * 2. Every instrument `completed` → `complete` (only ever set by full
 *    completion — a skipped item never counts, D3/§9.16).
 * 3. Every active item of every instrument presented (answered or skipped):
 *    zero answers → `skipped` (skipped through item-by-item); some answers →
 *    `partial-abandoned` (mixed walk-through is never complete).
 * 4. Any recorded activity (answer or skip) → `partial-abandoned`.
 * 5. Nothing presented → `not-reached`.
 *
 * A corrupt or absent settlement/skip extension falls through to the
 * no-settlement rows — `partial-abandoned` is the safe default (PRD failure
 * modes). Built on the shipped settlement vocabulary
 * (`isExplicitSettlement` / `outcomeFromSettlementStatus`), never a parallel
 * re-derivation.
 *
 * @agentic @benchmark/core
 */ const deriveAdaptiveSectionOutcome = (instruments)=>{
    let sectionAnswerCount = 0;
    let anyActivity = false;
    const explicitSettlements = [];
    let allCompleted = instruments.length > 0;
    let fullyPresented = instruments.length > 0;
    for (const instrument of instruments){
        const marker = instrument.qr ? readAdaptiveScreenerMarker(instrument.qr) : null;
        const answered = answeredLinkIds(instrument.qr);
        const skipped = new Set(marker?.skippedLinkIds ?? []);
        sectionAnswerCount += answered.size;
        if (answered.size > 0 || skipped.size > 0) {
            anyActivity = true;
        }
        const settlement = marker?.settlement;
        if (settlement && isExplicitSettlement(settlement)) {
            explicitSettlements.push(settlement);
        }
        // Per-instrument answered-content guard: a `completed` status with zero
        // answers is contradictory data — it must not count toward `complete`
        // (which would let an empty instrument mask as fully answered).
        if (instrument.qr?.status !== "completed" || answered.size === 0) {
            allCompleted = false;
        }
        const presented = instrument.qr !== undefined && instrument.activeLinkIds.length > 0 && instrument.activeLinkIds.every((linkId)=>answered.has(linkId) || skipped.has(linkId));
        if (!presented) {
            fullyPresented = false;
        }
    }
    if (explicitSettlements.length > 0) {
        if (sectionAnswerCount > 0) {
            return "partial-declined";
        }
        return explicitSettlements.every((settlement)=>settlement === "not-administered-clinical") ? "not-clinically-appropriate" : "skipped";
    }
    if (allCompleted) {
        return "complete";
    }
    if (fullyPresented) {
        return sectionAnswerCount === 0 ? "skipped" : "partial-abandoned";
    }
    if (anyActivity) {
        return "partial-abandoned";
    }
    return "not-reached";
};
/** Build one sitting's section records from its grouped instrument responses. */ const buildSitting = (qrs)=>{
    const sections = [];
    // Ungated single-instrument sections: content + outcome, no gate/opened.
    for (const qr of qrs){
        const key = qr.questionnaire ? UNGATED_SECTION_BY_URL[qr.questionnaire] : undefined;
        if (key) {
            sections.push({
                sectionKey: key,
                outcome: deriveOutcome(qr)
            });
        }
    }
    // Anxiety (GAD-2 short check → GAD-7 full set): gate opens on GAD-2 ≥ cutoff
    // or a present GAD-7; the short check is answered when GAD-2 has any answer.
    const gad2 = findByUrl(qrs, QUESTIONNAIRE_URLS$1.GAD2);
    const gad7 = findByUrl(qrs, QUESTIONNAIRE_URLS$1.GAD7);
    if (gad2 || gad7) {
        const gad2Score = (integerAnswer(gad2 ?? {}, "gad2-1") ?? 0) + (integerAnswer(gad2 ?? {}, "gad2-2") ?? 0);
        const gad7Present = gad7 !== undefined && hasAnyAnswer(gad7);
        sections.push({
            sectionKey: "anxiety",
            shortCheckAnswered: gad2 !== undefined && hasAnyAnswer(gad2),
            gateTriggered: gad2Score >= ANXIETY_SHORT_CHECK_CUTOFF || gad7Present,
            outcome: gad7 ? deriveOutcome(gad7) : undefined
        });
    }
    // Alcohol (AUDIT-C, internally gated): item 1 > 0 (or items 2–3 present) opens
    // the full set; item 1 = "Never" (0) completes the short check without opening.
    const audit = findByUrl(qrs, QUESTIONNAIRE_URLS$1.AUDIT_C);
    if (audit) {
        const item1 = integerAnswer(audit, "audit-c-1");
        const items23Present = itemsOf(audit).some((item)=>item.linkId === "audit-c-2" || item.linkId === "audit-c-3");
        const gateTriggered = item1 !== undefined && item1 > 0 || items23Present;
        sections.push({
            sectionKey: "alcohol",
            shortCheckAnswered: item1 !== undefined,
            gateTriggered,
            // A negative gate (item 1 = 0) did not open the full section, so it has no
            // full-section outcome — only the short check ran.
            outcome: gateTriggered ? deriveOutcome(audit) : undefined
        });
    }
    // Patient rating: any prior severity rating (PGI-S) establishes the change
    // anchor and is answered content; PGI-C is content but not an anchor.
    const pgis = findByUrl(qrs, QUESTIONNAIRE_URLS$1.PGI_S);
    const pgic = findByUrl(qrs, QUESTIONNAIRE_URLS$1.PGI_C);
    const severityRatingRecorded = pgis !== undefined && itemsOf(pgis).some((item)=>numericAnswerValue(item) !== undefined);
    if (pgis || pgic) {
        sections.push({
            sectionKey: "patient-rating",
            outcome: deriveOutcome(pgis ?? pgic)
        });
    }
    return {
        sections,
        severityRatingRecorded
    };
};
/**
 * Group a patient's prior adaptive-tagged QuestionnaireResponses (completed AND
 * in-progress) into `PriorSitting[]` for the follow-up selection engine.
 * Non-adaptive or untagged responses are ignored. Deterministic: sittings are
 * returned in first-seen session order; the engine's results are order-invariant
 * regardless (union across sittings).
 *
 * @agentic @benchmark/core
 */ const mapResponsesToPriorSittings = (responses)=>{
    const bySession = new Map();
    for (const qr of responses){
        const marker = readAdaptiveScreenerMarker(qr);
        if (!marker) {
            continue;
        }
        const group = bySession.get(marker.sessionId);
        if (group) {
            group.push(qr);
        } else {
            bySession.set(marker.sessionId, [
                qr
            ]);
        }
    }
    return [
        ...bySession.values()
    ].map(buildSitting);
};

/**
 * Extra wait added to Medplum's server hint so retries do not wake exactly on
 * the limiter boundary.
 * @agentic @benchmark/core
 */ const RATE_LIMIT_BUFFER_MS = 500;
/** Wait used for a throttled batch entry whose diagnostics are unparseable. */ const BATCH_RATE_LIMIT_DEFAULT_MS = 30_000;
/** Sleep utility. @agentic @benchmark/core */ const sleep$1 = (ms)=>new Promise((resolve)=>setTimeout(resolve, ms));
/** Agreed default rate-limit retry policy. @agentic @benchmark/core */ const DEFAULT_RATE_LIMIT_POLICY = {
    maxRetries: 5,
    bufferMs: RATE_LIMIT_BUFFER_MS,
    baseDelayMs: 1000,
    maxDelayMs: 60_000,
    jitter: true,
    sleep: sleep$1
};
/** Extract `_msBeforeNext` (ms) from an OperationOutcome issue `diagnostics` JSON. */ const serverHintFromDiagnostics = (diagnostics)=>{
    if (!diagnostics) return undefined;
    try {
        const parsed = JSON.parse(diagnostics);
        return typeof parsed._msBeforeNext === "number" && parsed._msBeforeNext > 0 ? parsed._msBeforeNext : undefined;
    } catch  {
        return undefined;
    }
};
/** True when an OperationOutcome represents Medplum throttling. */ const isTooManyRequestsOutcome = (outcome)=>{
    if (outcome.id === "too-many-requests") return true;
    try {
        return getStatus(outcome) === 429;
    } catch  {
        return false;
    }
};
const errorMessage = (error)=>error instanceof Error ? error.message : String(error ?? "");
/**
 * Extract the first balanced `{...}` JSON object substring from a message
 * without regex backtracking (avoids ReDoS on adversarial input). Returns the
 * embedded `_msBeforeNext` if the substring parses, else undefined.
 */ const serverHintFromMessage = (message)=>{
    const start = message.indexOf("{");
    const end = message.lastIndexOf("}");
    if (start === -1 || end <= start) return undefined;
    return serverHintFromDiagnostics(message.slice(start, end + 1));
};
/** Detect a thrown rate-limit error. */ const detectErrorRateLimit = (error)=>{
    // 1. OperationOutcomeError / raw OperationOutcome (thrown ops + transactions).
    const outcome = error instanceof OperationOutcomeError ? error.outcome : isOperationOutcome(error) ? error : undefined;
    if (outcome && isTooManyRequestsOutcome(outcome)) {
        return {
            serverHintMs: serverHintFromDiagnostics(outcome.issue?.[0]?.diagnostics),
            fallbackMs: 0
        };
    }
    // 2. Defensive: a direct numeric `_msBeforeNext` property on the error.
    if (typeof error === "object" && error !== null) {
        const direct = error._msBeforeNext;
        if (typeof direct === "number" && direct > 0) {
            return {
                serverHintMs: direct,
                fallbackMs: 0
            };
        }
    }
    // 3. Fallback: message substring with optionally embedded JSON.
    const message = errorMessage(error);
    if (!message.includes("Too Many Requests")) return undefined;
    return {
        serverHintMs: serverHintFromMessage(message),
        fallbackMs: 0
    };
};
/** Detect a throttled entry inside a batch-response bundle. */ const detectBundleRateLimit = (bundle)=>{
    for (const entry of bundle.entry ?? []){
        const outcome = entry.response?.outcome;
        const status = entry.response?.status;
        const throttled = outcome && outcome.id === "too-many-requests" || typeof status === "string" && status.startsWith("429");
        if (throttled) {
            return {
                serverHintMs: serverHintFromDiagnostics(outcome?.issue?.[0]?.diagnostics),
                fallbackMs: BATCH_RATE_LIMIT_DEFAULT_MS
            };
        }
    }
    return undefined;
};
/**
 * Resolve the actual wait. A server hint is honored exactly (plus the policy
 * buffer, capped). Without a hint, wait at least the detection's fallback floor
 * (e.g. the 30s batch default) and at least the exponential backoff for the
 * attempt — whichever is larger — capped at `maxDelayMs`.
 */ const computeWaitMs = (detection, attempt, policy)=>{
    if (detection.serverHintMs !== undefined) {
        return Math.min(detection.serverHintMs + policy.bufferMs, policy.maxDelayMs);
    }
    let backoff = policy.baseDelayMs * 2 ** attempt;
    if (policy.jitter) backoff += Math.random() * policy.baseDelayMs;
    return Math.min(Math.max(detection.fallbackMs, Math.round(backoff)), policy.maxDelayMs);
};
const resolvePolicy = (policy)=>({
        ...DEFAULT_RATE_LIMIT_POLICY,
        ...policy
    });
/**
 * Run an async operation, retrying only on a thrown Medplum 429. Use for
 * single-resource ops and transaction bundles. Non-429 errors propagate.
 * @agentic @benchmark/core
 */ const withRateLimitRetry = async (fn, policy = {})=>{
    const resolved = resolvePolicy(policy);
    let attempt = 0;
    for(;;){
        try {
            return await fn();
        } catch (error) {
            const hint = detectErrorRateLimit(error);
            if (!hint || attempt >= resolved.maxRetries) throw error;
            const waitMs = computeWaitMs(hint, attempt, resolved);
            attempt += 1;
            resolved.onRetry?.({
                waitMs,
                attempt,
                maxRetries: resolved.maxRetries
            });
            await resolved.sleep(waitMs);
        }
    }
};
/**
 * Run a batch operation, retrying on a thrown 429 *and* on per-entry 429s in the
 * returned bundle. When retries are exhausted the last bundle is returned so the
 * caller still sees the throttled entries.
 * @agentic @benchmark/core
 */ const withBundleRateLimitRetry = async (fn, policy = {})=>{
    const resolved = resolvePolicy(policy);
    let attempt = 0;
    for(;;){
        let result;
        try {
            result = await fn();
        } catch (error) {
            const hint = detectErrorRateLimit(error);
            if (!hint || attempt >= resolved.maxRetries) throw error;
            const waitMs = computeWaitMs(hint, attempt, resolved);
            attempt += 1;
            resolved.onRetry?.({
                waitMs,
                attempt,
                maxRetries: resolved.maxRetries
            });
            await resolved.sleep(waitMs);
            continue;
        }
        const bundleHint = detectBundleRateLimit(result);
        if (!bundleHint || attempt >= resolved.maxRetries) return result;
        const waitMs = computeWaitMs(bundleHint, attempt, resolved);
        attempt += 1;
        resolved.onRetry?.({
            waitMs,
            attempt,
            maxRetries: resolved.maxRetries
        });
        await resolved.sleep(waitMs);
    }
};

/**
 * Collect distinct FHIR resource types referenced by bundle entries so
 * callers can invalidate Medplum search caches after a transaction.
 * @agentic @benchmark/core
 */ const collectBundleResourceTypes = (bundle)=>{
    const types = new Set();
    for (const entry of bundle.entry ?? []){
        if (entry.resource?.resourceType) {
            types.add(entry.resource.resourceType);
        }
        const requestUrl = entry.request?.url;
        if (requestUrl) {
            const resourceType = requestUrl.split(/[/?]/)[0];
            if (resourceType && /^[A-Z]/.test(resourceType)) {
                types.add(resourceType);
            }
        }
    }
    return [
        ...types
    ];
};
/**
 * Invalidate Medplum search caches for every resource type touched by a bundle.
 * `executeBatch` does not do this automatically (unlike `createResource`), so
 * event-driven refetches would otherwise read stale search results.
 * @agentic @benchmark/core
 */ const invalidateBundleSearchCaches = (medplum, bundle)=>{
    for (const resourceType of collectBundleResourceTypes(bundle)){
        medplum.invalidateSearches(resourceType);
    }
};
/**
 * Execute a FHIR batch transaction and invalidate search caches for touched types.
 * Retries automatically on a thrown 429 (transaction bundles) and on per-entry
 * 429s (non-atomic batch bundles) via the centralized rate-limit handler.
 * @agentic @benchmark/core
 */ const executeBatchWithCacheInvalidation = async (medplum, bundle, rateLimitPolicy = {})=>{
    const result = await withBundleRateLimitRetry(()=>medplum.executeBatch(bundle), rateLimitPolicy);
    invalidateBundleSearchCaches(medplum, bundle);
    invalidateBundleSearchCaches(medplum, result);
    return result;
};

const DEFAULT_RETRY_OPTIONS = {
    retries: 0,
    delay: 1000
};
/** Sleep utility for retry delays. @agentic @benchmark/core */ const sleep = (ms)=>new Promise((resolve)=>setTimeout(resolve, ms));
/** Submits a FHIR Bundle transaction with optional retry. @agentic @benchmark/core */ async function submitBundle(medplum, bundle, options = {}) {
    const { retries, delay } = {
        ...DEFAULT_RETRY_OPTIONS,
        ...options
    };
    let lastError;
    for(let attempt = 0; attempt <= retries; attempt++){
        try {
            return await executeBatchWithCacheInvalidation(medplum, bundle);
        } catch (error) {
            lastError = error;
            // Don't retry on last attempt
            if (attempt < retries) {
                await sleep(delay);
                continue;
            }
            throw error;
        }
    }
    throw lastError;
}
/**
 * Creates a FHIR resource. Retries automatically on a Medplum 429 (safe even
 * for this POST: the limiter rejects before the write is processed).
 * @agentic @benchmark/core
 */ async function createResource(medplum, resource, rateLimitPolicy = {}) {
    return await withRateLimitRetry(()=>medplum.createResource(resource), rateLimitPolicy);
}
/**
 * Atomically creates a FHIR resource only if no existing resource matches the
 * given search `query` (FHIR conditional create / `If-None-Exist`). Returns the
 * existing resource on a match, avoiding check-then-create races and duplicate
 * writes on retries. Retries on a Medplum 429.
 * @agentic @benchmark/core
 */ async function createResourceIfNoneExist(medplum, resource, query, rateLimitPolicy = {}) {
    return await withRateLimitRetry(()=>medplum.createResourceIfNoneExist(resource, query), rateLimitPolicy);
}
/**
 * Atomically upserts a FHIR resource keyed on a search `query` (FHIR conditional
 * update, `PUT [type]?query`): no match creates, exactly one match updates in
 * place, and more than one is rejected. A single transactional request, so two
 * concurrent writers for the same key can never mint duplicate resources the way
 * a read-then-create would. Retries on a Medplum 429.
 * @agentic @benchmark/core
 */ async function upsertResource(medplum, resource, query, rateLimitPolicy = {}) {
    return await withRateLimitRetry(()=>medplum.upsertResource(resource, query), rateLimitPolicy);
}
/** Updates a FHIR resource, retrying on a Medplum 429. @agentic @benchmark/core */ async function updateResource(medplum, resource, rateLimitPolicy = {}) {
    return await withRateLimitRetry(()=>medplum.updateResource(resource), rateLimitPolicy);
}
/** Reads a FHIR resource by type and ID, retrying on a Medplum 429. @agentic @benchmark/core */ async function readResource(medplum, resourceType, id, rateLimitPolicy = {}) {
    return await withRateLimitRetry(()=>medplum.readResource(resourceType, id), rateLimitPolicy);
}
/** Deletes a FHIR resource, retrying on a Medplum 429. @agentic @benchmark/core */ async function deleteResource(medplum, resourceType, id, rateLimitPolicy = {}) {
    await withRateLimitRetry(()=>medplum.deleteResource(resourceType, id), rateLimitPolicy);
}
/** Searches for FHIR resources by type and query, retrying on a Medplum 429. @agentic @benchmark/core */ async function searchResources(medplum, resourceType, query, rateLimitPolicy = {}) {
    const result = await withRateLimitRetry(()=>medplum.search(resourceType, query), rateLimitPolicy);
    return result;
}

/** Creates an Appointment resource. @agentic @benchmark/core */ async function createAppointment(medplum, appointment) {
    return await createResource(medplum, appointment);
}
/** Updates an existing Appointment resource. @agentic @benchmark/core */ async function updateAppointment(medplum, appointment) {
    return await updateResource(medplum, appointment);
}
/** Reads an Appointment resource by ID. @agentic @benchmark/core */ async function getAppointment(medplum, id) {
    return await readResource(medplum, "Appointment", id);
}
/** Searches for Appointment resources by query. @agentic @benchmark/core */ async function searchAppointments(medplum, query) {
    const bundle = await searchResources(medplum, "Appointment", query);
    return bundle.entry?.map((e)=>e.resource).filter(Boolean) || [];
}
/** Deletes an Appointment resource. @agentic @benchmark/core */ async function deleteAppointment(medplum, id) {
    return await deleteResource(medplum, "Appointment", id);
}

/**
 * Care Plan Service. Service for CarePlan resource CRUD operations.
 * @agentic @benchmark/core
 */ // Constants
/** Updates an existing CarePlan resource. @agentic @benchmark/core */ async function updateCarePlan(medplum, carePlan) {
    return await updateResource(medplum, carePlan);
}
/** Gets active care plans for a patient. @agentic @benchmark/core */ async function getActiveCarePlans(medplum, patientId) {
    const result = await medplum.searchResources("CarePlan", {
        subject: `Patient/${patientId}`,
        status: "active,draft",
        _sort: "-date",
        _count: 1000
    });
    return result;
}
/** Page size for project-wide CarePlan sweeps. */ const CARE_PLAN_SWEEP_PAGE_COUNT = "500";
/**
 * Fetches every on-hold care plan in the project, across all patients.
 *
 * This is the full set of held plans pause hygiene has to account for — but
 * not all of them are pause residue: `holdCarePlan` writes the same status
 * (no production caller today), and the CLI triage migration copies an on-hold
 * status onto a plan it creates. Callers must attribute a plan to a pause
 * before acting on it. Pages exhaustively; a partial sweep would leave plans
 * stranded with no error anywhere.
 * @agentic @benchmark/core
 */ async function findAllOnHoldCarePlans(medplum) {
    const carePlans = [];
    for await (const page of medplum.searchResourcePages("CarePlan", {
        status: "on-hold",
        _count: CARE_PLAN_SWEEP_PAGE_COUNT
    })){
        carePlans.push(...page);
    }
    return carePlans;
}
/** Gets on-hold (paused) care plans for a patient. @agentic @benchmark/core */ async function getOnHoldCarePlans(medplum, patientId) {
    const result = await medplum.searchResources("CarePlan", {
        subject: `Patient/${patientId}`,
        status: "on-hold",
        _sort: "-date",
        _count: 1000
    });
    return result;
}
/** Gets care plan by type for a patient. @agentic @benchmark/core */ async function getCarePlanByType(medplum, patientId, type) {
    const result = await medplum.searchResources("CarePlan", {
        subject: `Patient/${patientId}`,
        category: type,
        _sort: "-date",
        _count: QUERY_LIMIT_SINGLE.toString()
    });
    return result.length > 0 ? result[0] : undefined;
}
/** Checks if patient has completed intake. @agentic @benchmark/core */ async function hasCompletedIntake(medplum, patientId) {
    const intakeCarePlan = await getCarePlanByType(medplum, patientId, "intake");
    if (!intakeCarePlan) {
        return false;
    }
    return intakeCarePlan.status === "completed";
}
/** Checks if patient has active triage. @agentic @benchmark/core */ async function hasActiveTriage(medplum, patientId) {
    const triageCarePlan = await getCarePlanByType(medplum, patientId, "triage");
    if (!triageCarePlan) {
        return false;
    }
    return triageCarePlan.status === "active";
}
const carePlanHasCategoryCode = (plan, code)=>(plan.category ?? []).some((c)=>(c.coding ?? []).some((coding)=>coding.code === code && (coding.system === undefined || coding.system === CARE_PLAN_CATEGORY_SYSTEM$1)));
/**
 * Pick the default CarePlan reference for a Quick-Add care-time entry.
 *
 * Preference order (per the 2026-05-19 plan, A4):
 *   1. Active CoCM CarePlan (the canonical billing anchor)
 *   2. Most recent active CarePlan (Intake or Triage)
 *
 * Returns `undefined` when the patient has no active care plans. The
 * caller is expected to fall back to writing the Observation without a
 * `basedOn` reference (unchanged behavior).
 *
 * If the patient has multiple active CoCM plans (data integrity edge
 * case — Medplum invariant is at most one) the first match wins.
 * @agentic @benchmark/core
 */ async function findDefaultCarePlanForCareTime(medplum, patientId) {
    const active = await getActiveCarePlans(medplum, patientId);
    if (active.length === 0) return undefined;
    const cocm = active.find((plan)=>plan.id && carePlanHasCategoryCode(plan, "cocm"));
    if (cocm) return cocm;
    return active.find((plan)=>plan.id);
}
/**
 * Get plan-backed Tasks for a CarePlan
 *
 * OCPE Phase 4: Fetches Tasks that reference the CarePlan via based-on.
 * Used for Task-backed activity status display and UI.
 *
 * @param medplum - MedplumClient instance
 * @param carePlanId - CarePlan ID
 * @returns Array of Tasks linked to the CarePlan
 */ async function getActivityTasks(medplum, carePlanId) {
    // Pages exhaustively. `searchResources` returns only the first page, so a plan
    // with more Tasks than one page would silently drop the remainder — and every
    // caller (advancement, completion checks, the CoCM ready-state invariant, and
    // stalled-plan remediation) depends on the complete Task set to classify and
    // promote the correct activity.
    const tasks = [];
    for await (const page of medplum.searchResourcePages("Task", {
        "based-on": `CarePlan/${carePlanId}`,
        _sort: "authored-on",
        _count: 1000
    })){
        tasks.push(...page);
    }
    return tasks;
}
/**
 * Builds a Map of Tasks keyed by ID for Task-backed care plan activities.
 * @agentic @benchmark/core
 */ async function buildCarePlanTasksMap(medplum, carePlan, options = {}) {
    const { requireTaskBackedActivities = true, suppressErrors = false } = options;
    const hasTaskBackedActivities = (carePlan.activity ?? []).some((activity)=>!!activity.reference?.reference) || !requireTaskBackedActivities;
    if (!hasTaskBackedActivities || !carePlan.id) {
        return undefined;
    }
    try {
        const tasks = await getActivityTasks(medplum, carePlan.id);
        if (!tasks?.length) {
            return new Map();
        }
        return new Map(tasks.filter((task)=>!!task.id).map((task)=>[
                task.id,
                task
            ]));
    } catch (error) {
        if (suppressErrors) {
            return new Map();
        }
        throw error;
    }
}

/**
 * FHIR-related URLs used throughout the application
 */ // HL7 FHIR Base URLs
const HL7_TERMINOLOGY_BASE = "http://terminology.hl7.org";
// Organization Base URLs
const ORGANIZATION_BASE = "http://benchmarkhealth.com/fhir";
/**
 * Base URL for Benchmark Health custom FHIR extensions
 *
 * All custom StructureDefinition URLs should be derived from this base.
 * Use this constant to ensure consistency across the codebase.
 *
 * @example
 * ```ts
 * const myExtension = `${EXTENSION_BASE}/my-extension-name`;
 * // => "http://benchmarkhealth.com/fhir/StructureDefinition/my-extension-name"
 * ```
 */ const EXTENSION_BASE = `${ORGANIZATION_BASE}/StructureDefinition`;

/**
 * FHIR extension URL marking a care-time entry as excluded from CoCM billing.
 * Absent or `valueBoolean: false` means the entry is billable (backward
 * compatible — no historical data carries the extension). See the care-time
 * non-billable toggle plan (2026-07-23).
 * @agentic @benchmark/core
 */ const CARE_TIME_NON_BILLABLE_EXTENSION_URL = `${EXTENSION_BASE}/care-time-non-billable`;
/**
 * True only when the entry carries the non-billable extension set to `true`.
 * Any other shape (missing extension, `false`, absent `valueBoolean`) is
 * treated as billable.
 * @agentic @benchmark/core
 */ const isCareTimeNonBillable = (observation)=>(observation.extension ?? []).some((ext)=>ext.url === CARE_TIME_NON_BILLABLE_EXTENSION_URL && ext.valueBoolean === true);
/**
 * Care-time observation code system (Benchmark-minted).
 * Holds the single `care-management-minutes` code (D3).
 * @agentic @benchmark/core
 */ const CARE_TIME_CODE_SYSTEM = "https://benchmarkhealth.org/fhir/CodeSystem/care-time-type";
/**
 * Benchmark observation category system used as a sub-classification
 * alongside the HL7 `activity` category.
 * @agentic @benchmark/core
 */ const CARE_TIME_CATEGORY_SYSTEM = "https://benchmarkhealth.org/fhir/CodeSystem/observation-category";
/**
 * Token search expression for FHIR queries that target the Benchmark
 * `care-time` category (e.g. `category={CARE_TIME_CATEGORY_TOKEN}`).
 * @agentic @benchmark/core
 */ const CARE_TIME_CATEGORY_TOKEN = `${CARE_TIME_CATEGORY_SYSTEM}|care-time`;
/**
 * Single observation code for all care-time entries.
 * @agentic @benchmark/core
 */ const CARE_TIME_OBSERVATION_CODE_VALUE = "care-management-minutes";
/**
 * Source-surface method options carried on `Observation.method` (D3 LOCKED).
 * @agentic @benchmark/core
 */ const CARE_TIME_METHOD_OPTIONS = [
    {
        code: "task-completion",
        display: "Task Completion"
    },
    {
        code: "progress-update",
        display: "Progress Update"
    },
    {
        code: "action-list-quick-add",
        display: "Action List Quick Add"
    },
    {
        code: "patient-profile-quick-add",
        display: "Patient Profile Quick Add"
    },
    {
        code: "intake-call",
        display: "Intake Call"
    },
    {
        code: "intake-documentation",
        display: "Intake Documentation"
    },
    {
        code: "cocm-followup",
        display: "CoCM Follow-Up"
    },
    {
        code: "general-interaction",
        display: "General Interaction"
    },
    {
        code: "call",
        display: "Call"
    },
    {
        code: "sms",
        display: "SMS"
    }
];
/**
 * Set of all valid care-time methods for fast membership checks.
 * @agentic @benchmark/core
 */ new Set(CARE_TIME_METHOD_OPTIONS.map((o)=>o.code));
/**
 * Complete roster of every assessment instrument that can enter a patient's
 * chart, mapped to the code its score Observation carries. This is the ONE
 * place that enumerates all instruments; billing eligibility is derived from it
 * as a denylist (see {@link BILLING_INELIGIBLE_ASSESSMENTS}) so a newly added
 * instrument is never silently billable — it must be classified here.
 *
 * Observations are clinical chart data first; billing eligibility is a separate
 * decision this module owns. A `code: ""` entry is a categorical instrument
 * (PGI-S, PGI-C, Safety Plan, Drug Use) that emits NO numeric score
 * Observation; every such instrument is billing-ineligible and must appear in
 * {@link BILLING_INELIGIBLE_ASSESSMENTS}.
 *
 * This list is source-agnostic: eligibility reads Observations by code, so a
 * matching score unlocks billing whether it was written by the in-app
 * clinician encounter, a fixed remote survey link, or the adaptive screener.
 *
 * Two eligibility nuances the code-based list CANNOT enforce on its own:
 *   - AUDIT-C: only a FULLY administered AUDIT-C is billing-valid (2026-09-30).
 *     A gated "Never" first item is a genuine complete 0/12 and counts; an
 *     abandoned/partial administration must NOT emit a score Observation. Both
 *     share the one AUDIT-C score code, so completeness has to be enforced
 *     where the Observation is written, not here. Follow-up tracked on the
 *     billing-parity ticket.
 *   - Legacy PHQ-2 (deprecated LOINC 55758-7) is never billing-valid. It is not
 *     an administered instrument today, so it is intentionally absent from this
 *     roster; the lock test guards that its code never appears in the eligible
 *     list.
 * @agentic @benchmark/core
 */ const ALL_ASSESSMENTS = {
    PHQ4: OBSERVATION_LOINC_CODES.PHQ4_TOTAL_SCORE,
    PHQ9: OBSERVATION_LOINC_CODES.PHQ9_TOTAL_SCORE,
    PHQ9A: OBSERVATION_LOINC_CODES.PHQ9A_TOTAL_SCORE,
    GAD7: OBSERVATION_LOINC_CODES.GAD7_TOTAL_SCORE,
    GAD2: OBSERVATION_LOINC_CODES.GAD2_TOTAL_SCORE,
    PC_PTSD_5: OBSERVATION_LOINC_CODES.PC_PTSD_5_TOTAL_SCORE,
    PSC_17: OBSERVATION_LOINC_CODES.PSC_17_TOTAL_SCORE,
    C_SSRS: OBSERVATION_LOINC_CODES.C_SSRS_SEVERITY,
    ASRS: OBSERVATION_LOINC_CODES.ASRS_TOTAL_SCORE,
    OCI_4: OBSERVATION_LOINC_CODES.OCI_4_TOTAL_SCORE,
    TAPS_1: OBSERVATION_LOINC_CODES.TAPS_1_TOTAL_SCORE,
    TAPS_2: OBSERVATION_LOINC_CODES.TAPS_2_TOTAL_SCORE,
    SBIRT_ED: OBSERVATION_LOINC_CODES.SBIRT_ED_TOTAL_SCORE,
    AUDIT_C: OBSERVATION_LOINC_CODES.AUDIT_C_TOTAL_SCORE,
    UCLA3: OBSERVATION_LOINC_CODES.UCLA3_TOTAL_SCORE,
    WSAS: OBSERVATION_LOINC_CODES.WSAS_TOTAL_SCORE,
    WPAI: OBSERVATION_LOINC_CODES.WPAI_TOTAL_SCORE,
    WHO5: OBSERVATION_LOINC_CODES.WHO5_TOTAL_SCORE,
    SDOH: OBSERVATION_LOINC_CODES.SDOH_TOTAL_SCORE,
    MIRAH_COMPLEXITY: OBSERVATION_LOINC_CODES.MIRAH_COMPLEXITY_SCORE,
    MIRAH_SEVERITY: OBSERVATION_LOINC_CODES.MIRAH_SEVERITY_SCORE,
    NPS: OBSERVATION_LOINC_CODES.NPS_SCORE,
    PGI_S: "",
    PGI_C: "",
    SAFETY_PLAN: "",
    DRUG_USE: ""
};
/**
 * Instruments that are administered but are NOT billing-valid for CoCM
 * eligibility (D18). This is the denylist over {@link ALL_ASSESSMENTS}: every
 * key here is subtracted to produce {@link BILLING_ELIGIBLE_ASSESSMENTS}. Each
 * entry names WHY it is excluded so the decision stays auditable. Confirmed
 * with clinical/compliance (2026-06-22, refined 2026-09-30).
 *
 * Note: an instrument absent from BOTH this denylist and the eligible set can
 * only happen if a new key is added to ALL_ASSESSMENTS without classifying it;
 * the exact-set lock test in `care-time.constants.test.ts` fails loudly in that
 * case rather than letting an unreviewed instrument become billable.
 * @agentic @benchmark/core
 */ const BILLING_INELIGIBLE_ASSESSMENTS = {
    PHQ4: "Ultra-brief combined screener; not a standalone severity measure.",
    GAD2: "Two-item anxiety short check; not billing-valid (opens GAD-7, which is).",
    UCLA3: "Loneliness scale; outside the billing-valid clinical set.",
    WSAS: "Everyday-functioning scale; not a clinical severity measure.",
    WPAI: "Work productivity/activity; functional, not clinical severity.",
    WHO5: "Well-being index; not a billing-valid severity measure.",
    SDOH: "Social-needs screen; not a clinical severity measure.",
    MIRAH_COMPLEXITY: "Vendor composite score; not a billing-valid instrument.",
    MIRAH_SEVERITY: "Vendor composite score; not a billing-valid instrument.",
    NPS: "Member-feedback likelihood score; not clinical.",
    PGI_S: "Categorical patient self-rating; no numeric score Observation.",
    PGI_C: "Categorical patient self-rating; no numeric score Observation.",
    SAFETY_PLAN: "Care-planning artifact; no score Observation.",
    DRUG_USE: "House-modified categorical drug screen; not billing-valid (2026-09-30) and no numeric score Observation."
};
// Eligible-by-policy = every instrument NOT on the denylist. Confirmed valid
// (2026-09-30): PHQ-9, PHQ-9A, GAD-7, PC-PTSD-5, PSC-17, C-SSRS, ASRS, OCI-4,
// TAPS-1, TAPS-2, SBIRT-ED, and AUDIT-C (full administration only). Every one
// carries a real score code; the `.filter` below is a guard, and the lock test
// asserts no eligible-by-policy instrument is categorical (`code: ""`) — a
// categorical instrument must be denied above, never silently dropped here.
const BILLING_ELIGIBLE_POLICY_KEYS = Object.keys(ALL_ASSESSMENTS).filter((key)=>!(key in BILLING_INELIGIBLE_ASSESSMENTS));
/**
 * The single billing-eligible assessment list, honored regardless of which
 * entry surface (in-app encounter, remote survey, adaptive screener) wrote the
 * score. Derived as {@link ALL_ASSESSMENTS} minus
 * {@link BILLING_INELIGIBLE_ASSESSMENTS}, then narrowed to instruments that
 * actually emit a score Observation (non-empty code). Each entry's system is
 * resolved the same way `buildObservationFromScore` stamps the score
 * Observation (`getObservationCoding(code)?.system ?? "http://loinc.org"`), so
 * the eligibility search always matches what questionnaire completion writes.
 * Within the 6-week freshness window these unlock 99492 / 99493 / 99494.
 * @agentic @benchmark/core
 */ const BILLING_ELIGIBLE_ASSESSMENTS = BILLING_ELIGIBLE_POLICY_KEYS.map((key)=>ALL_ASSESSMENTS[key]).filter((code)=>code !== "").map((code)=>({
        system: getObservationCoding(code)?.system ?? "http://loinc.org",
        code
    }));
/**
 * Codes-only view of {@link BILLING_ELIGIBLE_ASSESSMENTS} for value comparisons
 * and tests.
 * @agentic @benchmark/core
 */ BILLING_ELIGIBLE_ASSESSMENTS.map((e)=>e.code);
/**
 * System-aware comma-delimited token search expression for the eligible
 * assessments (FHIR uses comma for OR within a single search parameter, and
 * `system|code` for the system-qualified token). Supports mixed LOINC and
 * organizational systems. Used by the eligibility service and the registry hook.
 * @agentic @benchmark/core
 */ const COCM_ELIGIBLE_ASSESSMENT_CODES_TOKEN = BILLING_ELIGIBLE_ASSESSMENTS.map((e)=>`${e.system}|${e.code}`).join(",");
/**
 * Assessment freshness window for CoCM billing eligibility (D18 — 6 weeks).
 * Applied uniformly to every rule that requires an assessment.
 * @agentic @benchmark/core
 */ const COCM_ASSESSMENT_FRESHNESS_WEEKS = 6;

/** Composition extension URL carrying the originating Dialpad call id. */ const DIALPAD_CALL_ID_EXTENSION_URL = "https://benchmark.health/fhir/StructureDefinition/dialpad-call-id";
/** Page size used for the activity-log drawer queries. @agentic @benchmark/core */ const CARE_TIME_PAGE_SIZE = 50;
/** Hard upper bound for "all entries this month" queries. @agentic @benchmark/core */ const CARE_TIME_MAX_PER_MONTH = 200;
/** Default status filter applied when soft-deleted entries are excluded. */ const ACTIVE_STATUS_TOKEN = "final,amended,corrected,preliminary";
/**
 * Build a tuple-array search payload for a care-time Observation query.
 *
 * Tuples (rather than `Record<string, string>`) are required because
 * date-range queries emit two `date` parameters (`ge…` and `le…`) — a
 * record with the same key twice would collapse to a single entry, and
 * the previous "join with `&date=`" workaround caused URL-encoding to
 * percent-escape the literal `&` and `=`. Mirrors the pattern in
 * `task.service.ts:buildDueTodayTaskSearchParams`.
 * @agentic @benchmark/core
 */ const buildCareTimeSearchTuples = (patientId, options = {})=>{
    const tuples = [
        [
            "patient",
            patientId
        ],
        [
            "category",
            CARE_TIME_CATEGORY_TOKEN
        ],
        [
            "code",
            `${CARE_TIME_CODE_SYSTEM}|${CARE_TIME_OBSERVATION_CODE_VALUE}`
        ],
        [
            "_sort",
            "-date"
        ],
        [
            "_count",
            String(options.count ?? CARE_TIME_PAGE_SIZE)
        ],
        [
            "_total",
            "accurate"
        ]
    ];
    if (!options.includeSoftDeleted) {
        // FHIR allows comma-OR within a single search parameter. Exclude
        // `entered-in-error` by allowlisting the statuses we count.
        tuples.push([
            "status",
            ACTIVE_STATUS_TOKEN
        ]);
    }
    if (options.startDate) {
        tuples.push([
            "date",
            `ge${options.startDate}`
        ]);
    }
    if (options.endDate) {
        tuples.push([
            "date",
            `le${options.endDate}`
        ]);
    }
    if (options.offset !== undefined) {
        tuples.push([
            "_offset",
            String(options.offset)
        ]);
    }
    return tuples;
};
/**
 * Search care-time Observations for a patient, optionally constrained to
 * a date range. Returns the full Observation list (caller paginates).
 * @agentic @benchmark/core
 */ const searchCareTimeByPatient = async (medplum, patientId, options = {})=>{
    const tuples = buildCareTimeSearchTuples(patientId, options);
    // `MedplumClient.search` accepts `URLSearchParams`; constructing it
    // from the tuples preserves duplicate `date` keys under URL encoding
    // so the FHIR server receives `?date=ge…&date=le…` (AND filter).
    const params = new URLSearchParams(tuples);
    const bundle = await medplum.search("Observation", params);
    return bundle.entry?.map((e)=>e.resource).filter(Boolean) ?? [];
};
/**
 * Convenience helper for "all care-time entries in a calendar month".
 *
 * The bounds are inclusive of the month edges; the caller is responsible
 * for constructing ISO date strings that anchor at the appropriate
 * timezone (the widget hook uses local-timezone month boundaries).
 * @agentic @benchmark/core
 */ const searchCareTimeByPatientAndMonth = async (medplum, patientId, monthStart, monthEnd)=>searchCareTimeByPatient(medplum, patientId, {
        startDate: monthStart,
        endDate: monthEnd,
        count: CARE_TIME_MAX_PER_MONTH
    });
/**
 * Fetch specific care-time Observations by id, oldest first.
 *
 * Used to build a billing report's activity log from the exact entries a
 * closed monthly billing record froze (`contributingObservationIds`), so the
 * pasted log stays consistent with the frozen header even after month close
 * or billing-window drift. Returns `[]` for an empty id list without a query.
 * @agentic @benchmark/core
 */ const searchCareTimeByIds = async (medplum, ids)=>{
    if (ids.length === 0) return [];
    const bundle = await searchResources(medplum, "Observation", {
        _id: ids.join(","),
        category: CARE_TIME_CATEGORY_TOKEN,
        status: ACTIVE_STATUS_TOKEN,
        _count: String(ids.length),
        _sort: "date"
    });
    return bundle.entry?.map((e)=>e.resource).filter(Boolean) ?? [];
};
/**
 * Find any care-time Observation already derived from a given
 * Communication. Used by the Dialpad auto-creator to prevent
 * double-writes on webhook retries, and by the intake completion
 * flow to update the existing intake-call entry instead of creating
 * a second one (D4).
 *
 * Returns the first match (queries are sorted newest-first; in
 * practice there should be at most one). Soft-deleted (`entered-
 * in-error`) entries are excluded so a previously-deleted intake-call
 * entry is not rediscovered and overwritten.
 * @agentic @benchmark/core
 */ const findCareTimeForCommunication = async (medplum, communicationId)=>{
    const bundle = await searchResources(medplum, "Observation", {
        "derived-from": `Communication/${communicationId}`,
        category: CARE_TIME_CATEGORY_TOKEN,
        status: ACTIVE_STATUS_TOKEN,
        _count: "1",
        _sort: "-date"
    });
    const entry = bundle.entry?.[0]?.resource;
    return entry;
};
/**
 * Resolve the intake call's Dialpad Communication and any existing care-time
 * Observation already derived from it, for a scribe `Composition`.
 *
 * Used by the encounter upsert action to dedupe the intake-call care-time
 * against the Dialpad webhook tee's auto-created `call` entry: both reference
 * the same call, so linking the intake-call Observation to this Communication
 * (and reusing the tee entry's id when present) collapses them into one
 * entry instead of double-counting the call minutes.
 *
 * Returns an empty object when the Composition carries no Dialpad call id or
 * no matching Communication exists.
 * @agentic @benchmark/core
 */ const resolveIntakeCallCareTimeLink = async (medplum, composition)=>{
    const callId = composition.extension?.find((ext)=>ext.url === DIALPAD_CALL_ID_EXTENSION_URL)?.valueString;
    if (!callId) return {};
    const bundle = await searchResources(medplum, "Communication", {
        identifier: `${IDENTIFIER_SYSTEM.DIALPAD_CALL_ID}|${callId}`,
        _count: "1",
        _sort: "-sent"
    });
    const communicationId = bundle.entry?.[0]?.resource?.id;
    if (!communicationId) return {};
    const existing = await findCareTimeForCommunication(medplum, communicationId);
    return {
        communicationId,
        existingObservationId: existing?.id
    };
};
/**
 * Sum the billable-eligible minutes across a set of care-time Observations:
 * entries that are neither soft-deleted (`entered-in-error`/`cancelled`) nor
 * explicitly flagged non-billable (the `care-time-non-billable` extension).
 *
 * This is the attestation-time definition of "what is actually billed" (D-2):
 * the CLI close-month freeze and the submit action net out flagged minutes so
 * the frozen `attestedMinutes` reflects billed minutes, not raw logged minutes.
 * No windowing is applied — callers pass the exact contributing entries.
 * @agentic @benchmark/core
 */ const sumBillableCareTimeMinutes = (entries)=>entries.filter((obs)=>obs.status !== "entered-in-error" && obs.status !== "cancelled" && !isCareTimeNonBillable(obs)).reduce((total, obs)=>{
        // A non-finite `valueQuantity.value` (corrupted entry) contributes 0 so
        // one bad row can't poison the frozen attested total into `NaN`.
        const value = obs.valueQuantity?.value;
        return total + (typeof value === "number" && Number.isFinite(value) ? value : 0);
    }, 0);
/**
 * Fetch active care-time Observations whose `focus` is the given Task,
 * newest first. Used by the unified progress + care-time history so a
 * Task's logged time appears alongside its progress Communications.
 * Soft-deleted (`entered-in-error`) entries are excluded.
 * @agentic @benchmark/core
 */ const getCareTimeForTask = async (medplum, taskId)=>{
    const bundle = await searchResources(medplum, "Observation", {
        focus: `Task/${taskId}`,
        category: CARE_TIME_CATEGORY_TOKEN,
        status: ACTIVE_STATUS_TOKEN,
        _sort: "-date",
        _count: String(CARE_TIME_PAGE_SIZE)
    });
    return bundle.entry?.map((e)=>e.resource).filter(Boolean) ?? [];
};

/** Identifier system for the `{patientId}|{YYYY-MM}` composite key. @agentic @benchmark/core */ const COCM_BILLING_PACKET_IDENTIFIER_SYSTEM = `${ORGANIZATION_BASE}/identifier-system/cocm-billing-packet`;
/** Identifier system for the ChargeItem `{patientId}|{YYYY-MM}|{code}|{n}` key. @agentic @benchmark/core */ const COCM_CHARGE_ITEM_IDENTIFIER_SYSTEM = `${ORGANIZATION_BASE}/identifier-system/cocm-charge-item`;
/** Build the composite identifier value for a patient-month packet. @agentic @benchmark/core */ const buildCoCMBillingPacketIdentifierValue = (patientId, billingMonth)=>`${patientId}|${billingMonth}`;
/** `system|value` token for a packet identifier search. @agentic @benchmark/core */ const buildCoCMBillingPacketIdentifierToken = (patientId, billingMonth)=>`${COCM_BILLING_PACKET_IDENTIFIER_SYSTEM}|${buildCoCMBillingPacketIdentifierValue(patientId, billingMonth)}`;
/**
 * Identifier-value prefix (`{patientId}|{YYYY-MM}|`) shared by every ChargeItem
 * of a patient-month, used to scope a reconciliation search to that month.
 * @agentic @benchmark/core
 */ const cocmChargeItemMonthPrefix = (patientId, billingMonth)=>`${buildCoCMBillingPacketIdentifierValue(patientId, billingMonth)}|`;

/** Page size for the ChargeItem history scan. */ const CHARGE_ITEM_PAGE_SIZE$1 = 100;
/** Hard page cap so a looping/broken server response can't spin forever. */ const CHARGE_ITEM_MAX_PAGES$1 = 100;
const EMPTY_FACTS = {
    anyCodeBilledBefore: false,
    billed99492Before: false,
    earliestBilledWasG2214: false
};
/**
 * Codes that define a billed month for classification. 99494 is only ever an
 * add-on to a base code, so it is not billing-defining on its own; a stray
 * 99494 must not by itself mark a month as billed.
 */ const BILLING_DEFINING_CODES = new Set([
    "G2214",
    "99492",
    "99493"
]);
/**
 * Parse a CoCM ChargeItem identifier of the EXACT shape
 * `{patientId}|{YYYY-MM}|{code}|{n}`, validating the patient id, month format,
 * a billing-defining code, and a numeric occurrence index. Anything else
 * (missing/extra segments, foreign patient id, non-CoCM code, add-on 99494) is
 * ignored so a malformed or add-on line-item can't corrupt the history facts.
 */ const parseChargeItemIdentifier = (chargeItem, patientId)=>{
    const value = chargeItem.identifier?.find((i)=>i.system === COCM_CHARGE_ITEM_IDENTIFIER_SYSTEM)?.value;
    if (!value) return undefined;
    const parts = value.split("|");
    if (parts.length !== 4) return undefined;
    const [pid, month, code, index] = parts;
    if (pid !== patientId) return undefined;
    if (!/^\d{4}-\d{2}$/.test(month)) return undefined;
    if (!BILLING_DEFINING_CODES.has(code)) return undefined;
    if (!/^\d+$/.test(index)) return undefined;
    return {
        month,
        code
    };
};
/**
 * Read the patient's authoritative CoCM billing history for months strictly
 * before `currentMonth` (`YYYY-MM`). Scans persisted ChargeItems (reopened
 * months delete their ChargeItems, so a reopened month is correctly treated as
 * not-yet-billed). Returns {@link EMPTY_FACTS} when the patient has no prior
 * billed months.
 * @agentic @benchmark/core
 */ const getPriorBilledCodeHistory = async (medplum, patientId, currentMonth)=>{
    const codesByMonth = new Map();
    for(let page = 0; page < CHARGE_ITEM_MAX_PAGES$1; page++){
        const bundle = await searchResources(medplum, "ChargeItem", {
            subject: `Patient/${patientId}`,
            _count: String(CHARGE_ITEM_PAGE_SIZE$1),
            _offset: String(page * CHARGE_ITEM_PAGE_SIZE$1),
            // Stable ordering so offset paging can't skip or repeat a row.
            _sort: "_id"
        });
        const items = (bundle.entry ?? []).map((e)=>e.resource).filter(Boolean);
        for (const item of items){
            const parsed = parseChargeItemIdentifier(item, patientId);
            if (!parsed) continue;
            // Lexicographic compare is correct for `YYYY-MM`. Strictly prior only.
            if (parsed.month >= currentMonth) continue;
            const set = codesByMonth.get(parsed.month) ?? new Set();
            set.add(parsed.code);
            codesByMonth.set(parsed.month, set);
        }
        if (items.length < CHARGE_ITEM_PAGE_SIZE$1) break;
    }
    if (codesByMonth.size === 0) return EMPTY_FACTS;
    let anyCodeBilledBefore = false;
    let billed99492Before = false;
    for (const codes of codesByMonth.values()){
        if (codes.size > 0) anyCodeBilledBefore = true;
        if (codes.has("99492")) billed99492Before = true;
    }
    // Earliest prior billed month: was it G2214-only (no base code)?
    const earliestMonth = [
        ...codesByMonth.keys()
    ].sort()[0];
    const earliestCodes = codesByMonth.get(earliestMonth) ?? new Set();
    const earliestBilledWasG2214 = earliestCodes.has("G2214") && !earliestCodes.has("99492") && !earliestCodes.has("99493");
    return {
        anyCodeBilledBefore,
        billed99492Before,
        earliestBilledWasG2214
    };
};

/**
 * CoCM billing rules — versioned, frozen table.
 *
 * Implements the brief's "Billing Rules Table" verbatim:
 *  - Initial month, assessments present (D14 + D18)
 *  - Subsequent month, assessments present (D13 cascade applies)
 *  - Any month, no eligible assessment (G2214-only)
 *
 * Bounds use the brief's "16 lower bound uniformly" convention (see
 * Boundary precision flag). If the user later confirms 15 for
 * subsequent months, only the two cells in `SUBSEQUENT_WITH_ASSESSMENT`
 * and `NO_ASSESSMENT` change.
 *
 * @agentic @benchmark/core
 */ /** Version stamp included on any copied billing report (D11). @agentic @benchmark/core */ const COCM_BILLING_RULES_VERSION = "COCM_BILLING_RULES_V1";
/**
 * Initial month with eligible assessment (D14).
 * 99492-based ladder.
 * @agentic @benchmark/core
 */ const INITIAL_WITH_ASSESSMENT = [
    {
        minMinutes: 0,
        maxMinutes: 15,
        codes: [],
        packageDisplay: ""
    },
    {
        minMinutes: 16,
        maxMinutes: 35,
        codes: [
            "G2214"
        ],
        packageDisplay: "G2214"
    },
    {
        minMinutes: 36,
        maxMinutes: 85,
        codes: [
            "99492"
        ],
        packageDisplay: "99492"
    },
    {
        minMinutes: 86,
        maxMinutes: 115,
        codes: [
            "99492",
            "99494"
        ],
        packageDisplay: "99492 + 99494"
    },
    {
        minMinutes: 116,
        maxMinutes: 145,
        codes: [
            "99492",
            "99494",
            "99494"
        ],
        packageDisplay: "99492 + 99494×2"
    },
    {
        minMinutes: 146,
        maxMinutes: 175,
        codes: [
            "99492",
            "99494",
            "99494",
            "99494"
        ],
        packageDisplay: "99492 + 99494×3"
    },
    {
        minMinutes: 176,
        maxMinutes: null,
        codes: [
            "99492",
            "99494",
            "99494",
            "99494",
            "99494"
        ],
        packageDisplay: "99492 + 99494×4"
    }
];
/**
 * Subsequent month with eligible assessment.
 * 99493-based ladder. Also used as the cascade ladder per D13 when the
 * episode's first bill was G2214.
 * @agentic @benchmark/core
 */ const SUBSEQUENT_WITH_ASSESSMENT = [
    {
        minMinutes: 0,
        maxMinutes: 15,
        codes: [],
        packageDisplay: ""
    },
    {
        minMinutes: 16,
        maxMinutes: 30,
        codes: [
            "G2214"
        ],
        packageDisplay: "G2214"
    },
    {
        minMinutes: 31,
        maxMinutes: 75,
        codes: [
            "99493"
        ],
        packageDisplay: "99493"
    },
    {
        minMinutes: 76,
        maxMinutes: 105,
        codes: [
            "99493",
            "99494"
        ],
        packageDisplay: "99493 + 99494"
    },
    {
        minMinutes: 106,
        maxMinutes: 135,
        codes: [
            "99493",
            "99494",
            "99494"
        ],
        packageDisplay: "99493 + 99494×2"
    },
    {
        minMinutes: 136,
        maxMinutes: 165,
        codes: [
            "99493",
            "99494",
            "99494",
            "99494"
        ],
        packageDisplay: "99493 + 99494×3"
    },
    {
        minMinutes: 166,
        maxMinutes: null,
        codes: [
            "99493",
            "99494",
            "99494",
            "99494",
            "99494"
        ],
        packageDisplay: "99493 + 99494×4"
    }
];
/**
 * Any month, no eligible assessment in window (D18).
 * Only G2214 may fire without an assessment.
 * @agentic @benchmark/core
 */ const NO_ASSESSMENT = [
    {
        minMinutes: 0,
        maxMinutes: 15,
        codes: [],
        packageDisplay: ""
    },
    {
        minMinutes: 16,
        maxMinutes: null,
        codes: [
            "G2214"
        ],
        packageDisplay: "G2214"
    }
];
/**
 * Care units per billed code — the single TypeScript source of truth, aligned
 * with the dbt `billing_codes` seed (D24): 99492 / 99493 = 1.0 (base month),
 * 99494 = 0.5 (each 30-min add-on), G2214 = 0.25 (GBHI). Summing these over a
 * code package yields the packet's `careUnits`.
 *
 * NOTE (D24): the value for G2214 is 0.25, matching `billing_codes.csv` and both
 * `fct_*_cocm_billing` models. Two documentation strings historically said 0.5;
 * those are the stale ones, not this.
 * @agentic @benchmark/core
 */ const CARE_UNITS_BY_CODE = {
    "99492": 1.0,
    "99493": 1.0,
    "99494": 0.5,
    G2214: 0.25
};
/**
 * Sum the care units for a billed code package. Empty package = 0.
 * @agentic @benchmark/core
 */ const careUnitsForCodes = (codes)=>codes.reduce((sum, code)=>sum + (CARE_UNITS_BY_CODE[code] ?? 0), 0);

/**
 * v1 assessments-used list. `computeCoCMEligibility` surfaces the freshness
 * signal + the latest eligible assessment date; the fuller per-instrument list
 * is a D26 fast-follow. When an eligible assessment exists we record the latest
 * one as the criterion that satisfied the 99492/99493 requirement.
 */ const assessmentsUsedFromSummary = (summary)=>summary.hasEligibleAssessment && summary.latestAssessmentDate ? [
        {
            code: "cocm-eligible",
            date: summary.latestAssessmentDate
        }
    ] : [];
/**
 * Assemble the versioned billing-code packet for a patient-month. Pure.
 * @agentic @benchmark/core
 */ const buildBillingCodePacket = (input)=>{
    const { patientId, billingMonth, summary, attribution } = input;
    const codes = [
        ...summary.billingResult.codes
    ];
    const enrollmentWindow = summary.cocmPeriodStart || summary.cocmPeriodEnd || summary.cocmCarePlanId ? {
        start: summary.cocmPeriodStart,
        end: summary.cocmPeriodEnd,
        carePlanId: summary.cocmCarePlanId
    } : undefined;
    return {
        packetVersion: 1,
        rulesVersion: COCM_BILLING_RULES_VERSION,
        patientId,
        billingMonth,
        codes,
        packageDisplay: summary.billingResult.packageDisplay,
        careUnits: careUnitsForCodes(codes),
        billableMinutes: summary.billableMinutes,
        totalMinutes: summary.totalMinutes,
        isInitialMonth: summary.isInitialMonth,
        episodeFirstBillWasG2214: summary.episodeFirstBillWasG2214,
        assessmentsUsed: assessmentsUsedFromSummary(summary),
        hasEligibleAssessment: summary.hasEligibleAssessment,
        ...enrollmentWindow ? {
            enrollmentWindow
        } : {},
        ...attribution ? {
            attribution
        } : {},
        generatedAt: input.generatedAt ?? new Date().toISOString()
    };
};

/**
 * Returns display name for practitioner from formatted name, parts, or ID fallback.
 * @agentic @benchmark/core
 */ const getPractitionerDisplayName = (practitioner)=>{
    // Try formatted name first
    const formattedName = formatHumanName(practitioner.name?.[0]);
    if (formattedName) return formattedName;
    // Try individual name parts
    const name = practitioner.name?.[0];
    if (name) {
        const parts = [];
        if (name.prefix?.length) parts.push(...name.prefix);
        if (name.given?.length) parts.push(...name.given);
        if (name.family) parts.push(name.family);
        if (parts.length > 0) return parts.join(" ");
    }
    // Fallback to ID-based display
    return `Practitioner ${practitioner.id}`;
};

/**
 * OCPE Phase 1: Taxonomy Compatibility Mapping
 *
 * Central mapping module for legacy-to-v2 activity/task taxonomy translation.
 * Supports display preference (v2 labels wherever possible) and deprecation checks.
 */ /**
 * Legacy activity code → v2 activity code mapping
 */ const ACTIVITY_CODE_MAP = {
    "initial-contact": "schedule-intake",
    "intake-assessment": "complete-intake",
    "care-plan-selection": "finalize-care-plan-enrollment",
    "enroll-in-care-plan": "finalize-care-plan-enrollment",
    "resource-offering": "offer-services",
    "appointment-scheduling": "schedule-services",
    "appointment-completion": "follow-up-on-services",
    "mirah-enrollment": "enroll-in-mirah",
    "cocm-intake": "complete-intake",
    "patient-check-in": "follow-up-on-cocm",
    "psychiatric-consultation": "consult-psych",
    "pcp-consultation": "consult-pcp",
    discharge: "discharge-member",
    // Old v2 codes → new v2 codes (safety net for staging/branch data)
    "select-services-for-referral": "select-services",
    "offer-referral": "offer-services",
    "confirm-referral-services": "confirm-services",
    "initiate-referral": "schedule-services",
    "follow-up-on-referral": "follow-up-on-services"
};
/**
 * Deprecated activity codes (no v2 equivalent, should not be created)
 * Display overrides for legacy data rendering
 */ /**
 * Deprecated plan-backed ad-hoc activity; use standalone custom tasks instead.
 * @agentic @benchmark/core
 */ const DEPRECATED_CUSTOM_CARE_PLAN_ACTIVITY_CODE = "custom-activity";
const DEPRECATED_ACTIVITY_DISPLAYS = {
    [DEPRECATED_CUSTOM_CARE_PLAN_ACTIVITY_CODE]: "Custom Activity (deprecated)",
    "resource-acceptance": "Resource Acceptance",
    "needs-evaluation": "Needs Evaluation",
    "ehr-documentation": "EHR Documentation",
    "time-logging": "Time Logging",
    "chart-review": "Chart Review"
};
/**
 * Standalone task type migration
 * - deprecated: Stop creating; maps to plan-backed activities or is superseded
 * - no-change: Remains standalone (custom, fax workflow tasks)
 */ const STANDALONE_TASK_MIGRATION_MAP = {
    "new-member-intake": {
        action: "deprecated",
        display: "Complete New Member Intake"
    },
    "offer-member-resources": {
        action: "deprecated",
        display: "Offer Resources to Member"
    },
    "schedule-member-appointment": {
        action: "deprecated",
        display: "Schedule Appointment for Member"
    },
    "member-check-in": {
        action: "deprecated",
        newActivityCode: "check-in-on-member"
    },
    custom: {
        action: "no-change",
        display: "Custom Task"
    },
    "review-new-referral": {
        action: "no-change",
        display: "🔔 NEW REFERRAL"
    },
    "potential-duplicate-review": {
        action: "no-change",
        display: "Review New Referral: Potential Duplicate"
    },
    "manual-review-fax": {
        action: "no-change",
        display: "Review New Referral: Complete Insurance & Verification"
    },
    "review-unmatched-fax": {
        action: "no-change",
        display: "Review Unmatched Fax: No Patient Found"
    },
    "post-care-followup": {
        action: "deprecated",
        display: "Complete Post-Care Followup"
    },
    "cadenced-followup": {
        action: "deprecated",
        display: "Complete Cadenced Followup"
    },
    "send-nps-survey": {
        action: "deprecated",
        display: "Send NPS Survey"
    }
};
/**
 * V2 activity code → display label
 * Canonical display strings for all v2 activity codes
 */ const V2_ACTIVITY_DISPLAYS = {
    // Intake
    "schedule-intake": "Schedule Intake",
    "complete-intake": "Complete Intake",
    "finalize-care-plan-enrollment": "Finalize Care Plan Enrollment",
    "check-in-on-member": "Check In on Member",
    // Triage
    "select-services": "Select Services",
    "approve-selected-services": "Approve Selected Services",
    "request-approval": "Approve Selected Services",
    "offer-services": "Offer Services to Member",
    "confirm-services": "Confirm Service Selections",
    "schedule-services": "Schedule Services",
    "follow-up-on-services": "Follow Up on Services",
    "complete-cadenced-followup": "Complete Cadenced Followup",
    // CoCM
    "enroll-in-mirah": "Confirm Mirah Enrollment",
    "build-cocm-care-plan": "Build CoCM Care Plan",
    "follow-up-on-cocm": "Follow Up on CoCM Progress",
    "consult-psych": "Consult Psychiatrist",
    "consult-pcp": "Consult PCP",
    "discharge-member": "Discharge Member"
};
/**
 * Resolve legacy or standalone task code to v2 activity code
 */ const resolveActivityCode = (code)=>ACTIVITY_CODE_MAP[code] ?? code;
/**
 * Resolve any code (activity or standalone task type) to v2 display label.
 * Prefer v2 labels wherever possible for consistent UI rendering.
 */ const resolveActivityDisplay = (code)=>{
    // Standalone task type with explicit newActivityCode → use v2 activity display
    const standaloneEntry = STANDALONE_TASK_MIGRATION_MAP[code];
    if (standaloneEntry?.newActivityCode) {
        return V2_ACTIVITY_DISPLAYS[standaloneEntry.newActivityCode] ?? code;
    }
    // Standalone task type with display override (no-change or deprecated fallback)
    if (standaloneEntry?.display) {
        return standaloneEntry.display;
    }
    // Deprecated activity codes: use legacy display for rendering
    if (DEPRECATED_ACTIVITY_DISPLAYS[code]) {
        return DEPRECATED_ACTIVITY_DISPLAYS[code];
    }
    // Activity code: resolve legacy → v2, then get display
    const v2Code = resolveActivityCode(code);
    return V2_ACTIVITY_DISPLAYS[v2Code] ?? code;
};
/**
 * Check if a standalone task type is deprecated (should not be created)
 */ const isDeprecatedStandaloneTaskType = (code)=>STANDALONE_TASK_MIGRATION_MAP[code]?.action === "deprecated";

/**
 * ActivityDefinition Type System
 * Custom system for activity definition codes
 */ const ACTIVITY_DEFINITION_TYPE_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/activity-type";
/**
 * ActivityDefinition Options (v2 canonical taxonomy)
 * Defines all available activity definitions for NEW CarePlans.
 * Legacy codes remain in taxonomy-mapping.ts for backwards-compatible display.
 */ const ACTIVITY_DEFINITION_OPTIONS = [
    // Intake Protocol Activities
    {
        code: "schedule-intake",
        display: "Schedule Intake",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Make initial contact with the member to schedule an intake session.",
        canonical: "ActivityDefinition/schedule-intake",
        protocol: "intake"
    },
    {
        code: "complete-intake",
        display: "Complete Intake",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Complete the initial clinical intake session.",
        canonical: "ActivityDefinition/complete-intake",
        protocol: "intake"
    },
    {
        code: "finalize-care-plan-enrollment",
        display: "Finalize Care Plan Enrollment",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Finalize care plan enrollment after clinical intake session is completed.",
        canonical: "ActivityDefinition/finalize-care-plan-enrollment",
        protocol: "intake"
    },
    {
        code: "check-in-on-member",
        display: "Check In on Member",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Check in on member to assess progress or needs.",
        canonical: "ActivityDefinition/check-in-on-member",
        protocol: "intake"
    },
    // Triage Protocol Activities
    {
        code: "select-services",
        display: "Select Services",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Select external services that match the member's documented service needs.",
        canonical: "ActivityDefinition/select-services",
        protocol: "triage"
    },
    {
        code: "approve-selected-services",
        display: "Approve Selected Services",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Review the services selected by SpecOps for your member.",
        canonical: "ActivityDefinition/approve-selected-services",
        protocol: "triage"
    },
    {
        code: "offer-services",
        display: "Offer Services to Member",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Present the selected services to the member and discuss options.",
        canonical: "ActivityDefinition/offer-services",
        protocol: "triage"
    },
    {
        code: "confirm-services",
        display: "Confirm Service Selections",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Confirm the member's acceptance of offered services before scheduling.",
        canonical: "ActivityDefinition/confirm-services",
        protocol: "triage"
    },
    {
        code: "schedule-services",
        display: "Schedule Services",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Schedule appointment(s) with external service(s) on behalf of the member.",
        canonical: "ActivityDefinition/schedule-services",
        protocol: "triage"
    },
    {
        code: "follow-up-on-services",
        display: "Follow Up on Services",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Reach out to the member to gauge appointment completion and satisfaction.",
        canonical: "ActivityDefinition/follow-up-on-services",
        protocol: "triage"
    },
    {
        code: "complete-cadenced-followup",
        display: "Complete Cadenced Followup",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Check-in on member at 6 week intervals to assess ongoing progress or needs.",
        canonical: "ActivityDefinition/complete-cadenced-followup",
        protocol: "triage"
    },
    // CoCM Protocol Activities
    // Note: `enroll-in-mirah` was retired when the Benchmark/Mirah partnership
    // ended. The display label is still resolvable via `taxonomy-mapping.ts`
    // (`V2_ACTIVITY_DISPLAYS`) so existing tasks render correctly. New CoCM
    // plans no longer include this activity.
    {
        code: "build-cocm-care-plan",
        display: "Build CoCM Care Plan",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Select and schedule the CoCM activities for this member's care plan.",
        canonical: "ActivityDefinition/build-cocm-care-plan",
        protocol: "cocm"
    },
    {
        code: "follow-up-on-cocm",
        display: "Follow Up on CoCM Progress",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Check in on member to assess ongoing CoCM progress or needs.",
        canonical: "ActivityDefinition/follow-up-on-cocm",
        protocol: "cocm"
    },
    {
        code: "consult-psych",
        display: "Consult Psychiatrist",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Schedule a consultation with a psychiatric provider to assess the member's mental health needs.",
        canonical: "ActivityDefinition/consult-psych",
        protocol: "cocm"
    },
    {
        code: "consult-pcp",
        display: "Consult PCP",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Schedule a consultation with a primary care provider to assess the member's physical health needs.",
        canonical: "ActivityDefinition/consult-pcp",
        protocol: "cocm"
    },
    {
        code: "discharge-member",
        display: "Discharge Member",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Discharge the member from the CoCM program.",
        canonical: "ActivityDefinition/discharge-member",
        protocol: "cocm"
    }
];
/**
 * Helper Functions
 * getActivityDefinitionDisplay uses taxonomy mapping for legacy code support
 */ const getActivityDefinitionDisplay = (code)=>resolveActivityDisplay(code);
/**
 * Returns activity definitions for a protocol.
 * @agentic @benchmark/core
 */ const getActivityDefinitionsByProtocol = (protocol)=>{
    return ACTIVITY_DEFINITION_OPTIONS.filter((o)=>o.protocol === protocol);
};
/**
 * UI-ready select options
 */ ACTIVITY_DEFINITION_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));
ACTIVITY_DEFINITION_OPTIONS.filter((o)=>o.protocol === "intake").map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));
ACTIVITY_DEFINITION_OPTIONS.filter((o)=>o.protocol === "triage").map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));
ACTIVITY_DEFINITION_OPTIONS.filter((o)=>o.protocol === "cocm").map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));

/**
 * FHIR-Compliant Constants for Appointment Domain
 *
 * This file contains all constants used in Appointment forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 */ // FHIR R4-compliant specialty options from Practice Setting Code Value Set
// http://hl7.org/fhir/ValueSet/c80-practice-codes
// Based on SNOMED CT International edition
const APPOINTMENT_SPECIALTY_OPTIONS = [
    {
        code: "394576009",
        display: "Accident & emergency",
        system: "http://snomed.info/sct"
    },
    {
        code: "408467006",
        display: "Adult mental illness",
        system: "http://snomed.info/sct"
    },
    {
        code: "394577000",
        display: "Anesthetics",
        system: "http://snomed.info/sct"
    },
    {
        code: "394579002",
        display: "Cardiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394588006",
        display: "Child and adolescent psychiatry",
        system: "http://snomed.info/sct"
    },
    {
        code: "394803006",
        display: "Clinical hematology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408454008",
        display: "Clinical microbiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408478003",
        display: "Critical care medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394582007",
        display: "Dermatology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394583002",
        display: "Endocrinology",
        system: "http://snomed.info/sct"
    },
    {
        code: "419772000",
        display: "Family practice",
        system: "http://snomed.info/sct"
    },
    {
        code: "394584008",
        display: "Gastroenterology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408444009",
        display: "General dental practice",
        system: "http://snomed.info/sct"
    },
    {
        code: "394802001",
        display: "General medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394915009",
        display: "General pathology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394814009",
        display: "General practice",
        system: "http://snomed.info/sct"
    },
    {
        code: "394811001",
        display: "Geriatric medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394586005",
        display: "Gynecology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394597005",
        display: "Histopathology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394807007",
        display: "Infectious diseases",
        system: "http://snomed.info/sct"
    },
    {
        code: "419192003",
        display: "Internal medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394593009",
        display: "Medical oncology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394733009",
        display: "Medical specialty - Other",
        system: "http://snomed.info/sct"
    },
    {
        code: "394589003",
        display: "Nephrology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394591006",
        display: "Neurology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394649004",
        display: "Nuclear medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394585009",
        display: "Obstetrics and gynecology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408470005",
        display: "Obstetrics",
        system: "http://snomed.info/sct"
    },
    {
        code: "394821009",
        display: "Occupational medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394594003",
        display: "Ophthalmology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394605001",
        display: "Oral surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394608004",
        display: "Orthodontics",
        system: "http://snomed.info/sct"
    },
    {
        code: "394882004",
        display: "Pain management",
        system: "http://snomed.info/sct"
    },
    {
        code: "394806003",
        display: "Palliative medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "408459003",
        display: "Pediatric cardiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394607009",
        display: "Pediatric dentistry",
        system: "http://snomed.info/sct"
    },
    {
        code: "419610006",
        display: "Pediatric endocrinology",
        system: "http://snomed.info/sct"
    },
    {
        code: "418058008",
        display: "Pediatric gastroenterology",
        system: "http://snomed.info/sct"
    },
    {
        code: "419365004",
        display: "Pediatric nephrology",
        system: "http://snomed.info/sct"
    },
    {
        code: "418002000",
        display: "Pediatric oncology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394539006",
        display: "Pediatric surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "409968004",
        display: "Preventive medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394587001",
        display: "Psychiatry",
        system: "http://snomed.info/sct"
    },
    {
        code: "394913002",
        display: "Psychotherapy",
        system: "http://snomed.info/sct"
    },
    {
        code: "408440000",
        display: "Public health medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "419815003",
        display: "Radiation oncology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394914008",
        display: "Radiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408455009",
        display: "Radiology - Interventional radiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394602003",
        display: "Rehabilitation",
        system: "http://snomed.info/sct"
    },
    {
        code: "394810000",
        display: "Rheumatology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408466002",
        display: "Surgery - Cardiac surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394609007",
        display: "Surgery - General",
        system: "http://snomed.info/sct"
    },
    {
        code: "394610002",
        display: "Surgery - Neurosurgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394611003",
        display: "Surgery - Plastic surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394801008",
        display: "Surgery - Trauma and orthopedics",
        system: "http://snomed.info/sct"
    },
    {
        code: "408463005",
        display: "Surgery - Vascular",
        system: "http://snomed.info/sct"
    },
    {
        code: "394732004",
        display: "Surgical specialty - Other",
        system: "http://snomed.info/sct"
    },
    {
        code: "394590007",
        display: "Thoracic medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394612005",
        display: "Urology",
        system: "http://snomed.info/sct"
    }
];
// FHIR R4-compliant appointment status options
// http://hl7.org/fhir/R4/valueset-appointmentstatus.html
const APPOINTMENT_STATUS_OPTIONS = [
    {
        code: "pending",
        display: "Pending",
        system: "http://hl7.org/fhir/appointmentstatus",
        description: "Some or all of the participant(s) have not/did not appear for the appointment (usually the patient)."
    },
    {
        code: "booked",
        display: "Booked",
        system: "http://hl7.org/fhir/appointmentstatus",
        description: "All participant(s) have been considered and the appointment is confirmed to go ahead at the date/times specified."
    },
    {
        code: "fulfilled",
        display: "Fulfilled",
        system: "http://hl7.org/fhir/appointmentstatus",
        description: "The appointment has been completed and resulted in a clinical encounter."
    },
    {
        code: "cancelled",
        display: "Cancelled",
        system: "http://hl7.org/fhir/appointmentstatus",
        description: "The appointment has been cancelled."
    },
    {
        code: "noshow",
        display: "No Show",
        system: "http://hl7.org/fhir/appointmentstatus",
        description: "Some or all of the participant(s) have not/did not appear for the appointment (usually the patient)."
    },
    {
        code: "waitlist",
        display: "Waitlisted",
        system: "http://hl7.org/fhir/appointmentstatus",
        description: "The appointment has been placed on a waitlist, to be scheduled/confirmed in the future when a slot/service is available."
    }
];
// UI-specific mappings for Select components
APPOINTMENT_SPECIALTY_OPTIONS.map((specialty)=>({
        value: specialty.code,
        label: specialty.display
    }));
APPOINTMENT_STATUS_OPTIONS.map((status)=>({
        value: status.code,
        label: status.display
    }));

/**
 * CarePlan Category System
 * Custom Benchmark Health system for care plan categorization
 */ const CARE_PLAN_CATEGORY_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/care-plan-type";
/**
 * CarePlan Category Options
 * Defines the types of care plans available
 */ const CARE_PLAN_CATEGORY_OPTIONS = [
    {
        code: "intake",
        display: "Intake",
        system: CARE_PLAN_CATEGORY_SYSTEM,
        description: "Initial Member Intake & Onboarding"
    },
    {
        code: "triage",
        display: "Triage",
        system: CARE_PLAN_CATEGORY_SYSTEM,
        description: "Benchmark Care Triage & Navigation"
    },
    {
        code: "cocm",
        display: "CoCM",
        system: CARE_PLAN_CATEGORY_SYSTEM,
        description: "Benchmark CoCM"
    }
];
/**
 * CarePlan Activity Code System
 * Custom system for activity phases within care plans
 * Note: Activity definitions come from ActivityDefinition constants.
 * This system is used for CarePlan.activity.code codings.
 */ const CARE_PLAN_ACTIVITY_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/care-plan-activity";
/**
 * Metadata mapping for all v2 activities
 * Legacy codes resolve via taxonomy-mapping for backwards compatibility
 */ const ACTIVITY_METADATA = {
    // Intake
    "schedule-intake": {
        order: 1,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "complete-intake": {
        order: 2,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "finalize-care-plan-enrollment": {
        order: 3,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "check-in-on-member": {
        order: 4,
        category: "flexible",
        isFlexible: true,
        assignmentMode: "current-user"
    },
    // Triage — tasks start unassigned in the shared queue
    "select-services": {
        order: 1,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "unassigned-queue"
    },
    "approve-selected-services": {
        order: 2,
        category: "as-needed",
        isFlexible: true,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "offer-services": {
        order: 3,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "unassigned-queue"
    },
    "confirm-services": {
        order: 4,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "unassigned-queue"
    },
    "schedule-services": {
        order: 5,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "unassigned-queue"
    },
    "follow-up-on-services": {
        order: 6,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "unassigned-queue"
    },
    "complete-cadenced-followup": {
        order: 7,
        category: "sequential",
        isFlexible: false,
        maxOccurrences: 3,
        slaDurationHours: 1008,
        assignmentMode: "unassigned-queue"
    },
    // CoCM — all tasks assigned to patient's primary advocate (benchmark-advocate extension)
    // Note: `enroll-in-mirah` (order 1) was retired with the Benchmark/Mirah
    // partnership wind-down. `build-cocm-care-plan` is now the first CoCM
    // sequential activity. Historical task displays still resolve via taxonomy
    // mapping.
    "build-cocm-care-plan": {
        order: 1,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "follow-up-on-cocm": {
        order: 3,
        category: "flexible",
        isFlexible: true,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "consult-psych": {
        order: 4,
        category: "flexible",
        isFlexible: true,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "consult-pcp": {
        order: 5,
        category: "flexible",
        isFlexible: true,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "discharge-member": {
        order: 50,
        category: "flexible",
        isFlexible: true,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    }
};
/**
 * Get activity metadata for a given activity code.
 * Resolves legacy codes to v2 for backwards compatibility.
 */ const getActivityMetadata = (code)=>{
    const v2Code = resolveActivityCode(code);
    return ACTIVITY_METADATA[v2Code];
};
/**
 * Create CarePlan activity option from ActivityDefinition code
 * Combines ActivityDefinition data with CarePlan metadata
 */ const createCarePlanActivityOption = (code)=>{
    const activityDef = ACTIVITY_DEFINITION_OPTIONS.find((a)=>a.code === code);
    const metadata = getActivityMetadata(code);
    if (!activityDef || !metadata) {
        return undefined;
    }
    return {
        code: activityDef.code,
        display: activityDef.display,
        system: CARE_PLAN_ACTIVITY_SYSTEM,
        description: activityDef.description,
        order: metadata.order,
        isFlexible: metadata.isFlexible,
        category: metadata.category
    };
};
/**
 * Get activities for a care plan type
 * Returns CarePlan activity options with metadata
 */ const getActivitiesForCarePlanType = (type)=>{
    const protocol = type === "cocm" ? "cocm" : type;
    const activityDefs = getActivityDefinitionsByProtocol(protocol);
    return activityDefs.map((def)=>createCarePlanActivityOption(def.code)).filter((option)=>option !== undefined);
};
/**
 * Get all CarePlan activity options
 * Useful for lookups and validation
 */ const getAllCarePlanActivityOptions = ()=>{
    return ACTIVITY_DEFINITION_OPTIONS.map((def)=>createCarePlanActivityOption(def.code)).filter((option)=>option !== undefined);
};
/**
 * Backwards-compatible exports for activity options
 * These now derive from ActivityDefinition constants with metadata
 */ getActivitiesForCarePlanType("intake");
const TRIAGE_ACTIVITY_OPTIONS = getActivitiesForCarePlanType("triage");
/**
 * Set of all triage activity codes for efficient lookup.
 * @agentic @benchmark/core
 */ new Set(TRIAGE_ACTIVITY_OPTIONS.map((opt)=>opt.code));
getActivitiesForCarePlanType("cocm");
/**
 * UI-ready select options
 */ CARE_PLAN_CATEGORY_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));
getActivitiesForCarePlanType("intake").map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description,
        isFlexible: option.isFlexible
    }));
getActivitiesForCarePlanType("triage").map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description,
        isFlexible: option.isFlexible
    }));
getActivitiesForCarePlanType("cocm").map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description,
        isFlexible: option.isFlexible
    }));
/**
 * CoCM Consent Date Extension URL
 * FHIR extension for storing the date when a patient consented to receive CoCM services
 */ const COCM_CONSENT_DATE_EXTENSION_URL = "http://benchmarkhealth.com/fhir/StructureDefinition/cocm-consent-date";

/** `Observation.code` value for a CoCM monthly billing summary. @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_CODE_VALUE = "cocm-monthly-billing-summary";
/** Token for a `code` search. @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_CODE_TOKEN = `${CARE_TIME_CODE_SYSTEM}|${COCM_BILLING_SUMMARY_CODE_VALUE}`;
/** Identifier system for the `{patientId}|{YYYY-MM}` composite key. @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_IDENTIFIER_SYSTEM = `${ORGANIZATION_BASE}/identifier-system/cocm-billing-summary`;
/** Build the composite identifier value for a patient-month. @agentic @benchmark/core */ const buildCoCMBillingSummaryIdentifierValue = (patientId, billingMonth)=>`${patientId}|${billingMonth}`;
/** `system|value` token for an identifier search. @agentic @benchmark/core */ const buildCoCMBillingSummaryIdentifierToken = (patientId, billingMonth)=>`${COCM_BILLING_SUMMARY_IDENTIFIER_SYSTEM}|${buildCoCMBillingSummaryIdentifierValue(patientId, billingMonth)}`;

/**
 * FHIR-Compliant Constants for ClinicalImpression Domain
 *
 * This file contains all constants used in ClinicalImpression forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 */ // FHIR-compliant clinical impression type options
const CLINICAL_IMPRESSION_TYPE_OPTIONS = [
    {
        code: "assessmentPlan",
        display: "Assessment and Plan",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "caseManagementSummary",
        display: "Case Management Summary",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "currentOutpatientTreaters",
        display: "Current Outpatient Treaters",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "chiefComplaint",
        display: "Chief Complaint",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "familyHistory",
        display: "Family History",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "generalNote",
        display: "General Note",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "hpiCurrentSymptoms",
        display: "HPI Current Symptoms",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "medicalHistory",
        display: "Medical History",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalHealthHistory",
        display: "Mental Health History",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusAppearance",
        display: "Mental Status - Appearance",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusBehavior",
        display: "Mental Status - Behavior",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusSpeech",
        display: "Mental Status - Speech",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusMoodAffect",
        display: "Mental Status - Mood/Affect",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusThoughtProcessContent",
        display: "Mental Status - Thought Process/Content",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusCognition",
        display: "Mental Status - Cognition",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusPerception",
        display: "Mental Status - Perception",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusInsightJudgment",
        display: "Mental Status - Insight/Judgment",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "socialHistory",
        display: "Social History",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "substanceUseHistory",
        display: "Substance Use History",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "traumaHistory",
        display: "Trauma History",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    }
];
// FHIR-compliant clinical impression status options
// http://hl7.org/fhir/R4/valueset-clinicalimpression-status.html
const CLINICAL_IMPRESSION_STATUS_OPTIONS = [
    {
        code: "in-progress",
        display: "In Progress",
        system: "http://hl7.org/fhir/clinicalimpression-status"
    },
    {
        code: "completed",
        display: "Completed",
        system: "http://hl7.org/fhir/clinicalimpression-status"
    },
    {
        code: "entered-in-error",
        display: "Entered in Error",
        system: "http://hl7.org/fhir/clinicalimpression-status"
    }
];
// UI-specific mappings for Select components
CLINICAL_IMPRESSION_TYPE_OPTIONS.map((type)=>({
        value: type.code,
        label: type.display
    }));
CLINICAL_IMPRESSION_STATUS_OPTIONS.map((status)=>({
        value: status.code,
        label: status.display
    }));
// Backward compatibility: Create a lookup object for existing code
CLINICAL_IMPRESSION_TYPE_OPTIONS.reduce((acc, type)=>{
    acc[type.code] = type.display;
    return acc;
}, {});

/**
 * FHIR-Compliant Constants for Communication Domain
 *
 * This file contains all constants used in Communication forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 *
 * ── Sacred Communication model (ratified 2026-06-08, Decision R1: multi-system) ──
 *
 * Every creation path and every backfill must satisfy this single contract.
 * Channel and interaction type are deliberately separate axes; the category
 * systems are purpose-specific and are NOT collapsed into one enum.
 *
 *   medium    → CHANNEL ("how"). Always HL7 `v3-ParticipationMode`
 *               (`COMMUNICATION_MEDIUM_SYSTEM`): phone | text | email |
 *               secure-message | fax | portal. Set whenever a real channel
 *               exists; omitted only for genuinely channel-less records.
 *   category  → TYPE/PURPOSE ("what"). One coding per cohesive question, each on
 *               its own canonical Benchmark host:
 *                 • `COMMUNICATION_TYPE_SYSTEM`        patient-message |
 *                                                      manual-outreach | nps-4-week |
 *                                                      nps-onboarding | nps-ongoing-care
 *                 • `CALL_STATE_SYSTEM` (calls only)   completed | missed | voicemail
 *                 • `PROGRESS_LOG_CATEGORY_SYSTEM`     outreach-attempt |
 *                                                      provider-outreach |
 *                                                      administrative-note
 *                 • `AVAILABILITY_UPDATE_CATEGORY_SYSTEM`  availability-update
 *   reasonCode → OUTCOME/FINDING (progress-log-outcome, availability-finding).
 *   statusReason → NPS only (`NPS_STATUS_REASON_SYSTEM`).
 *   sender/recipient/subject → `ResourceType/id` WITH `display`, semantically
 *               correct per archetype (no patient-as-recipient on provider
 *               outreach).
 *   status    → per-archetype policy. Hardcoded `completed` on the staff/log
 *               paths (interaction occurred); NPS is the only path that varies
 *               it; SMS/call lifecycle status is deferred (Decision D7).
 *
 * Deprecated, read-only, target = 0 rows after backfill (legacy detection +
 * read-side fallback ONLY): `LEGACY_COMMUNICATION_CHANNEL_SYSTEM`,
 * `LEGACY_CALL_STATE_SYSTEM`. Channel now lives exclusively in `medium`.
 *
 * Conscious exception: call extensions (`call-duration-ms`, `call-direction`)
 * still use the `benchmark.health/fhir/StructureDefinition/...` host. They are
 * extension URLs, not category systems, so they are out of the canonical-host
 * migration (Decision D3) scope.
 */ /** HL7 v3-ParticipationMode system URL for communication medium codings. */ const COMMUNICATION_MEDIUM_SYSTEM = "http://terminology.hl7.org/CodeSystem/v3-ParticipationMode";
/** All communication medium options (member + provider facing). */ const COMMUNICATION_MEDIUM_OPTIONS = [
    {
        code: "phone",
        display: "Phone",
        system: COMMUNICATION_MEDIUM_SYSTEM
    },
    {
        code: "text",
        display: "Text",
        system: COMMUNICATION_MEDIUM_SYSTEM
    },
    {
        code: "email",
        display: "Email",
        system: COMMUNICATION_MEDIUM_SYSTEM
    },
    {
        code: "secure-message",
        display: "Secure Message",
        system: COMMUNICATION_MEDIUM_SYSTEM
    },
    {
        code: "fax",
        display: "Fax",
        system: COMMUNICATION_MEDIUM_SYSTEM
    },
    {
        code: "portal",
        display: "Portal",
        system: COMMUNICATION_MEDIUM_SYSTEM
    }
];
/** Member-facing medium select options (phone, text, email, secure-message). */ COMMUNICATION_MEDIUM_OPTIONS.filter((o)=>o.code !== "fax" && o.code !== "portal").map((option)=>({
        value: option.code,
        label: option.display
    }));
/** Provider-facing medium select options (phone, fax, email, portal). */ COMMUNICATION_MEDIUM_OPTIONS.filter((o)=>o.code === "phone" || o.code === "fax" || o.code === "email" || o.code === "portal").map((option)=>({
        value: option.code,
        label: option.display
    }));

/**
 * FHIR-Compliant Constants for Coverage Domain
 *
 * This file contains all constants used in Coverage forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 */ // FHIR R4-compliant coverage type options
// http://hl7.org/fhir/R4/valueset-coverage-type.html
const COVERAGE_TYPE_OPTIONS = [
    {
        code: "EHCPOL",
        display: "Extended Healthcare",
        system: "http://terminology.hl7.org/CodeSystem/v3-ActCode"
    },
    {
        code: "PUBLICPOL",
        display: "Public Healthcare",
        system: "http://terminology.hl7.org/CodeSystem/v3-ActCode"
    },
    {
        code: "DENTPRG",
        display: "Dental Program",
        system: "http://terminology.hl7.org/CodeSystem/v3-ActCode"
    },
    {
        code: "MENTPRG",
        display: "Mental Health Program",
        system: "http://terminology.hl7.org/CodeSystem/v3-ActCode"
    },
    {
        code: "SUBPRG",
        display: "Substance Use Program",
        system: "http://terminology.hl7.org/CodeSystem/v3-ActCode"
    },
    {
        code: "VISPOL",
        display: "Vision Care Policy",
        system: "http://terminology.hl7.org/CodeSystem/v3-ActCode"
    }
];
// FHIR R4-compliant coverage status options
// http://hl7.org/fhir/R4/valueset-fm-status.html
const COVERAGE_STATUS_OPTIONS = [
    {
        code: "active",
        display: "Active",
        system: "http://hl7.org/fhir/fm-status",
        description: "The coverage is currently in-force and providing benefits."
    },
    {
        code: "cancelled",
        display: "Cancelled",
        system: "http://hl7.org/fhir/fm-status",
        description: "The coverage has been cancelled and is no longer in effect."
    },
    {
        code: "draft",
        display: "Draft",
        system: "http://hl7.org/fhir/fm-status",
        description: "The coverage is being set up but not yet active."
    },
    {
        code: "entered-in-error",
        display: "Entered in Error",
        system: "http://hl7.org/fhir/fm-status",
        description: "The coverage was entered in error and should be ignored."
    }
];
// FHIR R4-compliant relationship options
// http://hl7.org/fhir/R4/valueset-subscriber-relationship.html
const COVERAGE_RELATIONSHIP_OPTIONS = [
    {
        code: "self",
        display: "Self",
        system: "http://terminology.hl7.org/CodeSystem/subscriber-relationship",
        description: "The beneficiary is the subscriber."
    },
    {
        code: "spouse",
        display: "Spouse",
        system: "http://terminology.hl7.org/CodeSystem/subscriber-relationship",
        description: "The beneficiary is the spouse or equivalent of the subscriber."
    },
    {
        code: "child",
        display: "Child",
        system: "http://terminology.hl7.org/CodeSystem/subscriber-relationship",
        description: "The beneficiary is a child of the subscriber."
    },
    {
        code: "parent",
        display: "Parent",
        system: "http://terminology.hl7.org/CodeSystem/subscriber-relationship",
        description: "The beneficiary is a parent of the subscriber."
    },
    {
        code: "other",
        display: "Other",
        system: "http://terminology.hl7.org/CodeSystem/subscriber-relationship",
        description: "The beneficiary has some other relationship to the subscriber."
    }
];
// UI-specific mappings for Select components
COVERAGE_TYPE_OPTIONS.map((type)=>({
        value: type.code,
        label: type.display
    }));
COVERAGE_STATUS_OPTIONS.map((status)=>({
        value: status.code,
        label: status.display
    }));
COVERAGE_RELATIONSHIP_OPTIONS.map((relationship)=>({
        value: relationship.code,
        label: relationship.display
    }));

/**
 * FHIR-Compliant Constants for Patient Domain
 *
 * This file contains all constants used in Patient forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 */ /**
 * US States with FHIR-compliant codes
 * Based on USPS state codes as per FHIR US Core
 */ const US_STATES = [
    {
        code: "AK",
        display: "Alaska"
    },
    {
        code: "AL",
        display: "Alabama"
    },
    {
        code: "AR",
        display: "Arkansas"
    },
    {
        code: "AZ",
        display: "Arizona"
    },
    {
        code: "CA",
        display: "California"
    },
    {
        code: "CO",
        display: "Colorado"
    },
    {
        code: "CT",
        display: "Connecticut"
    },
    {
        code: "DC",
        display: "District of Columbia"
    },
    {
        code: "DE",
        display: "Delaware"
    },
    {
        code: "FL",
        display: "Florida"
    },
    {
        code: "GA",
        display: "Georgia"
    },
    {
        code: "HI",
        display: "Hawaii"
    },
    {
        code: "IA",
        display: "Iowa"
    },
    {
        code: "ID",
        display: "Idaho"
    },
    {
        code: "IL",
        display: "Illinois"
    },
    {
        code: "IN",
        display: "Indiana"
    },
    {
        code: "KS",
        display: "Kansas"
    },
    {
        code: "KY",
        display: "Kentucky"
    },
    {
        code: "LA",
        display: "Louisiana"
    },
    {
        code: "MA",
        display: "Massachusetts"
    },
    {
        code: "MD",
        display: "Maryland"
    },
    {
        code: "ME",
        display: "Maine"
    },
    {
        code: "MI",
        display: "Michigan"
    },
    {
        code: "MN",
        display: "Minnesota"
    },
    {
        code: "MO",
        display: "Missouri"
    },
    {
        code: "MS",
        display: "Mississippi"
    },
    {
        code: "MT",
        display: "Montana"
    },
    {
        code: "NC",
        display: "North Carolina"
    },
    {
        code: "ND",
        display: "North Dakota"
    },
    {
        code: "NE",
        display: "Nebraska"
    },
    {
        code: "NH",
        display: "New Hampshire"
    },
    {
        code: "NJ",
        display: "New Jersey"
    },
    {
        code: "NM",
        display: "New Mexico"
    },
    {
        code: "NV",
        display: "Nevada"
    },
    {
        code: "NY",
        display: "New York"
    },
    {
        code: "OH",
        display: "Ohio"
    },
    {
        code: "OK",
        display: "Oklahoma"
    },
    {
        code: "OR",
        display: "Oregon"
    },
    {
        code: "PA",
        display: "Pennsylvania"
    },
    {
        code: "RI",
        display: "Rhode Island"
    },
    {
        code: "SC",
        display: "South Carolina"
    },
    {
        code: "SD",
        display: "South Dakota"
    },
    {
        code: "TN",
        display: "Tennessee"
    },
    {
        code: "TX",
        display: "Texas"
    },
    {
        code: "UT",
        display: "Utah"
    },
    {
        code: "VA",
        display: "Virginia"
    },
    {
        code: "VT",
        display: "Vermont"
    },
    {
        code: "WA",
        display: "Washington"
    },
    {
        code: "WI",
        display: "Wisconsin"
    },
    {
        code: "WV",
        display: "West Virginia"
    },
    {
        code: "WY",
        display: "Wyoming"
    }
];
/**
 * Legacy Patient Engagement Status (V1)
 * @deprecated Use PATIENT_ENGAGEMENT_STATUS_OPTIONS instead
 * Maintained for backward compatibility only
 */ const LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM = "http://terminology.hl7.org/CodeSystem/patient-engagement";
const LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS = [
    {
        code: "initial-referral",
        display: "Initial Referral",
        system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v1",
        deprecated: true
    },
    {
        code: "engaged",
        display: "Engaged",
        system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v1",
        deprecated: false
    },
    {
        code: "activated",
        display: "Activated",
        system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v1",
        deprecated: false
    },
    {
        code: "unsure",
        display: "Unsure",
        system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v1",
        deprecated: true
    },
    {
        code: "declined",
        display: "Declined Services",
        system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v1",
        deprecated: false
    },
    {
        code: "unable-to-reach",
        display: "Unable to Reach",
        system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v1",
        deprecated: true
    }
];
/**
 * Patient Engagement Status (V2 - Current)
 * Enhanced engagement status codes following FHIR best practices
 */ const PATIENT_ENGAGEMENT_STATUS_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/patient-engagement-v2";
const PATIENT_ENGAGEMENT_STATUS_OPTIONS = [
    {
        code: "referred",
        display: "Referred",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Initial state when ServiceRequest created"
    },
    {
        code: "engaged",
        display: "Engaged",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Consented to services, completed intake"
    },
    {
        code: "activated",
        display: "Activated",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Patient activated in system (backwards compatibility)"
    },
    {
        code: "graduated",
        display: "Graduated",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Successfully completed program"
    },
    {
        code: "unreachable",
        display: "Unreachable",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Unable to contact after multiple attempts"
    },
    {
        code: "declined",
        display: "Declined",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Explicitly declined services"
    },
    {
        code: "discharged",
        display: "Discharged",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Administratively discharged from program"
    }
];
/**
 * All Patient Engagement Status Options (V1 + V2)
 * For display/editing existing patient statuses
 */ [
    ...LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS,
    ...PATIENT_ENGAGEMENT_STATUS_OPTIONS
];
/**
 * UI-ready select options for engagement status
 */ PATIENT_ENGAGEMENT_STATUS_OPTIONS.map((status)=>({
        value: status.code,
        label: status.display,
        description: status.description
    }));
(()=>{
    // Deduplicate by preferring V2 entries over V1 when codes match
    const seenCodes = new Set();
    const result = [];
    // Add V2 entries first (preferred)
    for (const status of PATIENT_ENGAGEMENT_STATUS_OPTIONS){
        if (!seenCodes.has(status.code)) {
            seenCodes.add(status.code);
            result.push({
                value: status.code,
                label: status.display,
                deprecated: false,
                version: status.version
            });
        }
    }
    // Add V1 entries that don't exist in V2
    for (const status of LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS){
        if (!seenCodes.has(status.code)) {
            seenCodes.add(status.code);
            result.push({
                value: status.code,
                label: status.display,
                deprecated: "deprecated" in status ? status.deprecated : false,
                version: status.version
            });
        }
    }
    return result;
})();

/**
 * Identifier system used to look up the canonical state-coverage
 * `Location` resource. The identifier value is the two-letter state
 * code (e.g. "MA", "CA").
 * @agentic @benchmark/core
 */ const COVERAGE_AREA_STATE_IDENTIFIER_SYSTEM = "https://benchmarkhealth.com/fhir/sid/coverage-area-state";
/**
 * State codes for which Connections currently authors and queries
 * coverage areas. Adding a new state is a one-line change here.
 * @agentic @benchmark/core
 */ const SUPPORTED_COVERAGE_AREA_STATE_CODES = [
    "MA",
    "CA"
];
new Set(SUPPORTED_COVERAGE_AREA_STATE_CODES);
/**
 * Returns the human display label for a supported state code (e.g.
 * "Massachusetts" for "MA"). Falls back to the code itself when the
 * state is somehow not in the US_STATES catalogue.
 * @agentic @benchmark/core
 */ const getCoverageAreaStateDisplay = (code)=>{
    const entry = US_STATES.find((state)=>state.code === code);
    return entry?.display ?? code;
};
/**
 * Returns the canonical Location.name for a supported state coverage
 * area, e.g. "Massachusetts Coverage Area".
 * @agentic @benchmark/core
 */ const getCoverageAreaLocationName = (code)=>`${getCoverageAreaStateDisplay(code)} Coverage Area`;

/**
 * FHIR-Compliant Constants for Encounter Domain
 *
 * This file contains all constants used in Encounter forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 *
 * ARCHITECTURE NOTE:
 * These constants replace the JSON ValueSets in packages/medplum/src/data/valuesets/.
 * While the JSON approach offers better FHIR compliance and versioning support,
 * the TypeScript constants provide immediate developer experience benefits:
 * - Type safety and IntelliSense
 * - Direct UI mappings for Select components
 * - Helper functions for display lookups
 *
 * VERSIONING STRATEGY:
 * - LEGACY_ENCOUNTER_TYPE_OPTIONS: Deprecated v1 types (for backwards compatibility)
 * - ENCOUNTER_TYPE_OPTIONS: Current v2 types (for new forms)
 * - ALL_ENCOUNTER_TYPE_OPTIONS: Combined for display/lookup functions
 *
 * Future consideration: Migrate to versioned JSON ValueSet system for better
 * FHIR server integration and centralized management.
 */ // =============================================================================
// LEGACY ENCOUNTER TYPES (v1) - DEPRECATED
// =============================================================================
// These are maintained for backwards compatibility with existing encounters.
// DO NOT use these in new forms - use ENCOUNTER_TYPE_OPTIONS instead.
const CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE = "clinical-intake-interaction";
const COCM_INTAKE_ENCOUNTER_TYPE_CODE = "cocm-intake-interaction";
/** Wellness-track follow-up code. Mirrors the clinical (90791) intake track. */ const CLINICAL_FOLLOW_UP_ENCOUNTER_TYPE_CODE = "clinical-follow-up-interaction";
/** CoCM-track follow-up code. Minutes roll into the month-end CoCM packet. */ const COCM_FOLLOW_UP_ENCOUNTER_TYPE_CODE = "cocm-follow-up-interaction";
/**
 * No-bill intake code. Written when the Schedule Intake billing flag is
 * "No bill"; parity with the billed intake codes so unbilled intakes are a
 * first-class (non-billable) intake type rather than an untagged encounter.
 */ const UNBILLED_INTAKE_ENCOUNTER_TYPE_CODE = "unbilled-intake-interaction";
/**
 * No-bill follow-up code. Mirrors {@link UNBILLED_INTAKE_ENCOUNTER_TYPE_CODE}
 * for follow-up encounters whose originating intake was unbilled.
 */ const UNBILLED_FOLLOW_UP_ENCOUNTER_TYPE_CODE = "unbilled-follow-up-interaction";
/**
 * @deprecated Use ENCOUNTER_TYPE_OPTIONS for new forms.
 * This constant is maintained only for backwards compatibility with existing encounters.
 */ const LEGACY_ENCOUNTER_TYPE_OPTIONS = [
    {
        code: "outreach",
        display: "Outreach",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v1",
        deprecated: true,
        deprecationReason: "Outreach interactions are now handled by Communication resources"
    },
    {
        code: "initial",
        display: "Initial Assessment",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v1",
        deprecated: true,
        deprecationReason: "Replaced by more specific interaction types in v2"
    },
    {
        code: "check-in-evaluation",
        display: "Check-In/Evaluation",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v1",
        deprecated: true,
        deprecationReason: "Replaced by more specific interaction types in v2"
    },
    {
        code: "follow-up",
        display: "Follow-Up Assessment",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v1",
        deprecated: true,
        deprecationReason: "Replaced by more specific interaction types in v2"
    }
];
// =============================================================================
// CURRENT ENCOUNTER TYPES (v2) - ACTIVE
// =============================================================================
// These are the current encounter types for use in new forms and encounters.
const ENCOUNTER_TYPE_OPTIONS = [
    {
        code: "general-interaction",
        display: "General Interaction",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "General visit for information, coordination, or support"
    },
    {
        code: CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE,
        display: "Clinical Intake Interaction",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Initial clinical intake visit (90791) for assessment and planning"
    },
    {
        code: COCM_INTAKE_ENCOUNTER_TYPE_CODE,
        display: "CoCM Intake Interaction",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Initial Collaborative Care visit (99492) with expanded clinical history and goal setting"
    },
    {
        code: CLINICAL_FOLLOW_UP_ENCOUNTER_TYPE_CODE,
        display: "Clinical Follow-Up Interaction",
        uiLabel: "Followup",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Follow-up visit with an enrolled member. The billing track is confirmed on the Details step."
    },
    {
        code: "case-management-interaction",
        display: "Case Management Interaction",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Case management visit for coordination and support"
    },
    {
        code: COCM_FOLLOW_UP_ENCOUNTER_TYPE_CODE,
        display: "CoCM Follow-Up Interaction",
        uiLabel: "CoCM Followup",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Follow-up Collaborative Care visit with ongoing care coordination, goal progress tracking, and monitoring"
    },
    {
        code: "discharge-interaction",
        display: "Discharge Interaction",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Final member interaction documenting discharge reason, follow-up plan, and care transition"
    },
    {
        code: UNBILLED_INTAKE_ENCOUNTER_TYPE_CODE,
        display: "Unbilled Intake Interaction",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Initial intake visit documented but not billed (schedule-intake billing flag = No bill)"
    },
    {
        code: UNBILLED_FOLLOW_UP_ENCOUNTER_TYPE_CODE,
        display: "Unbilled Follow-Up Interaction",
        uiLabel: "Unbilled Followup",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Follow-up visit for a member whose intake was unbilled; documented but not billed"
    }
];
// =============================================================================
// COMBINED OPTIONS FOR BACKWARDS COMPATIBILITY
// =============================================================================
// This includes both legacy and current options for display/lookup functions
const ALL_ENCOUNTER_TYPE_OPTIONS = [
    ...LEGACY_ENCOUNTER_TYPE_OPTIONS,
    ...ENCOUNTER_TYPE_OPTIONS
];
const ENCOUNTER_METHOD_OPTIONS = [
    {
        code: "phone",
        display: "Phone",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-method",
        description: "Voice conversation with patient"
    },
    {
        code: "text",
        display: "Text",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-method",
        description: "SMS-based communication"
    },
    {
        code: "email",
        display: "Email",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-method",
        description: "Email-based communication"
    },
    {
        code: "emr-secure-message",
        display: "Secure Message",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-method",
        description: "Secure messaging through an EMR"
    },
    {
        code: "video",
        display: "Video Chat",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-method",
        description: "Video-based telehealth session"
    },
    {
        code: "in-person",
        display: "In-Person",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-method",
        description: "Face-to-face meeting"
    }
];
// Encounter direction options
const ENCOUNTER_DIRECTION_OPTIONS = [
    {
        code: "outbound",
        display: "Outbound",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-direction",
        description: "We initiated this interaction"
    },
    {
        code: "inbound",
        display: "Inbound",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-direction",
        description: "Member initiated this interaction"
    }
];
// Encounter success status options
/**
 * @deprecated This constant is deprecated by Communication resource support.
 * This constant is maintained only for backwards compatibility with existing encounters.
 */ const ENCOUNTER_SUCCESS_OPTIONS = [
    {
        code: "pending",
        display: "Pending",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-outcome"
    },
    {
        code: "successful",
        display: "Successful",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-outcome"
    },
    {
        code: "unsuccessful",
        display: "Unsuccessful",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-outcome"
    }
];
// Encounter billable status options
const ENCOUNTER_BILLABLE_OPTIONS = [
    {
        code: "billable",
        display: "Billable",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-billing",
        description: "This interaction meets the criteria for billing"
    },
    {
        code: "non-billable",
        display: "Non-Billable",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-billing",
        description: "This interaction does not meet the criteria for billing"
    }
];
/**
 * @deprecated Import PATIENT_ENGAGEMENT_STATUS_OPTIONS (V2) or LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS (V1) directly from patient.ts instead
 * This re-export is removed to avoid export conflicts in the domain constants index.
 *
 * For V1 statuses: import { LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS } from "./patient"
 * For V2 statuses: import { PATIENT_ENGAGEMENT_STATUS_OPTIONS } from "./patient"
 */ // =============================================================================
// UI-SPECIFIC MAPPINGS FOR SELECT COMPONENTS
// =============================================================================
/**
 * The label a clinician reads for an encounter type. `display` is the FHIR
 * `meta.tag.display` written on save and the join key for dbt's
 * `encounter_category`, so it is frozen; `uiLabel` lets a surface read
 * differently without touching stored data or analytics (D18/D21).
 */ const getEncounterTypeUiLabel = (type)=>type.uiLabel ?? type.display;
// Current (non-deprecated) types offered in new-form pickers. Excludes the
// unbilled tracks: those codes are derived from the Schedule Intake billing
// flag (intake) or the member's care plan / originating intake (follow-up) and
// are never chosen directly from this picker, so they must not appear as
// selectable options. Use ALL_ENCOUNTER_TYPE_SELECT_OPTIONS for
// display/editing of existing encounters (which must still resolve the
// unbilled labels).
ENCOUNTER_TYPE_OPTIONS.filter((type)=>type.code !== UNBILLED_INTAKE_ENCOUNTER_TYPE_CODE && type.code !== UNBILLED_FOLLOW_UP_ENCOUNTER_TYPE_CODE).map((type)=>({
        value: type.code,
        label: getEncounterTypeUiLabel(type),
        description: type.description
    }));
// All types (including legacy) for display/editing existing encounters
ALL_ENCOUNTER_TYPE_OPTIONS.map((type)=>({
        value: type.code,
        label: getEncounterTypeUiLabel(type),
        deprecated: "deprecated" in type ? type.deprecated : false,
        version: "version" in type ? type.version : undefined
    }));
// Standard select options (backward compatible)
ENCOUNTER_METHOD_OPTIONS.map((method)=>({
        value: method.code,
        label: method.display
    }));
// Enhanced options with descriptions for CardSelect component
ENCOUNTER_METHOD_OPTIONS.map((method)=>({
        value: method.code,
        label: method.display,
        description: method.description
    }));
ENCOUNTER_DIRECTION_OPTIONS.map((direction)=>({
        value: direction.code,
        label: direction.display,
        description: direction.description
    }));
ENCOUNTER_BILLABLE_OPTIONS.map((billable)=>({
        value: billable.code,
        label: billable.display,
        description: billable.description
    }));
ENCOUNTER_DIRECTION_OPTIONS.map((direction)=>({
        value: direction.code,
        label: direction.display
    }));
ENCOUNTER_SUCCESS_OPTIONS.map((success)=>({
        value: success.code,
        label: success.display
    }));
ENCOUNTER_BILLABLE_OPTIONS.map((billable)=>({
        value: billable.code,
        label: billable.display
    }));

/**
 * All legacy (v1) encounter codes. Stable ordering matches
 * `LEGACY_ENCOUNTER_TYPE_OPTIONS`.
 * @agentic @benchmark/core
 */ LEGACY_ENCOUNTER_TYPE_OPTIONS.map((t)=>t.code);

/**
 * Flag Domain Constants (FHIR R4)
 *
 * Defines controlled value sets for Flag.category and Flag.code.
 * - category: HL7-defined categories for flags
 * - code: Curated SNOMED CT clinical findings relevant to safety/behavioral risk flags
 */ // Flag.category uses HL7 flag-category codes
// Flag.code is a SNOMED CT clinical finding; constrain to relevant risk/safety findings
const FLAG_CODE_SYSTEM = "http://snomed.info/sct";
// Organization-specific codes for operational/policy flags
const FLAG_LOCAL_CODE_SYSTEM = "https://benchmark.health/fhir/CodeSystem/flag-code";
const FLAG_CODE_OPTIONS = [
    // Suicide risk and self-harm
    {
        code: "161901003",
        display: "Self-Harm",
        system: FLAG_CODE_SYSTEM,
        category: "safety",
        description: "History of self-harming behavior"
    },
    {
        code: "225444004",
        display: "Suicidality",
        system: FLAG_CODE_SYSTEM,
        category: "safety",
        description: "History of suicidal ideation or behavior"
    },
    {
        code: "275939002",
        display: "Suicide Attempt(s)",
        system: FLAG_CODE_SYSTEM,
        category: "safety",
        description: "History of suicide attempts"
    },
    // Violence/aggression risk
    {
        code: "248062006",
        display: "Aggression",
        system: FLAG_CODE_SYSTEM,
        category: "safety",
        description: "History of aggression"
    },
    {
        code: "110483000",
        display: "Violence",
        system: FLAG_CODE_SYSTEM,
        category: "safety",
        description: "History of violence"
    },
    // Operational/policy flags (local codes)
    {
        code: "clinician-contact-only",
        display: "Clinician Contact Only",
        system: FLAG_LOCAL_CODE_SYSTEM,
        category: "contact",
        description: "Member may only be contacted by a clinician"
    },
    {
        code: "cocm-enrolled",
        display: "Enrolled in COCM",
        system: FLAG_LOCAL_CODE_SYSTEM,
        category: "admin",
        description: "Member is enrolled in COCM"
    },
    {
        code: "patient-under-18",
        display: "Minor (Under 18)",
        system: FLAG_LOCAL_CODE_SYSTEM,
        category: "admin",
        description: "Member is a minor"
    },
    {
        code: "patient-paused",
        display: "Paused",
        system: FLAG_LOCAL_CODE_SYSTEM,
        category: "admin",
        description: "Member is temporarily paused"
    }
];
// UI-ready options for CardSelect with descriptions
FLAG_CODE_OPTIONS.map((o)=>({
        value: o.code,
        label: o.display,
        description: o.description
    }));

/**
 * Characteristic Visit Mode options (service-characteristic-visit-mode CodeSystem)
 * Comprehensive service delivery channels including modern text/app-based modalities
 */ const SERVICE_CHARACTERISTIC_VISIT_MODE_OPTIONS = [
    // Synchronous in-person
    {
        value: "in-person",
        label: "In-Person"
    },
    // Combinations
    {
        value: "hybrid",
        label: "Hybrid"
    },
    // Synchronous remote
    {
        value: "telehealth-video",
        label: "Telehealth (Video)"
    },
    {
        value: "telehealth-phone",
        label: "Telehealth (Phone)"
    },
    // Text-based
    {
        value: "text-sms",
        label: "Text/SMS"
    },
    {
        value: "chat",
        label: "Web Chat"
    },
    // Asynchronous
    {
        value: "async-messaging",
        label: "Asynchronous Messaging"
    },
    // Digital/self-guided
    {
        value: "digital-self-guided",
        label: "Digital Self-Guided"
    }
];

/**
 * FHIR-Compliant Constants for Insurance Plan Domain
 *
 * This file contains all constants used for InsurancePlan resources,
 * including plan type classification and network type classification.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 */ // =============================================================================
// INSURANCE PLAN TYPE CONSTANTS
// =============================================================================
/**
 * Insurance Plan Type CodeSystem URL
 * Used in InsurancePlan.type for plan classification
 */ const INSURANCE_PLAN_TYPE_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/insurance-plan-type";
/**
 * Insurance Plan Type Options (alphabetical by code)
 * Defines the classification system for insurance plan types:
 * - commercial: Standard commercial plans
 * - commercial-epo: Commercial EPO plans
 * - commercial-hmo: Commercial HMO plans
 * - commercial-ppo: Commercial PPO plans
 * - d-snp: Dual Eligible Special Needs Plans
 * - dual-eligible: Integrated dual-eligible plans (Medicare/Medicaid)
 * - eap: Employee Assistance Program plans
 * - fep: Federal Employees Program plans
 * - fep-ppo: Federal Employees Program PPO plans
 * - marketplace: ACA Marketplace plans
 * - masshealth-aco: MassHealth ACO plans
 * - masshealth-mco: MassHealth MCO plans
 * - medicaid: Medicaid plans
 * - medicare-advantage: Medicare Advantage/Replacement plans
 * - medicare-advantage-hmo: Medicare Advantage HMO plans
 * - medicare-advantage-ppo: Medicare Advantage PPO plans
 * - medicare-supplement: Medicare Supplement (Medigap) plans
 * - tricare: Military TRICARE plans
 * - tricare-hmo: TRICARE HMO plans
 * - tricare-ppo: TRICARE PPO plans
 * - workers-comp: Workers' Compensation plans
 */ const INSURANCE_PLAN_TYPE_OPTIONS = [
    {
        code: "commercial",
        display: "Commercial",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Standard commercial insurance plans"
    },
    {
        code: "commercial-epo",
        display: "Commercial EPO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Commercial Exclusive Provider Organization plans"
    },
    {
        code: "commercial-hmo",
        display: "Commercial HMO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Commercial Health Maintenance Organization plans"
    },
    {
        code: "commercial-ppo",
        display: "Commercial PPO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Commercial Preferred Provider Organization plans"
    },
    {
        code: "d-snp",
        display: "D-SNP",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Dual Eligible Special Needs Plan"
    },
    {
        code: "dual-eligible",
        display: "Dual Eligible (Medicare/Medicaid)",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Integrated dual-eligible plans for Medicare and Medicaid beneficiaries"
    },
    {
        code: "eap",
        display: "Employee Assistance Program",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Employee Assistance Program plans"
    },
    {
        code: "fep",
        display: "FEP",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Federal Employees Program plans"
    },
    {
        code: "fep-ppo",
        display: "FEP PPO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Federal Employees Program PPO plans"
    },
    {
        code: "marketplace",
        display: "ACA Marketplace",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "ACA Marketplace plans"
    },
    {
        code: "masshealth-aco",
        display: "MassHealth ACO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "MassHealth Accountable Care Organization plans"
    },
    {
        code: "masshealth-mco",
        display: "MassHealth MCO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "MassHealth Managed Care Organization plans"
    },
    {
        code: "medicaid",
        display: "Medicaid",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Medicaid plans"
    },
    {
        code: "medicare-advantage",
        display: "Medicare Advantage",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Medicare Advantage/Replacement plans"
    },
    {
        code: "medicare-advantage-hmo",
        display: "Medicare Advantage HMO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Medicare Advantage Health Maintenance Organization plans"
    },
    {
        code: "medicare-advantage-ppo",
        display: "Medicare Advantage PPO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Medicare Advantage Preferred Provider Organization plans"
    },
    {
        code: "medicare-supplement",
        display: "Medicare Supplement",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Medicare Supplement (Medigap) plans"
    },
    {
        code: "tricare",
        display: "TRICARE",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Military TRICARE plans"
    },
    {
        code: "tricare-hmo",
        display: "TRICARE HMO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Military TRICARE HMO plans"
    },
    {
        code: "tricare-ppo",
        display: "TRICARE PPO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Military TRICARE PPO plans"
    },
    {
        code: "workers-comp",
        display: "Workers' Compensation",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Workers' Compensation plans"
    }
];
/**
 * UI-ready select options for insurance plan type
 */ INSURANCE_PLAN_TYPE_OPTIONS.map((type)=>({
        value: type.code,
        label: type.display
    }));
// =============================================================================
// NETWORK TYPE CONSTANTS
// =============================================================================
/**
 * Network Type CodeSystem URL
 * Used in InsurancePlan.network for network classification
 */ const NETWORK_TYPE_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/insurance-network-type";
/**
 * Network Type Options
 * Defines the classification system for insurance network types:
 * - hmo: Health Maintenance Organization
 * - ppo: Preferred Provider Organization
 * - epo: Exclusive Provider Organization
 * - pos: Point of Service
 * - hdhp: High Deductible Health Plan
 */ const NETWORK_TYPE_OPTIONS = [
    {
        code: "hmo",
        display: "HMO",
        system: NETWORK_TYPE_SYSTEM,
        description: "Health Maintenance Organization"
    },
    {
        code: "ppo",
        display: "PPO",
        system: NETWORK_TYPE_SYSTEM,
        description: "Preferred Provider Organization"
    },
    {
        code: "epo",
        display: "EPO",
        system: NETWORK_TYPE_SYSTEM,
        description: "Exclusive Provider Organization"
    },
    {
        code: "pos",
        display: "POS",
        system: NETWORK_TYPE_SYSTEM,
        description: "Point of Service"
    },
    {
        code: "hdhp",
        display: "HDHP",
        system: NETWORK_TYPE_SYSTEM,
        description: "High Deductible Health Plan"
    }
];
/**
 * UI-ready select options for network type
 */ NETWORK_TYPE_OPTIONS.map((type)=>({
        value: type.code,
        label: type.display
    }));

/**
 * CodeSystem URL for the selected billing-flag value (eligibility + reality prefill).
 * @agentic @benchmark/core
 */ const INTAKE_BILLING_FLAG_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/intake-billing-flag";
/**
 * Billing-flag options shown as cards on the Schedule Intake step.
 * @agentic @benchmark/core
 */ const INTAKE_BILLING_FLAG_OPTIONS = [
    {
        code: "cocm",
        display: "CoCM-eligible",
        system: INTAKE_BILLING_FLAG_SYSTEM,
        description: "Patient is eligible for CoCM (99492)."
    },
    {
        code: "wellness",
        display: "Wellness-eligible",
        system: INTAKE_BILLING_FLAG_SYSTEM,
        description: "Patient is eligible for Wellness exam (90791)."
    },
    {
        code: "no-bill",
        display: "No Bill",
        system: INTAKE_BILLING_FLAG_SYSTEM,
        description: "Patient is not eligible for billing."
    }
];
/**
 * UI-ready select/card options for the intake billing flag.
 * @agentic @benchmark/core
 */ INTAKE_BILLING_FLAG_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));

/**
 * Clinical Notification Task constants and predicates.
 *
 * Clinical notifications are a distinct domain backed by FHIR `Task`, kept
 * strictly separate from care-plan and operational Tasks. These constants
 * define the discriminating `code.system` and the per-recipient read/unread
 * `businessStatus`, plus predicates used to slice notification Tasks from
 * operational Tasks in both directions.
 *
 * @agentic @benchmark/core
 */ /**
 * Canonical system URL for clinical notification Task types. A Task carrying a
 * `code.coding` in this system is a clinical notification, never an operational
 * or care-plan Task.
 * @agentic @benchmark/core
 */ const NOTIFICATION_TASK_TYPE_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/notification-task-type";
/**
 * Canonical system URL for the per-recipient read/unread state carried on
 * `Task.businessStatus`. Kept distinct from `Task.status` so "read" is a
 * business fact, not a workflow-lifecycle transition (read !== work done).
 * @agentic @benchmark/core
 */ const NOTIFICATION_READ_STATUS_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/notification-read-status";
/**
 * Read/unread codes stored on `Task.businessStatus.coding`.
 * @agentic @benchmark/core
 */ const NOTIFICATION_READ_STATUS = {
    UNREAD: {
        code: "unread"}};
/**
 * FHIR token that matches any notification Task by system (system-only token,
 * trailing pipe). Use with the `code` search param for the notification slice,
 * or with `code:not` to exclude notifications from operational searches.
 * @agentic @benchmark/core
 */ const NOTIFICATION_TASK_CODE_TOKEN = `${NOTIFICATION_TASK_TYPE_SYSTEM}|`;
/**
 * FHIR token matching unread notification Tasks via the `business-status`
 * search param.
 * @agentic @benchmark/core
 */ const NOTIFICATION_UNREAD_STATUS_TOKEN = `${NOTIFICATION_READ_STATUS_SYSTEM}|${NOTIFICATION_READ_STATUS.UNREAD.code}`;

/**
 * FHIR-Compliant Constants for Organization Domain
 *
 * This file contains all constants used for Organization resources,
 * including business partnership tier classification and organization type classification.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 */ // =============================================================================
// ORGANIZATION TIER CONSTANTS
// =============================================================================
/**
 * Organization Tier CodeSystem URL
 * Used in meta.tag for Organization resources to track business partnership tiers
 */ const ORGANIZATION_TIER_SYSTEM = "https://benchmarkhealth.com/fhir/CodeSystem/organization-tier";
/**
 * Organization Tier Options
 * Defines the three-tier classification system for business partnerships:
 * - Tier 1: QuIN Member Provider
 * - Tier 2: QuIN Candidate or Preferred Provider
 * - Tier 3: Non-QuIN Provider
 */ const ORGANIZATION_TIER_OPTIONS = [
    {
        code: "tier-1",
        display: "Tier 1",
        system: ORGANIZATION_TIER_SYSTEM,
        description: "QuIN Member Provider"
    },
    {
        code: "tier-2",
        display: "Tier 2",
        system: ORGANIZATION_TIER_SYSTEM,
        description: "QuIN Candidate or Preferred Provider"
    },
    {
        code: "tier-3",
        display: "Tier 3",
        system: ORGANIZATION_TIER_SYSTEM,
        description: "Non-QuIN Provider"
    }
];
/**
 * UI-ready select options for organization tier
 */ ORGANIZATION_TIER_OPTIONS.map((tier)=>({
        value: tier.code,
        label: tier.display,
        description: tier.description
    }));
// =============================================================================
// ORGANIZATION TYPE CONSTANTS
// =============================================================================
/**
 * FHIR R4 Standard Organization Type CodeSystem URL
 * Used in Organization.type for standard FHIR organization type classification
 */ const ORGANIZATION_TYPE_SYSTEM = "http://terminology.hl7.org/CodeSystem/organization-type";
/**
 * Custom Organization Type CodeSystem URL
 * Used in Organization.type for Benchmark-specific organization types
 */ const CUSTOM_ORGANIZATION_TYPE_SYSTEM = "https://benchmarkhealth.com/fhir/CodeSystem/organization-type";
/**
 * FHIR Standard Organization Type Options
 * Standard FHIR R4 organization type codes from HL7 terminology
 * Commented out codes are not used in the current application
 */ const FHIR_ORGANIZATION_TYPE_OPTIONS = [
    {
        code: "prov",
        display: "Healthcare Provider",
        system: ORGANIZATION_TYPE_SYSTEM
    },
    // {
    //   code: "dept",
    //   display: "Hospital Department",
    //   system: ORGANIZATION_TYPE_SYSTEM,
    // },
    // {
    //   code: "team",
    //   display: "Organizational Team",
    //   system: ORGANIZATION_TYPE_SYSTEM,
    // },
    // {
    //   code: "govt",
    //   display: "Government",
    //   system: ORGANIZATION_TYPE_SYSTEM,
    // },
    {
        code: "ins",
        display: "Insurance Company",
        system: ORGANIZATION_TYPE_SYSTEM
    },
    {
        code: "pay",
        display: "Payer",
        system: ORGANIZATION_TYPE_SYSTEM
    },
    // {
    //   code: "edu",
    //   display: "Educational Institute",
    //   system: ORGANIZATION_TYPE_SYSTEM,
    // },
    // {
    //   code: "reli",
    //   display: "Religious Institution",
    //   system: ORGANIZATION_TYPE_SYSTEM,
    // },
    // {
    //   code: "crs",
    //   display: "Clinical Research Sponsor",
    //   system: ORGANIZATION_TYPE_SYSTEM,
    // },
    {
        code: "cg",
        display: "Community Group",
        system: ORGANIZATION_TYPE_SYSTEM
    }
];
/**
 * Custom Organization Type Options
 * Benchmark-specific organization types not in the FHIR standard
 */ const CUSTOM_ORGANIZATION_TYPE_OPTIONS = [
    {
        code: "ehr",
        display: "Electronic Health Record Provider",
        system: CUSTOM_ORGANIZATION_TYPE_SYSTEM
    },
    {
        code: "referring-practice",
        display: "Referring Practice",
        system: CUSTOM_ORGANIZATION_TYPE_SYSTEM
    },
    {
        code: "private-practice",
        display: "Private Practice",
        system: CUSTOM_ORGANIZATION_TYPE_SYSTEM
    }
];
/**
 * Combined Organization Type Options
 * All organization types (FHIR standard + custom) for use in forms and UI
 */ const ORGANIZATION_TYPE_OPTIONS = [
    ...FHIR_ORGANIZATION_TYPE_OPTIONS,
    ...CUSTOM_ORGANIZATION_TYPE_OPTIONS
];
/**
 * Service Provider Type Options
 * Healthcare service provider organization types for UI distinction
 * Contains: Healthcare Provider, Community Group, and Private Practice
 */ [
    FHIR_ORGANIZATION_TYPE_OPTIONS.find((t)=>t.code === "prov"),
    FHIR_ORGANIZATION_TYPE_OPTIONS.find((t)=>t.code === "cg"),
    CUSTOM_ORGANIZATION_TYPE_OPTIONS.find((t)=>t.code === "private-practice")
];
/**
 * Insurance Provider Type Options
 * Insurance and payer organization types for UI distinction
 * Contains: Insurance Company and Payer
 */ [
    FHIR_ORGANIZATION_TYPE_OPTIONS.find((t)=>t.code === "ins"),
    FHIR_ORGANIZATION_TYPE_OPTIONS.find((t)=>t.code === "pay")
];
/**
 * UI-ready select options for organization type
 */ ORGANIZATION_TYPE_OPTIONS.map((type)=>({
        value: type.code,
        label: type.display
    }));

/**
 * PlanDefinition Type System
 * Custom system for plan definition templates
 */ const PLAN_DEFINITION_TYPE_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/plan-type";
/**
 * PlanDefinition Type Options
 * Defines available plan definition templates
 */ const PLAN_DEFINITION_TYPE_OPTIONS = [
    {
        code: "intake-protocol",
        display: "Intake Protocol",
        system: PLAN_DEFINITION_TYPE_SYSTEM,
        description: "Standard intake protocol for new patients",
        canonical: "PlanDefinition/intake-protocol"
    },
    {
        code: "triage-protocol",
        display: "Triage Protocol",
        system: PLAN_DEFINITION_TYPE_SYSTEM,
        description: "Standard triage protocol for ongoing care coordination",
        canonical: "PlanDefinition/triage-protocol"
    },
    {
        code: "cocm-protocol",
        display: "CoCM Protocol",
        system: PLAN_DEFINITION_TYPE_SYSTEM,
        description: "Standard Collaborative Care Model protocol",
        canonical: "PlanDefinition/cocm-protocol"
    }
];
/**
 * PlanDefinition Resources
 * FHIR PlanDefinition resources that define care protocols
 */ /**
 * Intake Protocol PlanDefinition
 *
 * Defines the standard intake workflow for new members.
 * Activities: Care Team Assignment → Initial Contact → Consent → Intake Assessment → Care Plan Selection
 * Uses ActivityDefinition constants as the single source of truth.
 */ ({
    action: getActivityDefinitionsByProtocol("intake").map((activity, index)=>{
        const action = {
            id: activity.code,
            title: activity.display,
            description: activity.description,
            code: [
                {
                    coding: [
                        {
                            system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
                            code: activity.code,
                            display: getActivityDefinitionDisplay(activity.code)
                        }
                    ],
                    text: activity.description
                }
            ]
        };
        // Add relationship chain: each activity comes after the previous one
        if (index > 0) {
            const previousActivity = getActivityDefinitionsByProtocol("intake")[index - 1];
            if (previousActivity) {
                action.relatedAction = [
                    {
                        actionId: previousActivity.code,
                        relationship: "after"
                    }
                ];
            }
        }
        return action;
    })
});
/**
 * Triage Protocol PlanDefinition
 *
 * Defines the standard triage workflow for members requiring additional care coordination.
 * Activities: Resource Offering → Resource Acceptance → Appointment Scheduling → Appointment Completion → Needs Evaluation
 * Uses ActivityDefinition constants as the single source of truth.
 */ ({
    action: getActivityDefinitionsByProtocol("triage").map((activity, index)=>{
        const action = {
            id: activity.code,
            title: activity.display,
            description: activity.description,
            code: [
                {
                    coding: [
                        {
                            system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
                            code: activity.code,
                            display: getActivityDefinitionDisplay(activity.code)
                        }
                    ],
                    text: activity.description
                }
            ]
        };
        // Add relationship chain: each activity comes after the previous one
        if (index > 0) {
            const previousActivity = getActivityDefinitionsByProtocol("triage")[index - 1];
            if (previousActivity) {
                action.relatedAction = [
                    {
                        actionId: previousActivity.code,
                        relationship: "after"
                    }
                ];
            }
        }
        return action;
    })
});
/**
 * CoCM Protocol PlanDefinition
 *
 * Defines the standard Collaborative Care Model workflow.
 * Sequential activities must happen in order; flexible activities can occur in any order and may repeat.
 * Uses ActivityDefinition constants as the single source of truth.
 */ ({
    action: getActivityDefinitionsByProtocol("cocm").map((activity, index)=>{
        const action = {
            id: activity.code,
            title: activity.display,
            description: activity.description,
            code: [
                {
                    coding: [
                        {
                            system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
                            code: activity.code,
                            display: getActivityDefinitionDisplay(activity.code)
                        }
                    ],
                    text: activity.description
                }
            ]
        };
        // Add relationship chain: sequential activities come after the previous one
        // Flexible activities don't have strict ordering requirements
        const cocmActivities = getActivityDefinitionsByProtocol("cocm");
        if (index > 0) {
            const previousActivity = cocmActivities[index - 1];
            if (previousActivity) {
                action.relatedAction = [
                    {
                        actionId: previousActivity.code,
                        relationship: "after"
                    }
                ];
            }
        }
        return action;
    })
});
/**
 * UI-ready select options
 */ PLAN_DEFINITION_TYPE_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));

/**
 * CodeSystem URL for the selected operational program value.
 * @agentic @benchmark/core
 */ const OPERATIONAL_PROGRAM_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/operational-program";
/**
 * Operational program options: the programs a patient can be enrolled into after intake.
 * @agentic @benchmark/core
 */ const OPERATIONAL_PROGRAM_OPTIONS = [
    {
        code: "triage",
        display: "Triage",
        system: OPERATIONAL_PROGRAM_SYSTEM,
        description: "Benchmark Care Triage & Navigation"
    },
    {
        code: "cocm",
        display: "CoCM",
        system: OPERATIONAL_PROGRAM_SYSTEM,
        description: "Benchmark Collaborative Care Model"
    }
];
/**
 * UI-ready select options for operational programs.
 * @agentic @benchmark/core
 */ OPERATIONAL_PROGRAM_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));
/**
 * CodeSystem URL for program selection reason codes.
 * @agentic @benchmark/core
 */ const PROGRAM_SELECTION_REASON_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/program-selection-reason";
/**
 * Reason code options for justifying program selection.
 * @agentic @benchmark/core
 */ const PROGRAM_SELECTION_REASON_OPTIONS = [
    {
        code: "clinical-need-acuity",
        display: "Clinical Need or Acuity",
        system: PROGRAM_SELECTION_REASON_SYSTEM
    },
    {
        code: "insurance-cost",
        display: "Insurance or Cost",
        system: PROGRAM_SELECTION_REASON_SYSTEM
    },
    {
        code: "patient-preference",
        display: "Patient Preference",
        system: PROGRAM_SELECTION_REASON_SYSTEM
    },
    {
        code: "pcp-recommendation",
        display: "PCP Recommendation",
        system: PROGRAM_SELECTION_REASON_SYSTEM
    }
];
/**
 * UI-ready select options for program selection reasons.
 * @agentic @benchmark/core
 */ PROGRAM_SELECTION_REASON_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display
    }));

/**
 * FHIR-Compliant Constants for Practitioner Domain
 *
 * This file contains all constants used in Practitioner forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 */ // FHIR R4-compliant specialty options from Practice Setting Code Value Set
// http://hl7.org/fhir/ValueSet/c80-practice-codes
// Based on SNOMED CT International edition
const PRACTITIONER_SPECIALTY_OPTIONS = [
    {
        code: "394576009",
        display: "Accident & emergency",
        system: "http://snomed.info/sct"
    },
    {
        code: "28351000087106",
        display: "Addiction specialty",
        system: "http://snomed.info/sct"
    },
    {
        code: "408467006",
        display: "Adult mental illness",
        system: "http://snomed.info/sct"
    },
    {
        code: "394577000",
        display: "Anesthetics",
        system: "http://snomed.info/sct"
    },
    {
        code: "394579002",
        display: "Cardiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394588006",
        display: "Child and adolescent psychiatry",
        system: "http://snomed.info/sct"
    },
    {
        code: "394803006",
        display: "Clinical hematology",
        system: "http://snomed.info/sct"
    },
    {
        code: "1255918004",
        display: "Clinical psychology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408454008",
        display: "Clinical microbiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408478003",
        display: "Critical care medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394582007",
        display: "Dermatology",
        system: "http://snomed.info/sct"
    },
    {
        code: "1255916000",
        display: "Educational psychology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394583002",
        display: "Endocrinology",
        system: "http://snomed.info/sct"
    },
    {
        code: "419772000",
        display: "Family practice",
        system: "http://snomed.info/sct"
    },
    {
        code: "394584008",
        display: "Gastroenterology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408444009",
        display: "General dental practice",
        system: "http://snomed.info/sct"
    },
    {
        code: "394802001",
        display: "General medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394915009",
        display: "General pathology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394814009",
        display: "General practice",
        system: "http://snomed.info/sct"
    },
    {
        code: "394811001",
        display: "Geriatric medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394586005",
        display: "Gynecology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394597005",
        display: "Histopathology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394807007",
        display: "Infectious diseases",
        system: "http://snomed.info/sct"
    },
    {
        code: "419192003",
        display: "Internal medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "408468001",
        display: "Learning disability - specialty",
        system: "http://snomed.info/sct"
    },
    {
        code: "394593009",
        display: "Medical oncology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394733009",
        display: "Medical specialty - Other",
        system: "http://snomed.info/sct"
    },
    {
        code: "394589003",
        display: "Nephrology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394591006",
        display: "Neurology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394649004",
        display: "Nuclear medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394585009",
        display: "Obstetrics and gynecology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408470005",
        display: "Obstetrics",
        system: "http://snomed.info/sct"
    },
    {
        code: "394821009",
        display: "Occupational medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394594003",
        display: "Ophthalmology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394605001",
        display: "Oral surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394608004",
        display: "Orthodontics",
        system: "http://snomed.info/sct"
    },
    {
        code: "394882004",
        display: "Pain management",
        system: "http://snomed.info/sct"
    },
    {
        code: "394806003",
        display: "Palliative medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "408459003",
        display: "Pediatric cardiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394607009",
        display: "Pediatric dentistry",
        system: "http://snomed.info/sct"
    },
    {
        code: "419610006",
        display: "Pediatric endocrinology",
        system: "http://snomed.info/sct"
    },
    {
        code: "418058008",
        display: "Pediatric gastroenterology",
        system: "http://snomed.info/sct"
    },
    {
        code: "419365004",
        display: "Pediatric nephrology",
        system: "http://snomed.info/sct"
    },
    {
        code: "418002000",
        display: "Pediatric oncology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394539006",
        display: "Pediatric surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "409968004",
        display: "Preventive medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394587001",
        display: "Psychiatry",
        system: "http://snomed.info/sct"
    },
    {
        code: "394817002",
        display: "Forensic psychiatry",
        system: "http://snomed.info/sct"
    },
    {
        code: "1255881004",
        display: "Mental health nursing",
        system: "http://snomed.info/sct"
    },
    {
        code: "721961006",
        display: "Psychosomatic medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394913002",
        display: "Psychotherapy",
        system: "http://snomed.info/sct"
    },
    {
        code: "722162001",
        display: "Psychology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408440000",
        display: "Public health medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "419815003",
        display: "Radiation oncology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394914008",
        display: "Radiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408455009",
        display: "Radiology - Interventional radiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394602003",
        display: "Rehabilitation",
        system: "http://snomed.info/sct"
    },
    {
        code: "394810000",
        display: "Rheumatology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408466002",
        display: "Surgery - Cardiac surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394609007",
        display: "Surgery - General",
        system: "http://snomed.info/sct"
    },
    {
        code: "394610002",
        display: "Surgery - Neurosurgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394611003",
        display: "Surgery - Plastic surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394801008",
        display: "Surgery - Trauma and orthopedics",
        system: "http://snomed.info/sct"
    },
    {
        code: "408463005",
        display: "Surgery - Vascular",
        system: "http://snomed.info/sct"
    },
    {
        code: "1251536003",
        display: "Sport medicine/psychology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394732004",
        display: "Surgical specialty - Other",
        system: "http://snomed.info/sct"
    },
    {
        code: "394590007",
        display: "Thoracic medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394612005",
        display: "Urology",
        system: "http://snomed.info/sct"
    }
];
// UI-specific mappings for Select components
PRACTITIONER_SPECIALTY_OPTIONS.map((specialty)=>({
        value: specialty.code,
        label: specialty.display
    }));

/** HL7 v2 Table 0360 — Degree/License/Certificate. */ const PRACTITIONER_QUALIFICATION_HL7_SYSTEM = `${HL7_TERMINOLOGY_BASE}/CodeSystem/v2-0360`;
/** Benchmark-owned CodeSystem for credentials HL7 v2-0360 does not define. */ const PRACTITIONER_QUALIFICATION_BENCHMARK_SYSTEM = `${ORGANIZATION_BASE}/CodeSystem/practitioner-qualification`;
/**
 * The complete v2-0360 value set (HL7 Terminology v7.3.0), with the published
 * casing — `PHD` not `PhD`, `RPH` not `RPh`, but `PharmD` genuinely mixed.
 */ const HL7_PRACTITIONER_QUALIFICATION_OPTIONS = [
    {
        code: "PN",
        display: "Advanced Practice Nurse"
    },
    {
        code: "AAS",
        display: "Associate of Applied Science"
    },
    {
        code: "AA",
        display: "Associate of Arts"
    },
    {
        code: "ABA",
        display: "Associate of Business Administration"
    },
    {
        code: "AE",
        display: "Associate of Engineering"
    },
    {
        code: "AS",
        display: "Associate of Science"
    },
    {
        code: "BA",
        display: "Bachelor of Arts"
    },
    {
        code: "BBA",
        display: "Bachelor of Business Administration"
    },
    {
        code: "BE",
        display: "Bachelor or Engineering"
    },
    {
        code: "BFA",
        display: "Bachelor of Fine Arts"
    },
    {
        code: "BN",
        display: "Bachelor of Nursing"
    },
    {
        code: "BS",
        display: "Bachelor of Science"
    },
    {
        code: "BSL",
        display: "Bachelor of Science - Law"
    },
    {
        code: "BSN",
        display: "Bachelor on Science - Nursing"
    },
    {
        code: "BT",
        display: "Bachelor of Theology"
    },
    {
        code: "CER",
        display: "Certificate"
    },
    {
        code: "CANP",
        display: "Certified Adult Nurse Practitioner"
    },
    {
        code: "CMA",
        display: "Certified Medical Assistant"
    },
    {
        code: "CNP",
        display: "Certified Nurse Practitioner"
    },
    {
        code: "CNM",
        display: "Certified Nurse Midwife"
    },
    {
        code: "CRN",
        display: "Certified Registered Nurse"
    },
    {
        code: "CNS",
        display: "Certified Nurse Specialist"
    },
    {
        code: "CPNP",
        display: "Certified Pediatric Nurse Practitioner"
    },
    {
        code: "CTR",
        display: "Certified Tumor Registrar"
    },
    {
        code: "DIP",
        display: "Diploma"
    },
    {
        code: "DBA",
        display: "Doctor of Business Administration"
    },
    {
        code: "DED",
        display: "Doctor of Education"
    },
    {
        code: "PharmD",
        display: "Doctor of Pharmacy"
    },
    {
        code: "PHE",
        display: "Doctor of Engineering"
    },
    {
        code: "PHD",
        display: "Doctor of Philosophy"
    },
    {
        code: "PHS",
        display: "Doctor of Science"
    },
    {
        code: "MD",
        display: "Doctor of Medicine"
    },
    {
        code: "DO",
        display: "Doctor of Osteopathy"
    },
    {
        code: "EMT",
        display: "Emergency Medical Technician"
    },
    {
        code: "EMTP",
        display: "Emergency Medical Technician - Paramedic"
    },
    {
        code: "FPNP",
        display: "Family Practice Nurse Practitioner"
    },
    {
        code: "HS",
        display: "High School Graduate"
    },
    {
        code: "JD",
        display: "Juris Doctor"
    },
    {
        code: "MA",
        display: "Master of Arts"
    },
    {
        code: "MBA",
        display: "Master of Business Administration"
    },
    {
        code: "MCE",
        display: "Master of Civil Engineering"
    },
    {
        code: "MDI",
        display: "Master of Divinity"
    },
    {
        code: "MED",
        display: "Master of Education"
    },
    {
        code: "MEE",
        display: "Master of Electrical Engineering"
    },
    {
        code: "ME",
        display: "Master of Engineering"
    },
    {
        code: "MFA",
        display: "Master of Fine Arts"
    },
    {
        code: "MME",
        display: "Master of Mechanical Engineering"
    },
    {
        code: "MS",
        display: "Master of Science"
    },
    {
        code: "MSL",
        display: "Master of Science - Law"
    },
    {
        code: "MSN",
        display: "Master of Science - Nursing"
    },
    {
        code: "MTH",
        display: "Master of Theology"
    },
    {
        code: "MDA",
        display: "Medical Assistant"
    },
    {
        code: "MT",
        display: "Medical Technician"
    },
    {
        code: "NG",
        display: "Non-Graduate"
    },
    {
        code: "NP",
        display: "Nurse Practitioner"
    },
    {
        code: "PA",
        display: "Physician Assistant"
    },
    {
        code: "RMA",
        display: "Registered Medical Assistant"
    },
    {
        code: "RN",
        display: "Registered Nurse"
    },
    {
        code: "RPH",
        display: "Registered Pharmacist"
    },
    {
        code: "SEC",
        display: "Secretarial Certificate"
    },
    {
        code: "TS",
        display: "Trade School Graduate"
    }
];
/**
 * Credentials Benchmark clinicians hold that v2-0360 does not define.
 *
 * Provenance: behavioral-health licenses referenced across this codebase
 * (`LCSW` in the CoCM billing packet and MCP directory; `PsyD`, `LMHC`,
 * `LICSW`, `LPC`, `LADC`, `MSW` in the practice-name credential parser) plus
 * the advanced-practice and referring-provider credentials the
 * `referring-practice` CLI has always accepted (`DNP`, `FNP`, `PNP`, `CRNA`,
 * `MBBS`, `DPM`, `DC`, `OD`, `DDS`, `DMD`), and the state-variant counseling
 * and psychiatric-nursing licenses that accompany them.
 */ const BENCHMARK_PRACTITIONER_QUALIFICATION_OPTIONS = [
    {
        code: "LCSW",
        display: "Licensed Clinical Social Worker"
    },
    {
        code: "LICSW",
        display: "Licensed Independent Clinical Social Worker"
    },
    {
        code: "LMSW",
        display: "Licensed Master Social Worker"
    },
    {
        code: "LSW",
        display: "Licensed Social Worker"
    },
    {
        code: "MSW",
        display: "Master of Social Work"
    },
    {
        code: "LPC",
        display: "Licensed Professional Counselor"
    },
    {
        code: "LPCC",
        display: "Licensed Professional Clinical Counselor"
    },
    {
        code: "LCPC",
        display: "Licensed Clinical Professional Counselor"
    },
    {
        code: "LMHC",
        display: "Licensed Mental Health Counselor"
    },
    {
        code: "LMFT",
        display: "Licensed Marriage and Family Therapist"
    },
    {
        code: "LADC",
        display: "Licensed Alcohol and Drug Counselor"
    },
    {
        code: "CADC",
        display: "Certified Alcohol and Drug Counselor"
    },
    {
        code: "PsyD",
        display: "Doctor of Psychology"
    },
    {
        code: "PMHNP",
        display: "Psychiatric-Mental Health Nurse Practitioner"
    },
    {
        code: "APRN",
        display: "Advanced Practice Registered Nurse"
    },
    {
        code: "DNP",
        display: "Doctor of Nursing Practice"
    },
    {
        code: "FNP",
        display: "Family Nurse Practitioner"
    },
    {
        code: "PNP",
        display: "Pediatric Nurse Practitioner"
    },
    {
        code: "CRNA",
        display: "Certified Registered Nurse Anesthetist"
    },
    {
        code: "MBBS",
        display: "Bachelor of Medicine, Bachelor of Surgery"
    },
    {
        code: "DPM",
        display: "Doctor of Podiatric Medicine"
    },
    {
        code: "DC",
        display: "Doctor of Chiropractic"
    },
    {
        code: "OD",
        display: "Doctor of Optometry"
    },
    {
        code: "DDS",
        display: "Doctor of Dental Surgery"
    },
    {
        code: "DMD",
        display: "Doctor of Dental Medicine"
    }
];
/** Every qualification concept, HL7 first, each tagged with its own system. */ const PRACTITIONER_QUALIFICATION_OPTIONS = [
    ...HL7_PRACTITIONER_QUALIFICATION_OPTIONS.map((option)=>({
            ...option,
            system: PRACTITIONER_QUALIFICATION_HL7_SYSTEM
        })),
    ...BENCHMARK_PRACTITIONER_QUALIFICATION_OPTIONS.map((option)=>({
            ...option,
            system: PRACTITIONER_QUALIFICATION_BENCHMARK_SYSTEM
        }))
];
/**
 * Case-folded lookup. Built once; the accompanying test asserts the two systems
 * never contribute the same folded key, so a lookup is never ambiguous.
 */ new Map(PRACTITIONER_QUALIFICATION_OPTIONS.map((option)=>[
        option.code.toUpperCase(),
        option
    ]));

// Business role codes use HL7 practitioner-role CodeSystem with our organization-specific codes
const PRACTITIONER_ROLE_CODE_SYSTEM = `${HL7_TERMINOLOGY_BASE}/CodeSystem/practitioner-role`;
const PRACTITIONER_ROLE_CODE_OPTIONS = [
    {
        code: "clinical-director",
        display: "Clinical Director",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Clinical leadership responsible for care program oversight"
    },
    {
        code: "lead-advocate",
        display: "Lead Advocate",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Senior LCSW responsible for licensed member care"
    },
    {
        code: "care-manager",
        display: "Care Manager",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Care coordination and ongoing member management"
    },
    {
        code: "intake-coordinator",
        display: "Intake Coordinator",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Responsible for scheduling and completing member intake"
    },
    {
        code: "special-operations-director",
        display: "Special Ops Director",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Operations leadership for non-licensed member programs"
    },
    {
        code: "special-operations-associate",
        display: "Special Ops Associate",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Operations Specialist responsible for non-licensed member outreach and follow-up"
    },
    {
        code: "consulting-psychiatrist",
        display: "Consulting Psychiatrist",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Psychiatric consultation for CoCM and complex cases"
    }
];
new Set(PRACTITIONER_ROLE_CODE_OPTIONS.map((o)=>o.code));
// UI-ready options for Select/CardSelect components
PRACTITIONER_ROLE_CODE_OPTIONS.map((o)=>({
        value: o.code,
        label: o.display,
        description: o.description
    }));
PRACTITIONER_ROLE_CODE_OPTIONS.map((o)=>({
        value: o.code,
        label: o.display,
        description: o.description
    }));
/**
 * Case-folded lookup keyed by both the code and the display label, so operator
 * input can be either (`Clinical Director` or `clinical-director`).
 */ new Map(PRACTITIONER_ROLE_CODE_OPTIONS.flatMap((option)=>[
        [
            foldRoleInput(option.code),
            option
        ],
        [
            foldRoleInput(option.display),
            option
        ]
    ]));
function foldRoleInput(input) {
    return input.trim().toLowerCase().replace(/[\s_]+/g, "-");
}

// PHQ-4 Questions (first 2 from PHQ-9, first 2 from GAD-7)
[
    {
        linkId: "phq4-1",
        text: "Little interest or pleasure in doing things",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq4-2",
        text: "Feeling down, depressed, or hopeless",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq4-3",
        text: "Feeling nervous, anxious or on edge",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq4-4",
        text: "Not being able to stop or control worrying",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    }
];
// PHQ-9 Questions
[
    {
        linkId: "phq9-1",
        text: "Little interest or pleasure in doing things",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-2",
        text: "Feeling down, depressed, or hopeless",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-3",
        text: "Trouble falling or staying asleep, or sleeping too much",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-4",
        text: "Feeling tired or having little energy",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-5",
        text: "Poor appetite or overeating",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-6",
        text: "Feeling bad about yourself - or that you are a failure or have let yourself or your family down",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-7",
        text: "Trouble concentrating on things, such as reading the newspaper or watching television",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-8",
        text: "Moving or speaking so slowly that other people could have noticed? Or the opposite - being so fidgety or restless that you have been moving around a lot more than usual",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-9",
        text: "Thoughts that you would be better off dead, or of hurting yourself in some way",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    // Functional impairment item (not scored in PHQ-9 total)
    // TODO(#989): Add LOINC item coding for PHQ-9 items, including impairment, to align with FHIR best practices
    {
        linkId: "phq9-10",
        text: "If you checked off any problems, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?",
        type: "choice",
        required: false,
        scorable: false,
        options: [
            {
                value: 0,
                display: "Not difficult at all"
            },
            {
                value: 1,
                display: "Somewhat difficult"
            },
            {
                value: 2,
                display: "Very difficult"
            },
            {
                value: 3,
                display: "Extremely difficult"
            }
        ]
    }
];
// PHQ-9A Questions (Adolescent version, ages 12-18)
// Same core questions as PHQ-9, with adolescent-appropriate language
[
    {
        linkId: "phq9a-1",
        text: "Little interest or pleasure in doing things",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-2",
        text: "Feeling down, depressed, irritable, or hopeless",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-3",
        text: "Trouble falling asleep, staying asleep, or sleeping too much",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-4",
        text: "Feeling tired or having little energy",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-5",
        text: "Poor appetite, weight loss, or overeating",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-6",
        text: "Feeling bad about yourself - or feeling that you are a failure, or that you have let yourself or your family down",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-7",
        text: "Trouble concentrating on things like school work, reading, or watching TV",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-8",
        text: "Moving or speaking so slowly that other people could have noticed? Or the opposite - being so fidgety or restless that you were moving around a lot more than usual",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-9",
        text: "Thoughts that you would be better off dead, or of hurting yourself in some way",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    // Functional impairment item (not scored in PHQ-9A total)
    {
        linkId: "phq9a-10",
        text: "If you checked off any problems, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?",
        type: "choice",
        required: false,
        scorable: false,
        options: [
            {
                value: 0,
                display: "Not difficult at all"
            },
            {
                value: 1,
                display: "Somewhat difficult"
            },
            {
                value: 2,
                display: "Very difficult"
            },
            {
                value: 3,
                display: "Extremely difficult"
            }
        ]
    }
];
// GAD-7 Questions
[
    {
        linkId: "gad7-1",
        text: "Feeling nervous, anxious or on edge",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad7-2",
        text: "Not being able to stop or control worrying",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad7-3",
        text: "Worrying too much about different things",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad7-4",
        text: "Trouble relaxing",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad7-5",
        text: "Being so restless that it is hard to sit still",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad7-6",
        text: "Becoming easily annoyed or irritable",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad7-7",
        text: "Feeling afraid as if something awful might happen",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    // Functional impairment item (not scored in GAD-7 total)
    // TODO(#989): Add LOINC item coding for GAD-7 items, including impairment, to align with FHIR best practices
    {
        linkId: "gad7-8",
        text: "If you checked off any problems, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?",
        type: "choice",
        required: false,
        scorable: false,
        options: [
            {
                value: 0,
                display: "Not difficult at all"
            },
            {
                value: 1,
                display: "Somewhat difficult"
            },
            {
                value: 2,
                display: "Very difficult"
            },
            {
                value: 3,
                display: "Extremely difficult"
            }
        ]
    }
];
/**
 * GAD-2 anxiety short check (D17). Its two items are, verbatim, the first two
 * GAD-7 items; the instrument is scored 0–6 on its own scale and recorded under
 * the GAD-2 name — never as a GAD-7 severity. A positive GAD-2 (published cut
 * ≥3) opens GAD-7 as a separate instrument in the adaptive flow.
 */ [
    {
        linkId: "gad2-1",
        text: "Feeling nervous, anxious or on edge",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad2-2",
        text: "Not being able to stop or control worrying",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    }
];
// PSC-17 Answer Options (3-point scale: Never, Sometimes, Often)
const PSC_17_FREQUENCY_OPTIONS = [
    {
        value: 0,
        coding: {
            code: "LA6270-8",
            display: "Never"
        }
    },
    {
        value: 1,
        coding: {
            code: "LA10082-8",
            display: "Sometimes"
        }
    },
    {
        value: 2,
        coding: {
            code: "LA10044-8",
            display: "Often"
        }
    }
];
// PSC-17 Questions (Pediatric Symptom Checklist-17)
// Parent-report screening for psychosocial dysfunction in children ages 4-18
// Subscales: Attention (1,2,3,4,7), Internalizing (5,6,9,10,11), Externalizing (8,12,13,14,15,16,17)
[
    // Attention subscale items: 1, 2, 3, 4, 7
    {
        linkId: "psc17-1",
        text: "Fidgety, unable to sit still",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-2",
        text: "Acts as if driven by a motor",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-3",
        text: "Daydreams too much",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-4",
        text: "Distracted easily",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    // Internalizing subscale items: 5, 6, 9, 10, 11
    {
        linkId: "psc17-5",
        text: "Feels sad, unhappy",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-6",
        text: "Feels hopeless",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    // Attention subscale item: 7
    {
        linkId: "psc17-7",
        text: "Has trouble concentrating",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    // Externalizing subscale items: 8, 12, 13, 14, 15, 16, 17
    {
        linkId: "psc17-8",
        text: "Fights with other children",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    // Internalizing subscale items: 9, 10, 11
    {
        linkId: "psc17-9",
        text: "Is down on him or herself",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-10",
        text: "Blames self for problems",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-11",
        text: "Seems to be having less fun",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    // Externalizing subscale items: 12, 13, 14, 15, 16, 17
    {
        linkId: "psc17-12",
        text: "Does not listen to rules",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-13",
        text: "Does not understand other people's feelings",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-14",
        text: "Teases others",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-15",
        text: "Blames others for his or her troubles",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-16",
        text: "Takes things that do not belong to him or her",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-17",
        text: "Refuses to share",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    }
];

const SERVICE_MODE_SELECT_OPTION_VALUES = [
    "in-person",
    "telehealth-video",
    "telehealth-phone",
    "text-sms",
    "hybrid"
];
/**
 * Referral mode select options with shared display labels.
 * @agentic @benchmark/core
 */ const REFERRAL_MODE_OPTIONS = [
    {
        value: "in-person",
        label: "In-Person Preferred"
    },
    {
        value: "telehealth-video",
        label: "Telehealth Preferred"
    },
    {
        value: "hybrid",
        label: "Flexible"
    }
];
/**
 * Service mode select options used when choosing a concrete referral delivery mode.
 * @agentic @benchmark/core
 */ const SERVICE_MODE_SELECT_OPTIONS = SERVICE_MODE_SELECT_OPTION_VALUES.map((value)=>({
        value,
        label: SERVICE_CHARACTERISTIC_VISIT_MODE_OPTIONS.find((option)=>option.value === value)?.label ?? value
    }));
new Map(REFERRAL_MODE_OPTIONS.map((option)=>[
        option.value,
        option.label
    ]));
new Map(SERVICE_MODE_SELECT_OPTIONS.map((option)=>[
        option.value,
        option.label
    ]));

/**
 * SNOMED CT category codes for ServiceRequest classification.
 * Separates inbound referrals (from PCP) from outbound referrals (to external services).
 * @agentic @benchmark/core
 */ const SERVICE_REQUEST_CATEGORY = {
    OUTBOUND_REFERRAL: {
        system: "http://snomed.info/sct",
        code: "306090001"}
};

/**
 * Canonical system URL for Benchmark Health task types.
 * Use this constant instead of hardcoding the URL in your code.
 */ const TASK_TYPE_SYSTEM = "http://www.benchmarkhealth.com/fhir/ValueSet/task-type";
/**
 * Predefined Task types for healthcare workflows.
 * Using custom system for business-specific task categories.
 *
 * OCPE Phase 5: Several standalone task types are deprecated and replaced by
 * Task-backed CarePlan activities (v2 taxonomy). Deprecated types are excluded
 * from the task creation UI via `isDeprecatedStandaloneTaskType`, but remain
 * here for display/lookup of existing tasks in production.
 */ const TASK_TYPE_OPTIONS = [
    {
        /** @deprecated Replaced by intake plan activities: schedule-intake, complete-intake, finalize-care-plan-enrollment */ code: "new-member-intake",
        display: "Complete New Member Intake",
        description: "Finish new member intake with initial assessment",
        instructions: "Either complete 90791 intake for the individual or document that the individual has accepted Benchmark Health's services.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "routine",
        estimatedDuration: 30,
        isRecurring: false,
        cadenceWeeks: []
    },
    {
        /** @deprecated Replaced by triage plan activity: offer-services */ code: "offer-member-resources",
        display: "Offer Resources to Member",
        description: "Find appropriate healthcare services and facilitate connection for member",
        instructions: "Find free or clinical resource options and share them with the patient. Document the interaction or communication.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "routine",
        estimatedDuration: 20,
        isRecurring: false,
        cadenceWeeks: []
    },
    {
        /** @deprecated Replaced by triage plan activity: schedule-services */ code: "schedule-member-appointment",
        display: "Schedule Appointment for Member",
        description: "Schedule an appointment for the member to receive care",
        instructions: "Schedule or confirm a member's appointment. Log the appointment in connections.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "urgent",
        estimatedDuration: 20,
        isRecurring: false,
        cadenceWeeks: []
    },
    {
        /** @deprecated Replaced by plan activity: check-in-on-member (intake as-needed) */ code: "member-check-in",
        display: "Check In on Member",
        description: "General check-in on member to assess progress or needs",
        instructions: "General check in with member. Log the interaction.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "routine",
        estimatedDuration: 15,
        isRecurring: false,
        cadenceWeeks: []
    },
    {
        /** @deprecated NPS surveys are now handled by automation, no longer a plan activity */ code: "send-nps-survey",
        display: "Send NPS Survey",
        description: "Send Net Promoter Score survey to assess member satisfaction with Benchmark",
        instructions: "Send Member NPS survey and log the communication.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "routine",
        estimatedDuration: 5,
        isRecurring: false,
        cadenceWeeks: []
    },
    {
        /** @deprecated Replaced by triage plan activity: follow-up-on-services */ code: "post-care-followup",
        display: "Complete Post-Care Followup",
        description: "Check-in with member after appointment completion to assess progress or needs",
        instructions: "Check in with the member after their appointment. Log the interaction and update the status of the appointment.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "routine",
        estimatedDuration: 20,
        isRecurring: false,
        cadenceWeeks: []
    },
    {
        /** @deprecated Replaced by triage plan activity: complete-cadenced-followup */ code: "cadenced-followup",
        display: "Complete Cadenced Followup",
        description: "Check-in at 6 week intervals to assess progress or needs",
        instructions: "Send member 6 week outcome questionnaire and log the communication. Log results in an encounter if the member responds.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "routine",
        estimatedDuration: 15,
        isRecurring: true,
        cadenceWeeks: [
            6,
            12,
            18
        ]
    }
];
const CUSTOM_TASK_TYPE = {
    display: "Custom Task"};
// Define groupings so multiple underlying task codes can share one tab.
// These groups are used for the TasksPage segmented filter control.
// Each group includes both legacy standalone codes (task-type system) and
// v2 plan-backed activity codes (care-plan-activity system) so the filter
// works for both old standalone tasks and new Task-backed CarePlan activities.
const TASK_TYPE_GROUPS = [
    {
        id: "intake",
        label: "Intake",
        codes: [
            "new-member-intake",
            "schedule-intake",
            "complete-intake",
            "finalize-care-plan-enrollment",
            "check-in-on-member"
        ]
    },
    {
        id: "triage",
        label: "Triage",
        codes: [
            "offer-member-resources",
            "schedule-member-appointment",
            "connect-member-care",
            "member-check-in",
            "post-care-followup",
            "cadenced-followup",
            "select-services",
            "offer-services",
            "schedule-services",
            "follow-up-on-services",
            "approve-selected-services",
            "complete-cadenced-followup"
        ]
    },
    {
        id: "cocm",
        label: "CoCM",
        codes: [
            // Note: `enroll-in-mirah` (v2 cocm sequential, retired with the
            // Mirah partnership wind-down) is intentionally absent; legacy task
            // displays still resolve via taxonomy-mapping for backwards
            // compatibility.
            "build-cocm-care-plan",
            "follow-up-on-cocm",
            "consult-psych",
            "consult-pcp",
            "discharge-member"
        ]
    },
    {
        id: "other",
        label: "Other",
        codes: [
            "custom-task",
            "send-nps-survey",
            "review-new-referral",
            "potential-duplicate-review",
            "manual-review-fax",
            "review-unmatched-fax"
        ]
    }
];
/**
 * FHIR R4 Task status options
 * http://hl7.org/fhir/task-status
 */ const TASK_STATUS_OPTIONS = [
    {
        code: "draft",
        display: "Draft",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "requested",
        display: "Requested",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "received",
        display: "Received",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "accepted",
        display: "Accepted",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "rejected",
        display: "Rejected",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "ready",
        display: "Ready",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "cancelled",
        display: "Cancelled",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "in-progress",
        display: "In Progress",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "on-hold",
        display: "On Hold",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "failed",
        display: "Failed",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "completed",
        display: "Completed",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "entered-in-error",
        display: "Entered in Error",
        system: "http://hl7.org/fhir/task-status"
    }
];
/**
 * FHIR R4 Task priority options
 * http://hl7.org/fhir/request-priority
 */ const TASK_PRIORITY_OPTIONS = [
    {
        code: "routine",
        display: "Routine",
        system: "http://hl7.org/fhir/request-priority"
    },
    {
        code: "urgent",
        display: "Urgent",
        system: "http://hl7.org/fhir/request-priority"
    },
    {
        code: "asap",
        display: "ASAP",
        system: "http://hl7.org/fhir/request-priority"
    },
    {
        code: "stat",
        display: "STAT",
        system: "http://hl7.org/fhir/request-priority"
    }
];
/**
 * Task intent options for workflow context
 * http://hl7.org/fhir/task-intent
 */ const TASK_INTENT_OPTIONS = [
    {
        code: "proposal",
        display: "Proposal",
        system: "http://hl7.org/fhir/task-intent"
    },
    {
        code: "plan",
        display: "Plan",
        system: "http://hl7.org/fhir/task-intent"
    },
    {
        code: "order",
        display: "Order",
        system: "http://hl7.org/fhir/task-intent"
    },
    {
        code: "original-order",
        display: "Original Order",
        system: "http://hl7.org/fhir/task-intent"
    }
];
// UI-specific mappings for Select components
// OCPE Phase 5: Exclude deprecated standalone task types from creation UI
TASK_TYPE_OPTIONS.filter((type)=>!isDeprecatedStandaloneTaskType(type.code)).map((type)=>({
        value: type.code,
        label: type.display,
        description: type.description,
        isRecurring: type.isRecurring || false
    }));
TASK_TYPE_GROUPS.map((group)=>({
        value: group.id,
        label: group.label,
        codes: group.codes
    }));
/**
 * Set of all known v2 plan-backed activity codes (CARE_PLAN_ACTIVITY_SYSTEM).
 * @agentic @benchmark/core
 */ const V2_ACTIVITY_CODE_SET = new Set(getAllCarePlanActivityOptions().map((a)=>a.code));
/**
 * Set of all known standalone task type codes (TASK_TYPE_SYSTEM).
 * @agentic @benchmark/core
 */ new Set(TASK_TYPE_OPTIONS.map((t)=>t.code));
/** Codes excluded from the "Other" filter group (deprecated / automated). @agentic connections */ const OTHER_GROUP_EXCLUDED_CODES = new Set([
    "send-nps-survey"
]);
/** Short display overrides for filter dropdown labels. @agentic connections */ const FILTER_DISPLAY_OVERRIDES = {
    "custom-task": CUSTOM_TASK_TYPE.display,
    "review-new-referral": "Fax | New Referral",
    "potential-duplicate-review": "Fax | Potential Duplicate",
    "manual-review-fax": "Fax | Manual Review",
    "review-unmatched-fax": "Fax | Unmatched"
};
/** Resolve display name for a code in the filter dropdown. @agentic connections */ const getFilterActivityDisplay = (code)=>FILTER_DISPLAY_OVERRIDES[code] ?? resolveActivityDisplay(code);
/**
 * Displayable activities per task type group for the filter dropdown.
 * For intake/triage/cocm: only v2 activity codes are shown as individual items.
 * For "other": all codes are shown. Legacy codes are included implicitly
 * via full-group selection when resolving FHIR queries.
 * @agentic connections
 */ const TASK_TYPE_FILTER_OPTIONS = TASK_TYPE_GROUPS.map((group)=>({
        id: group.id,
        label: group.label,
        activities: group.codes.filter((code)=>{
            if (group.id === "other") return !OTHER_GROUP_EXCLUDED_CODES.has(code);
            return V2_ACTIVITY_CODE_SET.has(code);
        }).map((code)=>({
                code,
                display: getFilterActivityDisplay(code)
            }))
    }));
/** Set of all individually-selectable activity codes in the filter. @agentic connections */ new Set(TASK_TYPE_FILTER_OPTIONS.flatMap((g)=>g.activities.map((a)=>a.code)));
/** Group ID lookup for fast deserialize. @agentic connections */ new Map(TASK_TYPE_FILTER_OPTIONS.map((g)=>[
        g.id,
        g
    ]));
TASK_STATUS_OPTIONS.map((status)=>({
        value: status.code,
        label: status.display
    }));
TASK_PRIORITY_OPTIONS.map((priority)=>({
        value: priority.code,
        label: priority.display
    }));
TASK_INTENT_OPTIONS.map((intent)=>({
        value: intent.code,
        label: intent.display
    }));

/**
 * Select the billing ladder for the month per D13 + D14 + D18.
 * @agentic @benchmark/core
 */ const selectLadder = (input)=>{
    if (!input.hasEligibleAssessment) return NO_ASSESSMENT;
    // Concern 3: 99492 is billable at most once per episode. Once a prior month
    // billed it, force the subsequent (99493-based) ladder so it can never
    // recur — independent of the initial-month recompute.
    if (input.block99492) return SUBSEQUENT_WITH_ASSESSMENT;
    // D13 cascade: a G2214 initial month locks every later month into the
    // subsequent-month ladder (never 99492).
    if (input.episodeFirstBillWasG2214 && !input.isInitialMonth) {
        return SUBSEQUENT_WITH_ASSESSMENT;
    }
    return input.isInitialMonth ? INITIAL_WITH_ASSESSMENT : SUBSEQUENT_WITH_ASSESSMENT;
};
/**
 * Normalize raw input minutes: clamp negatives to 0, floor fractional
 * values. Every ladder rule + gap calculation operates on the
 * normalized value so the resolver stays internally consistent.
 *
 * Non-finite input (NaN / ±Infinity, e.g. from a corrupted upstream
 * `valueQuantity`) fails CLOSED to 0 → no bill. Without this guard,
 * `NaN` comparisons are all false, so `findRung` skips every bounded
 * rung and lands on the open-ended TOP rung — silently billing the
 * maximal code package for garbage input.
 * @agentic @benchmark/core
 */ const normalizeMinutes = (totalMinutes)=>Number.isFinite(totalMinutes) ? Math.max(0, Math.floor(totalMinutes)) : 0;
/**
 * Locate the rung whose minute window covers `minutes`. The caller is
 * responsible for passing a normalized integer (see `normalizeMinutes`).
 * @agentic @benchmark/core
 */ const findRung = (ladder, minutes)=>ladder.find((rung)=>{
        if (minutes < rung.minMinutes) return false;
        if (rung.maxMinutes === null) return true;
        return minutes <= rung.maxMinutes;
    });
/**
 * Compute the gap (in whole minutes) from `minutes` to the next rung's
 * lower bound. Returns `undefined` when there is no next rung (top of
 * ladder) or when the current rung's `codes` is already the highest
 * non-empty value.
 *
 * Per D15 the widget shows "minutes to next code" — so we always
 * compute relative to the next non-empty rung, even if the current
 * rung itself is non-empty (i.e. "minutes until the next more
 * lucrative package").
 *
 * The caller is responsible for passing a normalized integer; the
 * resolver normalizes once at entry so the gap stays a whole number.
 * @agentic @benchmark/core
 */ const computeMinutesToNext = (ladder, minutes, currentRung)=>{
    if (!currentRung) return undefined;
    const currentIndex = ladder.indexOf(currentRung);
    if (currentIndex === -1) return undefined;
    const nextRung = ladder[currentIndex + 1];
    if (!nextRung) return undefined;
    // Already inside the topmost open-ended rung → no next bound to chase.
    if (nextRung.maxMinutes === null && nextRung.codes.length === 0) {
        return undefined;
    }
    const gap = nextRung.minMinutes - minutes;
    return gap > 0 ? gap : undefined;
};
/**
 * Resolve the billing code package for a CoCM month. Pure function.
 *
 * Contract per the brief:
 *  - Returns empty `codes` when no rule fires (< 16 min, or < 16 min
 *    with no assessment).
 *  - Returns the package (codes + display) per D15.
 *  - Returns `minutesToNextCode` (whole minutes) when there is a
 *    higher-value rung reachable from the current `totalMinutes`.
 *
 * Defensive normalization (negative / fractional minutes are clamped
 * to `Math.max(0, Math.floor(...))`) is applied once at the top so
 * every downstream calculation operates on consistent integer minutes.
 * @agentic @benchmark/core
 */ const resolveBillingCode = (input)=>{
    const ladder = selectLadder(input);
    const minutes = normalizeMinutes(input.totalMinutes);
    const rung = findRung(ladder, minutes);
    if (!rung) {
        // Should never happen — every ladder covers [0, ∞).
        return {
            codes: [],
            packageDisplay: "",
            minutesToNextCode: undefined
        };
    }
    return {
        codes: [
            ...rung.codes
        ],
        packageDisplay: rung.packageDisplay,
        minutesToNextCode: computeMinutesToNext(ladder, minutes, rung)
    };
};

/**
 * Get display name for a patient.
 * When a preferred name (FHIR nickname) exists, formats as "Legal Name (Preferred Name)".
 */ const getPatientDisplayName = (patient)=>{
    const officialName = patient.name?.find((n)=>n.use === "official") || patient.name?.[0];
    if (!officialName) {
        return `Patient ${patient.id}`;
    }
    const parts = [];
    if (officialName.given?.length) {
        parts.push(...officialName.given);
    }
    if (officialName.family) {
        parts.push(officialName.family);
    }
    if (parts.length === 0) {
        return `Patient ${patient.id}`;
    }
    const legalName = parts.join(" ");
    const nickname = patient.name?.find((n)=>n.use === "nickname" && n !== officialName);
    const preferredName = nickname?.given?.[0];
    if (preferredName) {
        return `${legalName} (${preferredName})`;
    }
    return legalName;
};
/**
 * The patient's name for a persisted FHIR reference `display`, or `undefined`
 * when they have no recorded name — `getPatientDisplayName`'s `Patient {id}`
 * placeholder reads as a UI fallback and is worse than no display at all on a
 * stored reference.
 */ const getPatientReferenceDisplay = (patient)=>{
    const display = getPatientDisplayName(patient);
    return display === `Patient ${patient.id}` ? undefined : display;
};
/**
 * Get referring provider (PCP) reference from patient's generalPractitioner array
 *
 * Resolution order:
 * 1. Find entry with "referring-provider" extension (new format)
 * 2. Fallback to [0] ONLY if it has no extension (legacy format)
 * 3. Return null if [0] is explicitly tagged as something else (e.g., LCSW)
 */ const getPatientReferringProvider = (patient)=>{
    if (!patient.generalPractitioner?.length) {
        return null;
    }
    // First: Look for explicitly tagged referring provider
    const typedEntry = patient.generalPractitioner.find((ref)=>ref.extension?.some((ext)=>ext.url === GP_ROLE_TYPE_EXTENSION && ext.valueCode === GP_ROLE_TYPES.REFERRING_PROVIDER));
    if (typedEntry) {
        return typedEntry;
    }
    // Fallback: Check [0] for legacy data (no extension = assume PCP)
    const firstEntry = patient.generalPractitioner[0];
    const hasAnyExtension = firstEntry.extension?.some((ext)=>ext.url === GP_ROLE_TYPE_EXTENSION);
    // If [0] has no GP extension, treat it as legacy PCP data
    // If [0] HAS an extension but it's not REFERRING_PROVIDER, don't return it
    if (!hasAnyExtension) {
        return firstEntry;
    }
    return null;
};
/**
 * Returns true when a gp-role-type code represents the Benchmark primary advocate.
 * Supports both current and legacy value codes.
 */ const isBenchmarkPrimaryAdvocateRoleCode = (valueCode)=>valueCode === GP_ROLE_TYPES.BENCHMARK_PRIMARY_ADVOCATE || valueCode === GP_ROLE_TYPES.BENCHMARK_LCSW;
/**
 * Get Benchmark primary advocate reference from patient's generalPractitioner array.
 * Function name preserved for backward compatibility.
 */ const getPatientBenchmarkLCSW = (patient)=>{
    return patient.generalPractitioner?.find((ref)=>ref.extension?.some((ext)=>ext.url === GP_ROLE_TYPE_EXTENSION && isBenchmarkPrimaryAdvocateRoleCode(ext.valueCode))) ?? null;
};
/**
 * Extracts CoCM consent (verified flag + date) from the Patient resource's
 * `cocm-consent` complex extension — populated via the patient edit form's
 * "CoCM Consent Verified" checkbox and "CoCM Consent Date" field
 * (`ReferralInformationSection`). This is distinct from the CoCM CarePlan's
 * `cocm-consent-date` extension (captured automatically at enrollment); see
 * `resolveCoCMConsent` in `cocm-eligibility.service` for how the two sources
 * are reconciled for billing/eligibility display.
 * @agentic @benchmark/core
 */ const getCoCMConsentFromPatient = (patient)=>{
    const cocmExtension = patient.extension?.find((ext)=>ext.url === COCM_CONSENT_EXTENSION_URL);
    if (!cocmExtension?.extension) return undefined;
    const verified = Boolean(cocmExtension.extension.find((sub)=>sub.url === "verified")?.valueBoolean);
    const date = cocmExtension.extension.find((sub)=>sub.url === "date")?.valueDate;
    return {
        verified,
        date
    };
};

const encounterHasTag = (encounter, system, code)=>(encounter.meta?.tag ?? []).some((tag)=>tag.system === system && tag.code === code);
const isFinishedWithType = (encounter, typeCode)=>encounter.status === "finished" && encounterHasTag(encounter, VALUE_SET.ENCOUNTER_TYPE, typeCode);
/**
 * A finished Encounter carrying any v2 intake-type tag (any billing track).
 *
 * Matches by tag PRESENCE — true when *any* `ENCOUNTER_TYPE` tag is an intake
 * code — not by inspecting only the first type tag. Every other signal in this
 * file already matches by presence (`encounterHasTag`); an encounter can carry
 * more than one type tag, so a first-tag check could report `hasCompletedIntake`
 * = false for an encounter that `hasBillableCoCMIntake` = true reports as an
 * intake, wrongly blocking all CoCM billing. Read-side billing signals must
 * match by presence (see `reconcileIntakeChargeItem.action.ts`).
 */ const isFinishedIntake = (encounter)=>encounter.status === "finished" && (encounter.meta?.tag ?? []).some((tag)=>tag.system === VALUE_SET.ENCOUNTER_TYPE && isIntakeEncounterType(tag.code));
/**
 * Fetch both encounter-based billing signals in a single search.
 *
 * Mirrors the logic previously inlined in Connections'
 * `useEncounterEligibility` so the two never drift. A "billable 90791"
 * is a finished clinical-intake Encounter carrying both the
 * clinical-intake type tag and the `billable` tag.
 * @agentic @benchmark/core
 */ const getPatientEncounterBillingSignals = async (medplum, patientId)=>{
    // Every signal here requires a FINISHED encounter, and the intake (which
    // gates billing, concern 6) is typically the OLDEST finished encounter — so a
    // newest-N slice would miss it and wrongly block a long-tenured patient. Scope
    // the search to finished encounters and take a full page so an old intake is
    // never truncated away.
    const encounters = await searchResources(medplum, "Encounter", {
        subject: `Patient/${patientId}`,
        status: "finished",
        _sort: "-date",
        _count: "1000"
    });
    const list = (encounters.entry ?? []).map((e)=>e.resource).filter(Boolean);
    const hasBillable90791 = list.some((e)=>isFinishedWithType(e, CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE$1) && encounterHasTag(e, VALUE_SET.ENCOUNTER_BILLABLE, "billable"));
    const hasBillableCoCMIntake = list.some((e)=>isFinishedWithType(e, COCM_INTAKE_ENCOUNTER_TYPE_CODE$1));
    const hasCompletedIntake = list.some(isFinishedIntake);
    const cocmIntakeStarts = list.filter((e)=>isFinishedWithType(e, COCM_INTAKE_ENCOUNTER_TYPE_CODE$1)).map((e)=>e.period?.start).filter((s)=>Boolean(s)).sort();
    return {
        hasBillable90791,
        hasBillableCoCMIntake,
        hasCompletedIntake,
        cocmIntakeStarts
    };
};
/**
 * Whether the patient has a billed 90791 clinical intake.
 *
 * Thin wrapper over {@link getPatientEncounterBillingSignals} used by the
 * CoCM eligibility service to apply the D1 pre-enrollment carve-out.
 * @agentic @benchmark/core
 */ const hasBillable90791 = async (medplum, patientId)=>(await getPatientEncounterBillingSignals(medplum, patientId)).hasBillable90791;

const isInCategory$1 = (plan, category)=>(plan.category ?? []).some((c)=>(c.coding ?? []).some((coding)=>coding.code === category));
/**
 * Inclusive lower bound (ms) of the assessment-freshness window for a billing
 * month: the START of the calendar day that is 6 weeks (42 days) before the
 * month's last day. Day-granular so an assessment dated exactly 6 weeks before
 * month-end still counts — e.g. an assessment logged 5/19 is valid for June
 * (last day 6/30), because 5/19 is exactly six weeks before 6/30. The window is
 * anchored to the LAST DAY of the billing month, never the first day of the next
 * billing period. Instant math (monthEnd − 42d against an end-of-day monthEnd)
 * dropped the boundary day; day-flooring fixes it.
 * @agentic @benchmark/core
 */ const assessmentFreshnessLowerBoundMs = (monthEndMs)=>{
    const d = new Date(monthEndMs);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - COCM_ASSESSMENT_FRESHNESS_WEEKS * 7);
};
const isFreshAssessment = (observation, monthEnd)=>{
    const effective = observation.effectiveDateTime ?? observation.effectivePeriod?.start;
    if (!effective) return false;
    const effectiveMs = Date.parse(effective);
    const monthEndMs = Date.parse(monthEnd);
    if (!Number.isFinite(effectiveMs) || !Number.isFinite(monthEndMs)) {
        return false;
    }
    return effectiveMs >= assessmentFreshnessLowerBoundMs(monthEndMs) && effectiveMs <= monthEndMs;
};
const monthKey = (iso)=>{
    const ms = Date.parse(iso);
    if (!Number.isFinite(ms)) return undefined;
    const d = new Date(ms);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};
const observationEffectiveMonthKey = (observation)=>{
    const effective = observation.effectiveDateTime ?? observation.effectivePeriod?.start;
    return effective ? monthKey(effective) : undefined;
};
/**
 * Minutes carried by a care-time entry. Non-finite values (a corrupted
 * `valueQuantity.value`) are treated as 0 so one bad entry can't poison the
 * month's summed totals into `NaN` (which would propagate to attested
 * minutes and the resolver input).
 */ const observationMinutes = (observation)=>{
    const value = observation.valueQuantity?.value;
    return typeof value === "number" && Number.isFinite(value) ? value : 0;
};
const isFinalStatus = (observation)=>observation.status !== "entered-in-error" && observation.status !== "cancelled";
/**
 * A care-time entry that counts toward CoCM billing: a final-status entry
 * that is NOT explicitly flagged non-billable. Used by the billable-minutes
 * sum and episode classification so a manually excluded entry never bills.
 * @agentic @benchmark/core
 */ const isBillableEntry = (observation)=>isFinalStatus(observation) && !isCareTimeNonBillable(observation);
const consentDateFromExtension = (carePlan)=>{
    for (const ext of carePlan.extension ?? []){
        if (ext.url === COCM_CONSENT_DATE_EXTENSION_URL) {
            return ext.valueDate ?? ext.valueDateTime;
        }
    }
    return undefined;
};
/**
 * Reconcile CoCM consent from the Patient resource (edit-form "verified"
 * flag + optional date) and the CoCM CarePlan's enrollment-time consent
 * extension into a single consent fact. Every consumer (eligibility, the
 * "Consented" chip, the CoCM registry, and the end-of-month billing report)
 * routes through this helper so they never disagree.
 *
 * Precedence: `consentObtained` is true when EITHER source signals consent
 * (the patient `verified` flag, or a consent date on either side).
 * `consentDate` prefers the Patient-level date and falls back to the CarePlan
 * enrollment consent date. Because enrollment now writes the consent date back
 * to the Patient (`buildCoCMEnrollmentBundle`), both sources agree for every
 * new enrollment regardless of precedence; the fallback only matters for
 * legacy records where consent was captured on just one side.
 * @agentic @benchmark/core
 */ const resolveCoCMConsent = (patient, carePlan)=>{
    const fromPatient = patient ? getCoCMConsentFromPatient(patient) : undefined;
    const fromPlan = carePlan ? consentDateFromExtension(carePlan) : undefined;
    // Consent is obtained when any source signals it: the patient verified flag,
    // a patient-level consent date, or the CarePlan enrollment consent date. This
    // keeps `consentObtained` consistent with `consentDate` — a populated date is
    // never reported as "not obtained".
    const consentObtained = Boolean(fromPatient?.verified) || Boolean(fromPatient?.date) || Boolean(fromPlan);
    const consentDate = fromPatient?.date ?? fromPlan;
    return {
        consentDate,
        consentObtained
    };
};
/**
 * Sum care-time Observation minutes filtered by:
 *  - status not entered-in-error / cancelled
 *  - effective date is within the calendar month window
 *
 * The `basedOn` reference is intentionally NOT consulted — per
 * A1/A3 (2026-05-19 plan) every minute counts when the caller has
 * already confirmed the patient is in CoCM.
 * @agentic @benchmark/core
 */ const sumMinutesInMonth = (observations, monthStart, monthEnd)=>{
    const startMs = Date.parse(monthStart);
    const endMs = Date.parse(monthEnd);
    let total = 0;
    for (const obs of observations){
        if (!isFinalStatus(obs)) continue;
        const effective = obs.effectiveDateTime ?? obs.effectivePeriod?.start;
        if (!effective) continue;
        const effectiveMs = Date.parse(effective);
        if (!Number.isFinite(effectiveMs)) continue;
        if (effectiveMs < startMs || effectiveMs > endMs) continue;
        total += observationMinutes(obs);
    }
    return total;
};
/**
 * Sum care-time minutes for entries within the calendar month that are
 * explicitly flagged non-billable (and not soft-deleted). Mirrors
 * {@link sumMinutesInMonth}'s window/status filtering so the returned value is
 * the non-billable slice of `totalMinutes`.
 * @agentic @benchmark/core
 */ const sumNonBillableMinutesInMonth = (observations, monthStart, monthEnd)=>{
    const startMs = Date.parse(monthStart);
    const endMs = Date.parse(monthEnd);
    let total = 0;
    for (const obs of observations){
        if (!isFinalStatus(obs)) continue;
        if (!isCareTimeNonBillable(obs)) continue;
        const effective = obs.effectiveDateTime ?? obs.effectivePeriod?.start;
        if (!effective) continue;
        const effectiveMs = Date.parse(effective);
        if (!Number.isFinite(effectiveMs)) continue;
        if (effectiveMs < startMs || effectiveMs > endMs) continue;
        total += observationMinutes(obs);
    }
    return total;
};
/** Inclusive calendar-month bounds (UTC) for a `YYYY-MM` key. */ const monthBoundsForKey = (key)=>{
    const [yearStr, monthStr] = key.split("-");
    const year = Number(yearStr);
    const monthIndex = Number(monthStr) - 1;
    if (!Number.isFinite(year) || !Number.isFinite(monthIndex)) return undefined;
    const startMs = Date.UTC(year, monthIndex, 1, 0, 0, 0, 0);
    const endMs = Date.UTC(year, monthIndex + 1, 0, 23, 59, 59, 999);
    return {
        startMs,
        endMs
    };
};
const episodeStartMs = (plan)=>{
    // Fall back to `created` when `period.start` is missing (legacy plans),
    // matching dbt's COALESCE(period_start, created) so both engines agree on
    // episode overlap.
    const start = plan.period?.start ?? plan.created;
    return start ? Date.parse(start) : Number.NaN;
};
/** Episode end in ms, or +Infinity for an open (still-enrolled) episode. */ const episodeEndMs = (plan)=>plan.period?.end ? Date.parse(plan.period.end) : Number.POSITIVE_INFINITY;
/**
 * Back-date each CoCM episode's start to the CoCM-intake encounter that began
 * THAT episode, returning copies with the corrected `period.start` so every
 * window consumer that reads the start through {@link episodeStartMs} sees it.
 *
 * The CoCM CarePlan is often created a day or two AFTER the actual CoCM intake
 * (its `period.start` defaults to the documentation date), so intake-month
 * care-time logged before it would otherwise be mis-classified as pre-enrollment
 * and dropped. This is the mechanism dbt already uses (`cocm_intake_start`).
 *
 * Association rule (brief IB2): for each episode (processed in raw-start order),
 * pick the LATEST — i.e. closest — finished CoCM-intake `t` such that
 *   `monthStartMs <= t <= rawStart` and `t > previousEpisodeRawStart`,
 * and back-date only when `t < rawStart`. The three bounds make this robust:
 *  - `t <= rawStart` and "latest": associates to THIS enrollment's intake, not a
 *    stray mid-episode one — closest wins.
 *  - `t >= monthStartMs`: keeps back-dating WITHIN the current billing month
 *    (cross-month is deferred, IB3) and ignores suspiciously old prior-month
 *    intakes that must not widen this episode.
 *  - `t > previousEpisodeRawStart`: a re-enrollment with no new intake of its own
 *    is never back-dated to an earlier episode's intake (the wrong-CPT guard).
 * @agentic @benchmark/core
 */ const buildBillingEpisodes = (episodes, cocmIntakeStartsMs, monthStartMs, monthEndMs)=>{
    const intakes = cocmIntakeStartsMs.filter((t)=>Number.isFinite(t)).sort((a, b)=>a - b);
    const sorted = [
        ...episodes
    ].filter((p)=>Number.isFinite(episodeStartMs(p))).sort((a, b)=>episodeStartMs(a) - episodeStartMs(b));
    const effectiveStartByPlan = new Map();
    let prevRawStart = Number.NEGATIVE_INFINITY;
    for (const plan of sorted){
        const rawStart = episodeStartMs(plan);
        // Only an episode whose raw start is at/before the month end is eligible for
        // back-dating. A FUTURE CarePlan (raw start after this month) must never be
        // pulled into the current month by a current-month intake — that would leak
        // a later enrollment's window into billing/classification (and cross-month
        // is deferred, IB3). An already-started episode (raw start before the month)
        // finds no match anyway, since `t >= monthStartMs` forces `t > rawStart`.
        if (rawStart <= monthEndMs) {
            // Latest intake in [monthStart, rawStart] and after the previous episode.
            let match;
            for (const t of intakes){
                if (t >= monthStartMs && t <= rawStart && t > prevRawStart) match = t;
            }
            if (match !== undefined && match < rawStart) {
                effectiveStartByPlan.set(plan, match);
            }
        }
        prevRawStart = rawStart;
    }
    return episodes.map((plan)=>{
        const eff = effectiveStartByPlan.get(plan);
        if (eff === undefined) return plan;
        return {
            ...plan,
            period: {
                ...plan.period,
                start: new Date(eff).toISOString()
            }
        };
    });
};
/**
 * Whether a CoCM episode overlaps the month by PERIOD alone. Billing
 * eligibility is intentionally `CarePlan.status`-independent (concerns 4 & 5):
 * a revoked plan still bills minutes before its `period.end`, and a paused
 * (`on-hold`) plan — which never sets `period.end` — still bills every in-month
 * minute, including those logged during the pause. Do not add a status filter
 * here; excluding revoked/paused plans would silently drop billable minutes.
 * @agentic @benchmark/core
 */ const episodeOverlapsMonth = (plan, monthStartMs, monthEndMs)=>{
    const start = episodeStartMs(plan);
    const end = episodeEndMs(plan);
    if (!Number.isFinite(start)) return false;
    return start <= monthEndMs && end >= monthStartMs;
};
/**
 * Build the set of billable millisecond intervals within a month (D1/D2):
 *  - one interval per overlapping episode, clamped to the month
 *  - plus the pre-enrollment span [monthStart, earliestEnrollment) when the
 *    patient enrolled mid-month AND has no billed 90791 intake (D1 default);
 *    the span is dropped when a billed 90791 exists (D1 carve-out).
 * Post-de-enrollment minutes and inter-episode gaps are excluded by
 * construction (they fall in no interval).
 * @agentic @benchmark/core
 */ const computeBillableIntervalsForMonth = (overlappingEpisodes, monthStartMs, monthEndMs, patientHasBillable90791)=>{
    if (overlappingEpisodes.length === 0) return [];
    const intervals = [];
    let earliestStart = Number.POSITIVE_INFINITY;
    for (const plan of overlappingEpisodes){
        const start = Math.max(episodeStartMs(plan), monthStartMs);
        const end = Math.min(episodeEndMs(plan), monthEndMs);
        if (Number.isFinite(start) && start <= end) {
            intervals.push({
                startMs: start,
                endMs: end
            });
            earliestStart = Math.min(earliestStart, start);
        }
    }
    // Pre-enrollment span, only when enrolled mid-month and no billed 90791.
    if (!patientHasBillable90791 && Number.isFinite(earliestStart) && earliestStart > monthStartMs) {
        intervals.push({
            startMs: monthStartMs,
            endMs: earliestStart
        });
    }
    return intervals;
};
const isInAnyInterval = (ms, intervals)=>intervals.some(({ startMs, endMs })=>ms >= startMs && ms <= endMs);
/** Sum care-time minutes whose effective instant falls in a billable interval. */ const sumMinutesInIntervals = (observations, intervals)=>{
    if (intervals.length === 0) return 0;
    let total = 0;
    for (const obs of observations){
        if (!isBillableEntry(obs)) continue;
        const effective = obs.effectiveDateTime ?? obs.effectivePeriod?.start;
        if (!effective) continue;
        const ms = Date.parse(effective);
        if (!Number.isFinite(ms)) continue;
        if (isInAnyInterval(ms, intervals)) total += observationMinutes(obs);
    }
    return total;
};
/** Billable minutes for an arbitrary month under the V2 window rules. */ const billableMinutesForMonthKey = (key, observations, episodes, patientHasBillable90791)=>{
    const bounds = monthBoundsForKey(key);
    if (!bounds) return 0;
    const overlapping = episodes.filter((p)=>episodeOverlapsMonth(p, bounds.startMs, bounds.endMs));
    if (overlapping.length === 0) return 0;
    const intervals = computeBillableIntervalsForMonth(overlapping, bounds.startMs, bounds.endMs, patientHasBillable90791);
    return sumMinutesInIntervals(observations, intervals);
};
/**
 * Whether any of `assessments` is fresh relative to the last day of the
 * `YYYY-MM` month key — used to classify a historical month with ITS OWN
 * assessment freshness rather than the current month's (concern 3). A
 * 99492/99493 bill requires a fresh assessment in that month's window; a month
 * with none can only ever have billed G2214.
 * @agentic @benchmark/core
 */ const hasFreshAssessmentForMonthKey = (assessments, monthKey)=>{
    const bounds = monthBoundsForKey(monthKey);
    if (!bounds) return false;
    const monthEndIso = new Date(bounds.endMs).toISOString();
    return assessments.some((a)=>isFreshAssessment(a, monthEndIso));
};
/**
 * D14 + D13 fallback classification under the V2 window: walk each `YYYY-MM`
 * present in the episode's care-time, compute windowed billable minutes, and
 * find the first month that produces a code. Each candidate month is classified
 * with its OWN assessment freshness (concern 3), so the result is deterministic
 * and independent of the current month's assessment status.
 *
 * This is the FALLBACK: the authoritative initial/subsequent + cascade signal
 * comes from persisted billing history (`getPriorBilledCodeHistory`); this walk
 * only covers months with no persisted packet.
 * @agentic @benchmark/core
 */ const classifyEpisodeMonthsWindowed = (observations, episodes, patientHasBillable90791, currentMonth, assessments)=>{
    const monthKeys = new Set();
    for (const obs of observations){
        if (!isFinalStatus(obs)) continue;
        const key = observationEffectiveMonthKey(obs);
        if (key) monthKeys.add(key);
    }
    const sortedKeys = [
        ...monthKeys
    ].sort();
    let initialMonth;
    let initialMonthBilledG2214 = false;
    for (const key of sortedKeys){
        const minutes = billableMinutesForMonthKey(key, observations, episodes, patientHasBillable90791);
        const tentative = resolveBillingCode({
            totalMinutes: minutes,
            isInitialMonth: true,
            episodeFirstBillWasG2214: false,
            hasEligibleAssessment: hasFreshAssessmentForMonthKey(assessments, key)
        });
        if (tentative.codes.length > 0) {
            initialMonth = key;
            initialMonthBilledG2214 = tentative.codes[0] === "G2214";
            break;
        }
    }
    return {
        isInitialMonth: initialMonth === undefined || initialMonth === currentMonth,
        episodeFirstBillWasG2214: initialMonthBilledG2214
    };
};
/** Pick the episode that represents the month for id/period/consent display. */ const pickCurrentEpisode = (overlappingEpisodes)=>{
    if (overlappingEpisodes.length === 0) return undefined;
    const active = overlappingEpisodes.find((p)=>p.status === "active");
    if (active) return active;
    return [
        ...overlappingEpisodes
    ].sort((a, b)=>episodeStartMs(b) - episodeStartMs(a))[0];
};
/**
 * All of a patient's CoCM CarePlans, any status — used to detect episodes
 * that overlapped a month even if since de-enrolled, and to find the
 * earliest episode start for full-history enumeration (billing tab).
 * Filters to the CoCM category server-side so a patient with many non-CoCM
 * plans can't push an older CoCM episode off the page; the client-side guard
 * stays as defense.
 * @agentic @benchmark/core
 */ const fetchAllCoCMEpisodes = async (medplum, patientId)=>{
    const bundle = await searchResources(medplum, "CarePlan", {
        subject: `Patient/${patientId}`,
        category: "cocm",
        _count: "100",
        _sort: "-_lastUpdated"
    });
    return (bundle.entry ?? []).map((e)=>e.resource).filter(Boolean).filter((p)=>isInCategory$1(p, "cocm"));
};
/**
 * Earliest CoCM episode start across a patient's full CarePlan history
 * (`period.start`, falling back to `created` — mirrors `episodeStartMs`).
 * Returns `undefined` when the patient has no CoCM CarePlan at all. Used by
 * the billing tab to enumerate every month the patient was ever CoCM-active
 * in, not just months with a persisted billing-summary record.
 * @agentic @benchmark/core
 */ const getEarliestCoCMEpisodeStart = async (medplum, patientId)=>{
    const episodes = await fetchAllCoCMEpisodes(medplum, patientId);
    if (episodes.length === 0) return undefined;
    const earliestMs = Math.min(...episodes.map(episodeStartMs));
    return Number.isFinite(earliestMs) ? new Date(earliestMs).toISOString() : undefined;
};
/**
 * Compute the CoCM eligibility summary for a patient + month (D1/D2 windowed).
 *
 * Pure orchestration: fetches FHIR resources, then invokes the pure
 * resolver. No domain events emitted (this is a read path). Billable minutes
 * are windowed to overlapping CoCM episodes (pre-enrollment same-month minutes
 * included unless a billed 90791 exists; post-de-enrollment minutes excluded).
 * @agentic @benchmark/core
 */ const computeCoCMEligibility = async (medplum, input)=>{
    const { patientId, monthStart, monthEnd } = input;
    const monthStartMs = Date.parse(monthStart);
    const monthEndMs = Date.parse(monthEnd);
    // 1. All CoCM CarePlans for the patient (any status), to detect episodes
    // that overlapped the month even if since de-enrolled.
    const cocmEpisodes = await fetchAllCoCMEpisodes(medplum, patientId);
    const overlapping = cocmEpisodes.filter((p)=>episodeOverlapsMonth(p, monthStartMs, monthEndMs));
    const hasCoCMInMonth = overlapping.length > 0;
    // 2. Encounter-derived billing signals: the billed-90791 signal for the D1
    // pre-enrollment carve-out, the completed-intake gate (concern 6), AND the
    // CoCM-intake starts used to back-date episode starts below. One Encounter
    // search covers all three; skipped when not enrolled in the month.
    const encounterSignals = hasCoCMInMonth ? await getPatientEncounterBillingSignals(medplum, patientId) : {
        hasBillable90791: false,
        hasCompletedIntake: false,
        cocmIntakeStarts: []
    };
    const patientHasBillable90791 = encounterSignals.hasBillable90791;
    const hasCompletedIntake = encounterSignals.hasCompletedIntake;
    // Back-date each episode's start to the CoCM-intake encounter that began it,
    // WITHIN the current billing month (brief IB2/IB3). The CoCM CarePlan's
    // `period.start` often lags the actual intake by a day or two, so intake-month
    // care-time logged before it would otherwise be mis-classified as
    // pre-enrollment and dropped. The back-dated episodes drive the billable-minute
    // window and episode classification; `hasCoCMInMonth`, month-overlap, and the
    // display fields keep the raw CarePlan (cross-month back-dating is deferred, so
    // raw and back-dated episodes overlap the month identically).
    const cocmIntakeStartsMs = encounterSignals.cocmIntakeStarts.map((s)=>Date.parse(s)).filter((n)=>Number.isFinite(n));
    const billingEpisodes = buildBillingEpisodes(cocmEpisodes, cocmIntakeStartsMs, monthStartMs, monthEndMs);
    const billingOverlapping = billingEpisodes.filter((p)=>episodeOverlapsMonth(p, monthStartMs, monthEndMs));
    // 3. Care-time from the earliest episode start (for episode classification)
    // through month end.
    const earliestEpisodeStartMs = cocmEpisodes.reduce((min, p)=>{
        const s = episodeStartMs(p);
        return Number.isFinite(s) ? Math.min(min, s) : min;
    }, Number.POSITIVE_INFINITY);
    const fetchStart = Number.isFinite(earliestEpisodeStartMs) && earliestEpisodeStartMs < monthStartMs ? new Date(earliestEpisodeStartMs).toISOString() : monthStart;
    const allObservations = await searchCareTimeByPatient(medplum, patientId, {
        startDate: fetchStart,
        endDate: monthEnd,
        count: 1000
    });
    // 4. Eligible assessments. Fetch back to the earliest episode month's window
    // (not just the current month's) so historical months can be classified with
    // THEIR OWN freshness (concern 3). The current-month freshness lower bound is
    // day-granular so the exact-6-weeks boundary day is included (concern 2).
    const sixWeeksMs = COCM_ASSESSMENT_FRESHNESS_WEEKS * 7 * 24 * 60 * 60 * 1000;
    const currentMonthLowerBoundMs = assessmentFreshnessLowerBoundMs(monthEndMs);
    const historyLowerBoundMs = (Number.isFinite(earliestEpisodeStartMs) ? earliestEpisodeStartMs : monthStartMs) - sixWeeksMs;
    const assessmentSearchStart = new Date(Math.min(currentMonthLowerBoundMs, historyLowerBoundMs)).toISOString();
    const assessmentBundle = await searchResources(medplum, "Observation", {
        patient: patientId,
        code: COCM_ELIGIBLE_ASSESSMENT_CODES_TOKEN,
        status: "final",
        date: `ge${assessmentSearchStart}`,
        _sort: "-date",
        _count: "100"
    });
    // Full eligible-assessment set over the fetched span (date-desc). Per-month
    // freshness is applied by the classifier; the current month uses its own.
    const allAssessments = (assessmentBundle.entry ?? []).map((e)=>e.resource).filter(Boolean);
    const currentMonthAssessments = allAssessments.filter((a)=>isFreshAssessment(a, monthEnd));
    const hasEligibleAssessment = currentMonthAssessments.length > 0;
    const latestAssessmentDate = currentMonthAssessments[0]?.effectiveDateTime ?? currentMonthAssessments[0]?.effectivePeriod?.start;
    // 5. Total minutes — all care-time in the calendar month (unchanged meaning).
    const totalMinutes = sumMinutesInMonth(allObservations, monthStart, monthEnd);
    // 5b. Non-billable slice of the calendar month (explicit exclusions).
    const nonBillableMinutes = sumNonBillableMinutesInMonth(allObservations, monthStart, monthEnd);
    // 6. Billable minutes — windowed sum, only when enrolled during the month.
    const billableMinutes = hasCoCMInMonth ? sumMinutesInIntervals(allObservations, computeBillableIntervalsForMonth(billingOverlapping, monthStartMs, monthEndMs, patientHasBillable90791)) : 0;
    // 7. Episode classification (D13/D14). Authoritative prior-billing history
    // (persisted ChargeItems) wins; the live recompute is the fallback for months
    // with no persisted packet (concerns 1 & 3).
    const currentMonthKey = monthKey(monthStart) ?? "";
    const priorFacts = hasCoCMInMonth ? await getPriorBilledCodeHistory(medplum, patientId, currentMonthKey) : {
        anyCodeBilledBefore: false,
        billed99492Before: false,
        earliestBilledWasG2214: false
    };
    const fallback = hasCoCMInMonth ? classifyEpisodeMonthsWindowed(allObservations, billingEpisodes, patientHasBillable90791, currentMonthKey, allAssessments) : {
        isInitialMonth: true,
        episodeFirstBillWasG2214: false
    };
    // Any authoritatively-billed prior month means this is not the initial month.
    const isInitialMonth = !priorFacts.anyCodeBilledBefore && fallback.isInitialMonth;
    // Cascade flag: prefer the authoritative earliest-billed-month signal.
    const episodeFirstBillWasG2214 = priorFacts.anyCodeBilledBefore ? priorFacts.earliestBilledWasG2214 : fallback.episodeFirstBillWasG2214;
    // 8. Pure resolver. `block99492` (authoritative) guarantees 99492 never
    // recurs, independent of the initial-month recompute.
    const resolved = resolveBillingCode({
        totalMinutes: billableMinutes,
        isInitialMonth,
        episodeFirstBillWasG2214,
        hasEligibleAssessment,
        block99492: priorFacts.billed99492Before
    });
    // 9. Intake gate (concern 6): no completed intake ⇒ no billable codes,
    // regardless of minutes. Minutes stay surfaced so the UI can explain why.
    const billingResult = hasCoCMInMonth && !hasCompletedIntake ? {
        codes: [],
        packageDisplay: "",
        minutesToNextCode: undefined
    } : resolved;
    // Display uses the raw overlapping plans so the surfaced enrollment period is
    // the CarePlan's own (back-dating is billing-only, within-month).
    const currentEpisode = pickCurrentEpisode(overlapping);
    const { consentDate, consentObtained } = resolveCoCMConsent(input.patient, currentEpisode);
    return {
        totalMinutes,
        billableMinutes,
        nonBillableMinutes,
        billingResult,
        hasCoCMCarePlan: hasCoCMInMonth,
        cocmCarePlanId: currentEpisode?.id,
        cocmPeriodStart: currentEpisode?.period?.start,
        cocmPeriodEnd: currentEpisode?.period?.end,
        consentDate,
        consentObtained,
        hasEligibleAssessment,
        latestAssessmentDate,
        isInitialMonth,
        episodeFirstBillWasG2214,
        hasCompletedIntake
    };
};

/** Inclusive UTC month bounds for a `YYYY-MM` key. */ const monthBounds = (billingMonth)=>{
    const [year, month] = billingMonth.split("-").map(Number);
    return {
        start: new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0)).toISOString(),
        end: new Date(Date.UTC(year, month, 0, 23, 59, 59, 999)).toISOString()
    };
};
/**
 * Compute the authoritative, versioned billing-code packet for a patient-month.
 *
 * Always uses the V2 billing window (the authoritative computation, per the
 * close-month CLI) rather than the flag-gated display path — the packet is the
 * source of truth, not an on-read estimate.
 * @agentic @benchmark/core
 */ const computeCoCMBillingPacket = async (medplum, input)=>{
    const { patientId, billingMonth } = input;
    const { start, end } = monthBounds(billingMonth);
    const summary = await computeCoCMEligibility(medplum, {
        patientId,
        monthStart: start,
        monthEnd: end,
        patient: input.patient
    });
    return buildBillingCodePacket({
        patientId,
        billingMonth,
        summary,
        attribution: input.attribution,
        generatedAt: input.generatedAt
    });
};
/**
 * Best-effort performer attribution for a PractitionerRole: the Practitioner's
 * display name (so persisted references carry a `display`, per our reference
 * best practice) and their license/credential (D22 — metadata only; never gates
 * code selection). Resolves the performer PractitionerRole → Practitioner in one
 * read pair and returns both. Fields are `undefined` when not recorded; the
 * fuller credential capture is the D26 fast-follow.
 * @agentic @benchmark/core
 */ const getPractitionerRoleAttribution = async (medplum, practitionerRoleId)=>{
    const role = await medplum.readResource("PractitionerRole", practitionerRoleId);
    const practitionerRef = role.practitioner?.reference;
    const practitionerId = practitionerRef?.split("/")[1];
    if (!practitionerId) {
        // Fall back to any display carried on the role's practitioner reference.
        return {
            display: role.practitioner?.display
        };
    }
    const practitioner = await medplum.readResource("Practitioner", practitionerId);
    const qualification = practitioner.qualification?.[0]?.code;
    return {
        display: getPractitionerDisplayName(practitioner) || role.practitioner?.display,
        license: qualification?.text ?? qualification?.coding?.find((c)=>c.display)?.display ?? qualification?.coding?.[0]?.code
    };
};
/**
 * Find the persisted attested billing packet (`Composition`) for a
 * patient-month, if one exists. Used by the billing tab to show a locked
 * month's frozen packet.
 * @agentic @benchmark/core
 */ const findCoCMBillingPacket = async (medplum, patientId, billingMonth)=>{
    const bundle = await searchResources(medplum, "Composition", {
        identifier: buildCoCMBillingPacketIdentifierToken(patientId, billingMonth),
        _count: "1"
    });
    return bundle.entry?.[0]?.resource ?? undefined;
};
/** Page size for the reconciliation ChargeItem scan. */ const CHARGE_ITEM_PAGE_SIZE = 100;
/** Hard cap on pages so a broken/looping server response can't spin forever. */ const CHARGE_ITEM_MAX_PAGES = 100;
/** The COCM identifier value carried by a ChargeItem, if any. */ const cocmChargeItemIdentifierValue = (chargeItem)=>chargeItem.identifier?.find((i)=>i.system === COCM_CHARGE_ITEM_IDENTIFIER_SYSTEM)?.value;
/**
 * Find every persisted CoCM `ChargeItem` line-item for a patient-month. Used by
 * the submit/reopen reconciliation to prune stale line-items (a re-submit with
 * fewer codes, or a reopen) so `SUM`-based downstream billing can never
 * double-count an orphaned occurrence. Scoped by the shared
 * `{patientId}|{YYYY-MM}|` identifier prefix.
 * @agentic @benchmark/core
 */ const findCoCMChargeItemsForMonth = async (medplum, patientId, billingMonth)=>{
    const prefix = cocmChargeItemMonthPrefix(patientId, billingMonth);
    const matches = [];
    for(let page = 0; page < CHARGE_ITEM_MAX_PAGES; page++){
        const bundle = await searchResources(medplum, "ChargeItem", {
            subject: `Patient/${patientId}`,
            _count: String(CHARGE_ITEM_PAGE_SIZE),
            _offset: String(page * CHARGE_ITEM_PAGE_SIZE),
            // Stable ordering so offset paging can't skip or repeat a row.
            _sort: "_id"
        });
        const items = (bundle.entry ?? []).map((e)=>e.resource).filter(Boolean);
        for (const ci of items){
            const value = cocmChargeItemIdentifierValue(ci);
            if (value !== undefined && value.startsWith(prefix)) matches.push(ci);
        }
        if (items.length < CHARGE_ITEM_PAGE_SIZE) break;
    }
    return matches;
};

const bundleToObservations = (bundle)=>(bundle.entry ?? []).map((e)=>e.resource).filter(Boolean);
/**
 * Find the single billing summary for a patient + month, if one exists.
 * @agentic @benchmark/core
 */ const findCoCMBillingSummary = async (medplum, patientId, billingMonth)=>{
    const bundle = await searchResources(medplum, "Observation", {
        identifier: buildCoCMBillingSummaryIdentifierToken(patientId, billingMonth),
        _count: "1"
    });
    return bundleToObservations(bundle)[0];
};
/**
 * Pick the display that belongs to `performerRoleId` from the names already at
 * hand. A display is only ever valid for the role it was resolved for, so a
 * candidate for a different role is discarded rather than reused — otherwise a
 * month whose performer changes hands would label the new role with the
 * previous care manager's name.
 * @agentic @benchmark/core
 */ const performerDisplayForRole = (performerRoleId, candidates)=>performerRoleId ? candidates.find((c)=>c.roleId === performerRoleId && c.display)?.display : undefined;
/**
 * Resolve the display names the summary Observation's `subject` and
 * `performer` references should carry (reference best practice) — reading only
 * what the caller couldn't supply, in parallel. Best-effort: a missing or
 * unreadable resource yields `undefined` rather than failing the write.
 *
 * Costs one `Patient` read plus, for the performer, the `PractitionerRole` →
 * `Practitioner` read pair — so an entirely unknown attribution is three reads.
 * Callers that already hold the names (the billing modal) pay none.
 * @agentic @benchmark/core
 */ const resolveCoCMBillingSummaryAttribution = async (medplum, input)=>{
    const [patientDisplay, performerDisplay] = await Promise.all([
        input.patientDisplay ?? readResource(medplum, "Patient", input.patientId).then(getPatientReferenceDisplay).catch(()=>undefined),
        input.performerDisplay ?? (input.performerRoleId ? getPractitionerRoleAttribution(medplum, input.performerRoleId).then((a)=>a.display).catch(()=>undefined) : undefined)
    ]);
    return {
        patientDisplay,
        performerDisplay
    };
};
/**
 * List all persisted billing summaries for a patient (billing tab), newest
 * first.
 * @agentic @benchmark/core
 */ const searchCoCMBillingSummariesByPatient = async (medplum, patientId)=>{
    const bundle = await searchResources(medplum, "Observation", {
        patient: patientId,
        code: COCM_BILLING_SUMMARY_CODE_TOKEN,
        _count: "100",
        _sort: "-date"
    });
    return bundleToObservations(bundle);
};

/**
 * Single source of truth for the 6-week eligible-assessment freshness
 * window, expressed in milliseconds. Both the registry FHIR search
 * (`fetchPatientLatestAssessments`) and the client-side trend summary
 * filter (`summarizeAssessmentTrend`) read from here so the two can
 * never drift to different unit conversions.
 * @agentic @benchmark/core
 */ const ASSESSMENT_FRESHNESS_MS = COCM_ASSESSMENT_FRESHNESS_WEEKS * 7 * 24 * 60 * 60 * 1000;
const isActiveStatus = (status)=>status !== "entered-in-error" && status !== "cancelled";
const observationEffective = (observation)=>observation.effectiveDateTime ?? observation.effectivePeriod?.start;
const observationValue = (observation)=>{
    const fromQuantity = observation.valueQuantity?.value;
    if (typeof fromQuantity === "number" && Number.isFinite(fromQuantity)) {
        return fromQuantity;
    }
    const fromInteger = observation.valueInteger;
    if (typeof fromInteger === "number" && Number.isFinite(fromInteger)) {
        return fromInteger;
    }
    return undefined;
};
/**
 * Compare a latest score against an optional prior score. Used both
 * by `summarizeAssessmentTrend` and as a public helper for tests /
 * future callers that already have the two values in hand.
 * @agentic @benchmark/core
 */ const computeAssessmentDirection = (latest, prior)=>{
    if (prior === undefined) return "none";
    if (latest > prior) return "up";
    if (latest < prior) return "down";
    return "stable";
};
/**
 * Summarize an unordered Observation list into a `{ value, date,
 * direction }` snapshot. Observations are filtered to the 6-week
 * freshness window before `monthEnd`, sorted newest-first, and the
 * top two drive the direction indicator. Returns `undefined` when
 * no fresh observation exists in the window.
 * @agentic @benchmark/core
 */ const summarizeAssessmentTrend = (observations, monthEnd)=>{
    const monthEndMs = Date.parse(monthEnd);
    if (!Number.isFinite(monthEndMs)) return undefined;
    const freshnessStartMs = monthEndMs - ASSESSMENT_FRESHNESS_MS;
    const fresh = [];
    for (const obs of observations){
        if (!isActiveStatus(obs.status)) continue;
        const effective = observationEffective(obs);
        if (!effective) continue;
        const timestampMs = Date.parse(effective);
        if (!Number.isFinite(timestampMs)) continue;
        if (timestampMs < freshnessStartMs || timestampMs > monthEndMs) continue;
        const value = observationValue(obs);
        if (value === undefined) continue;
        fresh.push({
            value,
            date: effective,
            timestampMs
        });
    }
    if (fresh.length === 0) return undefined;
    fresh.sort((a, b)=>b.timestampMs - a.timestampMs);
    const latest = fresh[0];
    const prior = fresh[1];
    return {
        value: latest.value,
        date: latest.date,
        direction: computeAssessmentDirection(latest.value, prior?.value),
        priorValue: prior?.value
    };
};

/**
 * Excludes cleared/terminal notices. A service-approval notice is cleared by
 * setting `Task.status = "cancelled"` (D8); acute-risk notices are always
 * `ready`, so this is a no-op for them and keeps a cleared notice out of both
 * the drawer list and the unread count.
 */ const NOTIFICATION_NOT_CANCELLED = {
    "status:not": "cancelled"
};
/**
 * Search params matching all live clinical notification Tasks (any notification
 * code, excluding cleared notices). Spread into a `Task` search alongside
 * owner/subject filters.
 * @agentic @benchmark/core
 */ function buildNotificationTaskSearchParams() {
    return {
        code: NOTIFICATION_TASK_CODE_TOKEN,
        ...NOTIFICATION_NOT_CANCELLED
    };
}
/**
 * Search params matching unread, live clinical notification Tasks (notification
 * code + `business-status = unread`, excluding cleared notices). Used for the
 * unread count/list.
 * @agentic @benchmark/core
 */ function buildUnreadNotificationTaskSearchParams() {
    return {
        code: NOTIFICATION_TASK_CODE_TOKEN,
        "business-status": NOTIFICATION_UNREAD_STATUS_TOKEN,
        ...NOTIFICATION_NOT_CANCELLED
    };
}
/**
 * Search params that exclude clinical notification Tasks from an operational
 * `Task` search. Spread into every broad operational Task query so
 * notifications never leak into task lists/counts/hooks.
 * @agentic @benchmark/core
 */ function buildOperationalTaskExclusionParams() {
    return {
        "code:not": NOTIFICATION_TASK_CODE_TOKEN
    };
}

/** Page size when sweeping a patient's open tasks. */ const OPEN_TASK_PAGE_COUNT = "200";
/**
 * Formats a Date to FHIR date search precision (YYYY-MM-DD).
 * @agentic @benchmark/core
 */ function formatTaskDueDateSearchDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}
/**
 * Builds today/tomorrow bounds for due-date range searches.
 * @agentic @benchmark/core
 */ function getTaskDueDateSearchRange(referenceDate) {
    const today = formatTaskDueDateSearchDate(referenceDate);
    const tomorrowDate = new Date(referenceDate);
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    const tomorrow = formatTaskDueDateSearchDate(tomorrowDate);
    return {
        today,
        tomorrow
    };
}
/**
 * Returns server-side Task due-date filters for a segmented control selection.
 * @agentic @benchmark/core
 */ function buildTaskDueDateFilterSearchParams(filter, range) {
    if (filter === "overdue") {
        return {
            "due-date": `lt${range.today}`
        };
    }
    if (filter === "upcoming") {
        return {
            "due-date": `ge${range.tomorrow}`
        };
    }
    if (filter === "noDate") {
        return {
            "due-date:missing": "true"
        };
    }
    return {};
}
/**
 * Returns a due-date range filter that isolates tasks due today.
 * @agentic @benchmark/core
 */ function buildDueTodayTaskSearchParams(range) {
    return [
        [
            "due-date",
            `ge${range.today}`
        ],
        [
            "due-date",
            `lt${range.tomorrow}`
        ]
    ];
}
/**
 * Extracts a YYYY-MM-DD day token from a Task due-date string.
 * @agentic @benchmark/core
 */ function getTaskDueDateDayToken(dueDate) {
    const datePrefixMatch = dueDate.match(/^(\d{4}-\d{2}-\d{2})/);
    if (datePrefixMatch?.[1]) {
        return datePrefixMatch[1];
    }
    const parsed = new Date(dueDate);
    if (Number.isNaN(parsed.getTime())) {
        return undefined;
    }
    return formatTaskDueDateSearchDate(parsed);
}
/**
 * Classifies a due date relative to a reference day using calendar-day semantics.
 * @agentic @benchmark/core
 */ function getTaskDueDateRelativePosition(dueDate, referenceDate) {
    const dueDayToken = getTaskDueDateDayToken(dueDate);
    if (!dueDayToken) {
        return "invalid";
    }
    const { today, tomorrow } = getTaskDueDateSearchRange(referenceDate);
    if (dueDayToken < today) {
        return "overdue";
    }
    if (dueDayToken === today) {
        return "today";
    }
    if (dueDayToken === tomorrow) {
        return "tomorrow";
    }
    return "future";
}
/**
 * Returns true if the patient has any historical
 * `complete-cadenced-followup` Task in any status. Used to enforce the
 * once-per-patient-lifetime cadenced-followup rule (D8 + D10) when creating a
 * new Triage CarePlan: if any prior cohort exists (even revoked or
 * cancelled), the new plan must omit the activity.
 *
 * Searches by both `subject` and `for` to mirror existing patient-scoped Task
 * lookups (Medplum tooling has historically populated either parameter).
 * Filters by `code` with the activity-code system to avoid matching custom
 * tasks that share the literal code value.
 *
 * Fail-soft: callers should treat a thrown error as "false" (include the
 * activity) per the brief's documented failure behavior.
 * @agentic @benchmark/core
 */ async function hasGeneratedCadencedFollowups(medplum, patientId) {
    const patientRef = `Patient/${patientId}`;
    const codeParam = `${CARE_PLAN_ACTIVITY_SYSTEM$1}|complete-cadenced-followup`;
    // `subject` is the FHIR R4 Task search parameter whose expression is
    // `Task.for` (Medplum rejects a literal `for` param as unknown).
    const bySubject = await medplum.searchResources("Task", {
        subject: patientRef,
        code: codeParam,
        _count: "1"
    }).catch(()=>[]);
    return bySubject.length > 0;
}
/**
 * Finds open tasks for a patient (by the `subject` search param = `Task.for`).
 *
 * A failed search **throws**. Every mutating caller acts on the result — pause
 * and unpause shift due dates, CoCM completion and cancellation close tasks —
 * so an empty list on error is not a degraded answer, it is a wrong one: the
 * pause would commit with no due date moved and the tasks would come due while
 * the patient is paused. Read-only callers that prefer to degrade should catch.
 * @agentic @benchmark/core
 */ async function findOpenTasksForPatient(medplum, patientId) {
    const patientRef = `Patient/${patientId}`;
    const openStatuses = "requested,received,accepted,ready,in-progress,on-hold";
    const excludeNotifications = buildOperationalTaskExclusionParams();
    // Pages exhaustively. `searchResources` returns a single page, so a patient
    // with more open tasks than the server's default page size would have had
    // the remainder silently ignored by every caller — pause would leave their
    // due dates unshifted, and cancellation would leave them open.
    const tasks = [];
    for await (const page of medplum.searchResourcePages("Task", {
        subject: patientRef,
        status: openStatuses,
        ...excludeNotifications,
        _count: OPEN_TASK_PAGE_COUNT
    })){
        tasks.push(...page);
    }
    // Deduplicate by ID
    const taskMap = new Map();
    tasks.forEach((task)=>{
        if (task?.id) {
            taskMap.set(task.id, task);
        }
    });
    return Array.from(taskMap.values());
}

const PHQ9_CODE = OBSERVATION_LOINC_CODES.PHQ9_TOTAL_SCORE;
const PHQ9A_CODE = OBSERVATION_LOINC_CODES.PHQ9A_TOTAL_SCORE;
const GAD7_CODE = OBSERVATION_LOINC_CODES.GAD7_TOTAL_SCORE;
const PSC17_CODE = OBSERVATION_LOINC_CODES.PSC_17_TOTAL_SCORE;
/**
 * Score codes tracked by the registry Trends column. PHQ-9, PHQ-9A, and GAD-7
 * are LOINC-coded; PSC-17 uses a Benchmark organizational code system (no
 * official LOINC), so partitioning and the search token below are system-aware.
 */ const TREND_ASSESSMENT_CODES = [
    PHQ9_CODE,
    PHQ9A_CODE,
    GAD7_CODE,
    PSC17_CODE
];
/**
 * Resolve the code system for a tracked score the same way
 * `buildObservationFromScore` stamps it (`getObservationCoding(code)?.system`),
 * falling back to LOINC. Keeps the search token in lockstep with what
 * questionnaire completion actually writes for both LOINC and organizational
 * instruments.
 */ const codeSystem = (code)=>getObservationCoding(code)?.system ?? "http://loinc.org";
/**
 * `system|code` token → canonical tracked score code. Partitioning keys on the
 * exact system+code pair (not the code alone) so a multi-coding Observation is
 * never mis-bucketed by an earlier coding that happens to reuse a tracked code
 * under a different system.
 */ const TREND_ASSESSMENT_CODE_BY_TOKEN = new Map(TREND_ASSESSMENT_CODES.map((code)=>[
        `${codeSystem(code)}|${code}`,
        code
    ]));
/**
 * System-aware comma-OR token expression used on the `code` search parameter to
 * pull PHQ-9 + PHQ-9A + GAD-7 + PSC-17 in a single Observation query. FHIR uses
 * comma for OR within one search parameter and `system|code` for the
 * system-qualified token, so mixed LOINC + organizational systems are supported.
 * @agentic @benchmark/core
 */ const ASSESSMENT_CODES_TOKEN = TREND_ASSESSMENT_CODES.map((code)=>`${codeSystem(code)}|${code}`).join(",");
const isInCategory = (plan, category)=>(plan.category ?? []).some((c)=>(c.coding ?? []).some((coding)=>coding.code === category));
// Partition by the exact tracked `system|code` pair (mirroring the search
// token), returning the canonical tracked code. Matching the full pair rather
// than the code alone keeps a multi-coding Observation correctly bucketed even
// if an earlier coding reuses a tracked code under a different system, and stays
// correct for PSC-17's organizational system.
const observationCode = (observation)=>{
    for (const coding of observation.code?.coding ?? []){
        if (coding.system === undefined || coding.code === undefined) continue;
        const matched = TREND_ASSESSMENT_CODE_BY_TOKEN.get(`${coding.system}|${coding.code}`);
        if (matched !== undefined) return matched;
    }
    return undefined;
};
/**
 * Fetch the active CarePlan labels for one patient. Used by the
 * registry to (a) filter to patients with an active CoCM plan and
 * (b) decide whether to suffix `"+ Triage"` on the care-plan badge.
 * @agentic @benchmark/core
 */ const fetchPatientActivePlanLabels = async (medplum, patientId)=>{
    const bundle = await medplum.search("CarePlan", {
        subject: `Patient/${patientId}`,
        status: "active",
        _count: "20"
    });
    const plans = (bundle.entry ?? []).map((e)=>e.resource).filter(Boolean);
    const cocm = plans.find((p)=>isInCategory(p, "cocm"));
    const triage = plans.find((p)=>isInCategory(p, "triage"));
    return {
        hasCoCM: Boolean(cocm),
        cocmCarePlanId: cocm?.id,
        hasTriage: Boolean(triage)
    };
};
/**
 * Fetch the freshest PHQ-9, PHQ-9A, GAD-7, and PSC-17 Observations for
 * one patient inside the 6-week window before `monthEnd`. Returns the
 * latest value + direction per assessment type (computed by the pure
 * helper).
 * @agentic @benchmark/core
 */ const fetchPatientLatestAssessments = async (medplum, patientId, monthEnd)=>{
    const monthEndMs = Date.parse(monthEnd);
    const freshnessStart = Number.isFinite(monthEndMs) ? new Date(monthEndMs - ASSESSMENT_FRESHNESS_MS).toISOString() : new Date(Date.now() - ASSESSMENT_FRESHNESS_MS).toISOString();
    // One `-date` query shared across all four instruments (PHQ-9, PHQ-9A,
    // GAD-7, PSC-17). We only need the two newest per type, but a single low
    // `_count` could let a high-frequency instrument crowd the newest rows and
    // starve a less-frequent one. The `date >= freshnessStart` bound already caps
    // the set to the 6-week window; `_count: 100` gives generous headroom over any
    // realistic in-window cadence (even weekly × 4 instruments) so no type is
    // truncated before its two newest scores are seen.
    const bundle = await medplum.search("Observation", {
        patient: `Patient/${patientId}`,
        code: ASSESSMENT_CODES_TOKEN,
        status: "final",
        _sort: "-date",
        _count: "100",
        date: `ge${freshnessStart}`
    });
    const observations = (bundle.entry ?? []).map((e)=>e.resource).filter(Boolean);
    const phq9Observations = observations.filter((o)=>observationCode(o) === PHQ9_CODE);
    const phq9aObservations = observations.filter((o)=>observationCode(o) === PHQ9A_CODE);
    const gad7Observations = observations.filter((o)=>observationCode(o) === GAD7_CODE);
    const psc17Observations = observations.filter((o)=>observationCode(o) === PSC17_CODE);
    return {
        phq9: summarizeAssessmentTrend(phq9Observations, monthEnd),
        phq9a: summarizeAssessmentTrend(phq9aObservations, monthEnd),
        gad7: summarizeAssessmentTrend(gad7Observations, monthEnd),
        psc17: summarizeAssessmentTrend(psc17Observations, monthEnd)
    };
};
const taskDueDate = (task)=>task.restriction?.period?.end ?? task.restriction?.period?.start;
const taskCodeAndDisplay = (task)=>{
    const primary = task.code?.coding?.[0];
    if (primary?.code && (primary.display ?? task.code?.text)) {
        return {
            code: primary.code,
            display: primary.display ?? task.code?.text ?? primary.code
        };
    }
    const description = task.description ?? task.code?.text;
    if (description) {
        return {
            code: "ad-hoc",
            display: description
        };
    }
    return undefined;
};
/**
 * Resolve the soonest-due open Task for one patient. Used by the
 * registry's "Next Activity" column. Sort uses the Task's
 * `restriction.period.end` (treat undefined as +∞ so dated tasks
 * win the tie-break).
 * @agentic @benchmark/core
 */ const fetchPatientNextOpenTask = async (medplum, patientId)=>{
    // Registry display degrades to a blank cell rather than failing the row; the
    // lookup itself throws because its mutating callers must not proceed blind.
    const tasks = await findOpenTasksForPatient(medplum, patientId).catch(()=>[]);
    if (tasks.length === 0) return undefined;
    const sorted = [
        ...tasks
    ].sort((a, b)=>{
        const aDue = taskDueDate(a);
        const bDue = taskDueDate(b);
        const aMs = aDue ? Date.parse(aDue) : Number.POSITIVE_INFINITY;
        const bMs = bDue ? Date.parse(bDue) : Number.POSITIVE_INFINITY;
        return aMs - bMs;
    });
    for (const task of sorted){
        const codeDisplay = taskCodeAndDisplay(task);
        if (codeDisplay) {
            return {
                ...codeDisplay,
                dueDate: taskDueDate(task)
            };
        }
    }
    return undefined;
};

/** Creates a Communication resource. @agentic @benchmark/core */ async function createCommunication(medplum, communication) {
    return await createResource(medplum, communication);
}
/** Updates an existing Communication resource. @agentic @benchmark/core */ async function updateCommunication(medplum, communication) {
    return await updateResource(medplum, communication);
}
/** Reads a Communication resource by ID. @agentic @benchmark/core */ async function getCommunication(medplum, id) {
    return await readResource(medplum, "Communication", id);
}
/** Searches for Communication resources by query. @agentic @benchmark/core */ async function searchCommunications(medplum, query) {
    const bundle = await searchResources(medplum, "Communication", query);
    return bundle.entry?.map((e)=>e.resource).filter(Boolean) || [];
}
/** Deletes a Communication resource. @agentic @benchmark/core */ async function deleteCommunication(medplum, id) {
    return await deleteResource(medplum, "Communication", id);
}

/** 24h window in milliseconds. @agentic @benchmark/core */ const DEFAULT_LOOKBACK_MS = 24 * 60 * 60 * 1000;
/** Type-safe window access for mock composition globals. @agentic @benchmark/core */ const getDevWindow = ()=>typeof window !== "undefined" ? window : undefined;
/** Gets mock composition - checks window globals first, then falls back to default. @agentic @benchmark/core */ const getMockComposition = (patientRef)=>{
    const devWindow = getDevWindow();
    // Check for full custom composition
    if (devWindow?.__MOCK_COMPOSITION__) {
        console.info("[Composition] 🧪 Using custom window.__MOCK_COMPOSITION__");
        return {
            ...devWindow.__MOCK_COMPOSITION__,
            subject: {
                reference: patientRef
            }
        };
    }
    // Check for custom sections only
    if (devWindow?.__MOCK_COMPOSITION_SECTIONS__) {
        console.info("[Composition] 🧪 Using custom window.__MOCK_COMPOSITION_SECTIONS__");
        return {
            ...createDefaultMockComposition(patientRef),
            section: devWindow.__MOCK_COMPOSITION_SECTIONS__
        };
    }
    // Use default mock
    return createDefaultMockComposition(patientRef);
};
/** Default mock composition for UI testing. Contains realistic clinical content with FHIR-compliant XHTML. @agentic @benchmark/core */ const createDefaultMockComposition = (patientRef)=>({
        resourceType: "Composition",
        id: "mock-composition-dev",
        status: "preliminary",
        type: {
            coding: [
                {
                    system: "http://loinc.org",
                    code: "34748-4",
                    display: "Telephone encounter note"
                }
            ]
        },
        identifier: {
            system: "http://benchmarkhealth.com/fhir/identifier/call-composition",
            value: "mock-call-900100200"
        },
        date: new Date().toISOString(),
        title: "AI-Generated Draft Note (Mock)",
        author: [
            {
                reference: "PractitionerRole/mock-practitioner-role",
                display: "Jane Doe, LCSW"
            },
            {
                display: "AI Clinical Note Generator (openai/gpt-4.1-mini)"
            }
        ],
        event: [
            {
                period: {
                    start: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
                    end: new Date(Date.now() - 7 * 60 * 1000).toISOString()
                }
            }
        ],
        subject: {
            reference: patientRef
        },
        section: [
            {
                title: "General Note",
                text: {
                    status: "generated",
                    div: '<div xmlns="http://www.w3.org/1999/xhtml"><p>Patient called seeking outpatient therapy due to ongoing sleep difficulties, general malaise, and decreased motivation related to recent job stress and transitions. Patient prefers in-person therapy with a female or gender non-conforming therapist and is not currently seeking medication changes.</p></div>'
                }
            },
            {
                title: "Chief Complaint",
                text: {
                    status: "generated",
                    div: '<div xmlns="http://www.w3.org/1999/xhtml"><p>Trouble sleeping and general malaise with low motivation, especially related to job stress.</p></div>'
                }
            },
            {
                title: "HPI Current Symptoms",
                text: {
                    status: "generated",
                    div: '<div xmlns="http://www.w3.org/1999/xhtml"><p>Patient reports chronic sleep difficulties with variable duration of 5 to 12 hours, often compensating by oversleeping on days off. Malaise and lack of motivation have been fluctuating but notably worse over the past four months, coinciding with job changes and stress. Patient describes difficulty finding enjoyment in usual activities and moderate functional impairment at work and home.</p></div>'
                }
            },
            {
                title: "Mental Health History",
                text: {
                    status: "generated",
                    div: '<div xmlns="http://www.w3.org/1999/xhtml"><p>Brief course of therapy as a teenager for family-related issues. No other prior therapy or psychiatric medication trials except current use of Trazodone 50 mg as needed for sleep. No history of hospitalizations or higher levels of care reported.</p></div>'
                }
            },
            {
                title: "Mental Status Examination",
                text: {
                    status: "generated",
                    div: "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p><strong>Speech:</strong> Normal rate, volume, fluency, rhythm, and quantity.</p><p><strong>Mood/Affect:</strong> Reports mood as 'several days' feeling down or depressed; affect appropriate and congruent.</p><p><strong>Thought Process/Content:</strong> Organized thought process; denies delusions or obsessions; denies suicidal or homicidal ideation.</p><p><strong>Cognition:</strong> Alert and oriented; no reported memory or concentration difficulties.</p><p><strong>Insight/Judgment:</strong> Good insight into symptoms and judgment intact.</p></div>"
                }
            },
            {
                title: "Trauma History",
                text: {
                    status: "generated",
                    div: '<div xmlns="http://www.w3.org/1999/xhtml"><p>Patient reports past family-related trauma, including parental separation and brief therapy as a teenager. Sleep difficulties may be related to past trauma, with some avoidance behaviors noted.</p></div>'
                }
            },
            {
                title: "Substance Use History",
                text: {
                    status: "generated",
                    div: '<div xmlns="http://www.w3.org/1999/xhtml"><p>Denies current or past substance use.</p></div>'
                }
            },
            {
                title: "Medical History",
                text: {
                    status: "generated",
                    div: '<div xmlns="http://www.w3.org/1999/xhtml"><p>Vitamin D deficiency currently treated with supplementation; no other chronic medical conditions or pain reported.</p></div>'
                }
            },
            {
                title: "Current Medications",
                text: {
                    status: "generated",
                    div: '<div xmlns="http://www.w3.org/1999/xhtml"><p><strong>Trazodone</strong> - 50 mg - as needed (Indication: sleep; Prescriber: not specified)</p><p><strong>Vitamin D</strong> - not specified - daily (Indication: vitamin D deficiency; Prescriber: PCP)</p></div>'
                }
            },
            {
                title: "Family History",
                text: {
                    status: "generated",
                    div: '<div xmlns="http://www.w3.org/1999/xhtml"><p>Parent diagnosed with anxiety; no other known mental health or substance use disorders in family.</p></div>'
                }
            },
            {
                title: "Social History",
                text: {
                    status: "generated",
                    div: '<div xmlns="http://www.w3.org/1999/xhtml"><p>Lives with partner, reports good relationship and social support from friends. Employed full-time with recent job changes and promotion. No reported social media use or other community involvement discussed.</p></div>'
                }
            },
            {
                title: "Current Outpatient Treaters",
                text: {
                    status: "generated",
                    div: '<div xmlns="http://www.w3.org/1999/xhtml"><p>No current outpatient therapists or psychiatrists reported; primary care provider managing vitamin D deficiency and prescribing Trazodone.</p></div>'
                }
            },
            {
                title: "Assessment and Plan",
                text: {
                    status: "generated",
                    div: '<div xmlns="http://www.w3.org/1999/xhtml"><p><strong>Summary:</strong> Patient presents with chronic sleep disturbances and moderate depressive symptoms linked to recent job stress and family history of anxiety. No current suicidal ideation or substance use. Prefers in-person therapy with a female or gender non-conforming therapist. Patient is not currently seeking medication changes.</p><p><strong>Reported Conditions:</strong></p><ul><li>sleep difficulties</li><li>depressive symptoms</li><li>vitamin D deficiency</li></ul><p><strong>Suspected Conditions:</strong></p><ul><li>possible depression</li><li>possible adjustment disorder</li></ul><p><strong>Plan:</strong></p><ul><li>Refer to outpatient therapist matching patient preferences (in-person, female or gender non-conforming).</li><li>Provide education about therapy options including CBT for insomnia if indicated.</li><li>Continue current Trazodone as needed for sleep.</li><li>Monitor vitamin D supplementation effects with PCP.</li><li>Follow up via text to coordinate therapy scheduling and provide support.</li><li>No immediate safety concerns; no suicide or self-harm risk identified.</li></ul><p><strong>Shared Decision Making:</strong> Patient prefers in-person therapy, flexible Monday through Thursday, no weekends. Prefers female or gender non-conforming therapist. Patient agrees to follow-up via text and understands next steps.</p></div>'
                }
            },
            {
                title: "Questionnaires",
                text: {
                    status: "generated",
                    div: '<div xmlns="http://www.w3.org/1999/xhtml"><p><strong>PHQ-9:</strong> Score: 9 - Mild depression (based on reported symptoms: several days of little interest, feeling down, poor appetite more than half the days, tired every day, somewhat difficult functioning)</p><p><strong>GAD-7:</strong> Score: 3 - Minimal anxiety (several days feeling nervous/anxious, no uncontrollable worry)</p><p><strong>C-SSRS:</strong> Score: 0 - No suicidal ideation or self-harm thoughts reported.</p><p><strong>TAPS:</strong> Score: 0 - No substance use reported.</p></div>'
                }
            },
            {
                title: "Social Determinants of Health",
                text: {
                    status: "generated",
                    div: '<div xmlns="http://www.w3.org/1999/xhtml"><p><strong>Transportation Strain:</strong> no (patient reports easy travel within the area, willing to travel up to 30 minutes)</p></div>'
                }
            }
        ],
        extension: [
            {
                url: "https://benchmark.health/fhir/StructureDefinition/llm-model",
                valueString: "gpt-4.1-mini"
            },
            {
                url: "https://benchmark.health/fhir/StructureDefinition/llm-provider",
                valueString: "openai"
            },
            {
                url: "https://benchmark.health/fhir/StructureDefinition/prompt-version",
                valueString: "3.0"
            },
            {
                url: "https://benchmark.health/fhir/StructureDefinition/call-duration-ms",
                valueInteger: 1397349
            },
            {
                url: "https://benchmark.health/fhir/StructureDefinition/dialpad-call-id",
                valueString: "900100200"
            },
            {
                url: "https://benchmark.health/fhir/StructureDefinition/ai-confidence",
                valueString: "4"
            },
            {
                url: "https://benchmark.health/fhir/StructureDefinition/call-direction",
                valueString: "inbound"
            },
            {
                url: "https://benchmark.health/fhir/StructureDefinition/transcript-source",
                valueString: "openai"
            },
            {
                url: "https://benchmark.health/fhir/StructureDefinition/transcription-model",
                valueString: "gpt-4o-transcribe-diarize"
            }
        ]
    });
/** Development-only mock transcript for testing the transcript viewer. @agentic @benchmark/core */ const MOCK_TRANSCRIPT = [
    "Speaker 1 (Clinician): Hi, thanks for calling Benchmark Health. How are you doing today?",
    "Speaker 2 (Patient): Hi, I'm okay. I've been having some trouble sleeping and just feeling kind of down lately.",
    "Speaker 1: I'm sorry to hear that. Can you tell me a little more about what's been going on?",
    "Speaker 2: Yeah, so for the past few months I've been really stressed with work. I got a new position and it's been a lot. I'm not sleeping well — some nights I only get four or five hours, and then on my days off I sleep like twelve hours to make up for it.",
    "Speaker 1: That sounds really difficult. How has this been affecting your day-to-day life?",
    "Speaker 2: I just don't have motivation to do anything. Things I used to enjoy, like going out with friends or cooking, I just can't get myself to do them. I feel tired all the time.",
    "Speaker 1: Have you experienced anything like this before?",
    "Speaker 2: I went to therapy briefly as a teenager when my parents were separating, but nothing since then.",
    "Speaker 1: Are you currently taking any medications?",
    "Speaker 2: I take Trazodone 50mg as needed for sleep, and vitamin D supplements.",
    "Speaker 1: And how would you describe your mood over the past two weeks?",
    "Speaker 2: I'd say several days I've been feeling down or depressed. Not every day, but more often than not.",
    "Speaker 1: Have you had any thoughts of hurting yourself or not wanting to be alive?",
    "Speaker 2: No, nothing like that.",
    "Speaker 1: I'm glad to hear that. Based on what you're telling me, I think connecting you with an outpatient therapist would be really helpful. Do you have any preferences for that?",
    "Speaker 2: I'd prefer someone in person, and ideally a female or gender non-conforming therapist. I'm flexible Monday through Thursday but no weekends.",
    "Speaker 1: That's very helpful. We'll work on finding someone who matches those preferences. I'll follow up with you by text to coordinate scheduling. Does that sound good?",
    "Speaker 2: That sounds great, thank you."
].join("\n\n");
/**
 * Classify whether a composition identifier belongs to the TypeScript pipeline.
 *
 * Recognises three formats:
 *  - CALL_COMPOSITION system (always TypeScript — new format)
 *  - PIPELINE_COMPOSITION with numeric callId (10+ digits)
 *  - PIPELINE_COMPOSITION with transcript-{hash} (legacy TS format)
 * @agentic @benchmark/core
 */ const classifyAsTypescriptPipeline = (identifier)=>{
    if (!identifier) return false;
    if (identifier.system === IDENTIFIER_SYSTEM.CALL_COMPOSITION) return true;
    const value = identifier.value ?? "";
    return /^\d{10,}$/.test(value) || value.startsWith("transcript-");
};
/**
 * Search for the most recent pipeline-generated Composition for a patient.
 *
 * Supports both legacy and new pipeline compositions (when enabled).
 * Returns the earliest (first created) composition to prefer the initial
 * clinical documentation over later updates.
 *
 * Note: We intentionally do NOT filter by author because:
 * 1. The pipeline stores PractitionerRole as author, not Practitioner
 * 2. A different clinician than the call participant might document the encounter
 * 3. The other filters (patient, date, status, identifier) are sufficient
 *
 * @param medplum - MedplumClient instance
 * @param patient - Patient resource
 * @param _practitioner - Practitioner resource (unused, kept for API compatibility)
 * @param options - Search options
 * @returns Composition, optional Encounter, and pipeline source
 * @agentic @benchmark/core
 */ async function searchLatestIntakeComposition(medplum, patient, _practitioner, options = {}) {
    const lookbackMs = options.lookbackMs ?? DEFAULT_LOOKBACK_MS;
    const since = new Date(Date.now() - lookbackMs).toISOString();
    const enableNewPipeline = options.enableNewPipeline ?? true;
    const mockScribe = options.mockScribe ?? false;
    const logPipelineSource = options.logPipelineSource ?? false;
    // Core filter - getReferenceString can return undefined if resource has no ID
    const subjectRef = getReferenceString(patient);
    if (!subjectRef) {
        console.warn("[Composition] Invalid patient - missing reference");
        return undefined;
    }
    if (mockScribe) {
        console.info("[Composition] 🧪 Mock mode enabled - returning test composition");
        await new Promise((resolve)=>setTimeout(resolve, 500));
        return {
            composition: getMockComposition(subjectRef),
            encounter: undefined,
            pipelineSource: "typescript"
        };
    }
    try {
        // Build search parameters
        // Note: author filter removed - pipeline uses PractitionerRole but callers pass Practitioner
        const searchParams = {
            subject: subjectRef,
            date: `ge${since}`,
            status: "preliminary",
            _sort: "date",
            _count: "25",
            _include: "Composition:encounter"
        };
        if (enableNewPipeline) {
            searchParams.identifier = [
                `${IDENTIFIER_SYSTEM.PIPELINE_COMPOSITION}|`,
                `${IDENTIFIER_SYSTEM.CALL_COMPOSITION}|`
            ].join(",");
        }
        const searchBundle = await medplum.search("Composition", searchParams);
        if (!searchBundle.entry || searchBundle.entry.length === 0) return undefined;
        const compositions = searchBundle.entry.filter((entry)=>entry.resource?.resourceType === "Composition").map((entry)=>{
            const comp = entry.resource;
            const identifiers = Array.isArray(comp.identifier) ? comp.identifier : comp.identifier ? [
                comp.identifier
            ] : [];
            const identifier = identifiers.find((id)=>id.system === IDENTIFIER_SYSTEM.PIPELINE_COMPOSITION || id.system === IDENTIFIER_SYSTEM.CALL_COMPOSITION);
            const isTypescript = classifyAsTypescriptPipeline(identifier);
            return {
                composition: comp,
                pipelineSource: isTypescript ? "typescript" : "legacy"
            };
        }).filter((item)=>{
            if (!enableNewPipeline && item.pipelineSource === "typescript") {
                return false;
            }
            return true;
        });
        if (compositions.length === 0) return undefined;
        const { composition, pipelineSource } = compositions[0];
        if (logPipelineSource) {
            console.info(`[Composition] Using ${pipelineSource} pipeline composition`);
        }
        // Extract encounter if present
        let encounter;
        // FHIR R4: Composition.encounter is a single Reference (0..1), not an array
        const encounterRef = composition.encounter?.reference;
        if (encounterRef) {
            // The _include parameter should have fetched the Encounter
            const encounterEntry = searchBundle.entry.find((entry)=>{
                const res = entry.resource;
                return res?.resourceType === "Encounter" && `Encounter/${res.id}` === encounterRef;
            });
            if (encounterEntry?.resource) {
                encounter = encounterEntry.resource;
            } else {
                // Fallback: manually fetch the encounter if _include didn't work
                try {
                    encounter = await medplum.readReference({
                        reference: encounterRef
                    });
                } catch (err) {
                    console.warn("Could not fetch included encounter:", err);
                }
            }
        }
        return {
            composition,
            encounter,
            pipelineSource
        };
    } catch (err) {
        console.error("Error searching for compositions:", err);
        return undefined;
    }
}
/** Extracts encounter from search bundle for a given composition. @agentic @benchmark/core */ const extractEncounterFromBundle = async (composition, searchBundle, medplum)=>{
    // FHIR R4: Composition.encounter is a single Reference (0..1), not an array
    const encounterRef = composition.encounter?.reference;
    if (!encounterRef) return undefined;
    // The _include parameter should have fetched the Encounter
    const encounterEntry = searchBundle.entry?.find((entry)=>{
        const res = entry.resource;
        return res?.resourceType === "Encounter" && `Encounter/${res.id}` === encounterRef;
    });
    if (encounterEntry?.resource) {
        return encounterEntry.resource;
    }
    // Fallback: manually fetch the encounter if _include didn't work
    try {
        return await medplum.readReference({
            reference: encounterRef
        });
    } catch (err) {
        console.warn("Could not fetch included encounter:", err);
        return undefined;
    }
};
/**
 * Search for ALL pipeline-generated Compositions for a patient within the lookback window.
 *
 * Unlike searchLatestIntakeComposition which returns only the first match,
 * this function returns all matching compositions to allow user selection.
 *
 * @param medplum - MedplumClient instance
 * @param patient - Patient resource
 * @param _practitioner - Practitioner resource (unused, kept for API compatibility)
 * @param options - Search options
 * @returns Array of compositions with their encounters and pipeline sources
 * @agentic @benchmark/core
 */ const DIALPAD_CALL_ID_EXT_URL = "https://benchmark.health/fhir/StructureDefinition/dialpad-call-id";
/**
 * Deduplicates compositions by Dialpad call ID, keeping the most recent per call.
 * Compositions without a call ID extension are always kept.
 * @agentic @benchmark/core
 */ const deduplicateByCallId = (results)=>{
    const byCallId = new Map();
    const noCallId = [];
    for (const result of results){
        const callIdExt = result.composition.extension?.find((e)=>e.url === DIALPAD_CALL_ID_EXT_URL);
        const callId = callIdExt?.valueString;
        if (!callId) {
            noCallId.push(result);
            continue;
        }
        const existing = byCallId.get(callId);
        if (!existing || (result.composition.date ?? "") > (existing.composition.date ?? "")) {
            byCallId.set(callId, result);
        }
    }
    return [
        ...byCallId.values(),
        ...noCallId
    ];
};
async function searchAllIntakeCompositions(medplum, patient, _practitioner, options = {}) {
    const lookbackMs = options.lookbackMs ?? DEFAULT_LOOKBACK_MS;
    const since = new Date(Date.now() - lookbackMs).toISOString();
    const enableNewPipeline = options.enableNewPipeline ?? true;
    const mockScribe = options.mockScribe ?? false;
    const logPipelineSource = options.logPipelineSource ?? false;
    const subjectRef = getReferenceString(patient);
    if (!subjectRef) {
        console.warn("[Composition] Invalid patient - missing reference");
        return [];
    }
    if (mockScribe) {
        console.info("[Composition] 🧪 Mock mode enabled - returning test compositions");
        await new Promise((resolve)=>setTimeout(resolve, 500));
        return [
            {
                composition: getMockComposition(subjectRef),
                encounter: undefined,
                pipelineSource: "typescript"
            }
        ];
    }
    try {
        const searchParams = {
            subject: subjectRef,
            date: `ge${since}`,
            status: "preliminary",
            _sort: "date",
            _count: "25",
            _include: "Composition:encounter"
        };
        if (enableNewPipeline) {
            searchParams.identifier = [
                `${IDENTIFIER_SYSTEM.PIPELINE_COMPOSITION}|`,
                `${IDENTIFIER_SYSTEM.CALL_COMPOSITION}|`
            ].join(",");
        }
        const searchBundle = await medplum.search("Composition", searchParams);
        if (!searchBundle.entry || searchBundle.entry.length === 0) {
            return [];
        }
        // Filter and map compositions
        const compositionEntries = searchBundle.entry.filter((entry)=>entry.resource?.resourceType === "Composition").map((entry)=>{
            const comp = entry.resource;
            const identifiers = Array.isArray(comp.identifier) ? comp.identifier : comp.identifier ? [
                comp.identifier
            ] : [];
            const identifier = identifiers.find((id)=>id.system === IDENTIFIER_SYSTEM.PIPELINE_COMPOSITION || id.system === IDENTIFIER_SYSTEM.CALL_COMPOSITION);
            const isTypescript = classifyAsTypescriptPipeline(identifier);
            return {
                composition: comp,
                pipelineSource: isTypescript ? "typescript" : "legacy"
            };
        }).filter((item)=>{
            if (!enableNewPipeline && item.pipelineSource === "typescript") {
                return false;
            }
            return true;
        });
        // Resolve encounters for each composition
        const results = await Promise.all(compositionEntries.map(async ({ composition, pipelineSource })=>{
            const encounter = await extractEncounterFromBundle(composition, searchBundle, medplum);
            return {
                composition,
                encounter,
                pipelineSource
            };
        }));
        const deduplicated = deduplicateByCallId(results);
        if (logPipelineSource) {
            console.info(`[Composition] Found ${results.length} compositions (${deduplicated.length} unique calls) from pipelines:`, deduplicated.map((r)=>r.pipelineSource));
        }
        return deduplicated;
    } catch (err) {
        console.error("Error searching for compositions:", err);
        return [];
    }
}
/** Marks a Composition as used by updating its status to "amended". @agentic @benchmark/core */ function markCompositionUsed(composition) {
    // Use FHIR-supported status to indicate composition has been consumed
    return {
        ...composition,
        status: "amended"
    };
}
/**
 * Fetches the raw call transcript for a Composition by looking up the
 * associated Communication resource via the Dialpad call ID.
 * @agentic @benchmark/core
 */ async function fetchTranscriptForComposition(medplum, dialpadCallId, options = {}) {
    if (!dialpadCallId) return undefined;
    if (options.mockScribe) {
        console.info("[Composition] 🧪 Mock mode - returning test transcript for call", dialpadCallId);
        await new Promise((resolve)=>setTimeout(resolve, 300));
        return MOCK_TRANSCRIPT;
    }
    try {
        const communication = await medplum.searchOne("Communication", {
            identifier: `${IDENTIFIER_SYSTEM.DIALPAD_CALL_ID}|${dialpadCallId}`
        });
        if (!communication) return undefined;
        const transcript = communication.payload?.[0]?.contentString;
        return transcript && transcript.trim().length > 0 ? transcript : undefined;
    } catch (err) {
        console.error("[Composition] Error fetching transcript for call", dialpadCallId, err);
        return undefined;
    }
}

/** Creates a Condition resource. @agentic @benchmark/core */ async function createCondition(medplum, condition) {
    return await createResource(medplum, condition);
}
/** Updates an existing Condition resource. @agentic @benchmark/core */ async function updateCondition(medplum, condition) {
    return await updateResource(medplum, condition);
}
/** Searches for Condition resources for a patient. @agentic @benchmark/core */ async function searchConditions(medplum, patientId, options) {
    const query = {
        subject: `Patient/${patientId}`
    };
    // Filter by clinical status
    if (options?.clinicalStatus) {
        query["clinical-status"] = options.clinicalStatus;
    }
    // Filter by category with system
    if (options?.category) {
        query.category = `${CONDITION_CATEGORY_SYSTEM}|${options.category}`;
    }
    // Add sort parameter
    if (options?._sort) {
        query._sort = options._sort;
    }
    // Add count parameter
    if (options?._count) {
        query._count = options._count.toString();
    }
    const bundle = await searchResources(medplum, "Condition", query);
    return bundle.entry?.map((e)=>e.resource).filter(Boolean) || [];
}
/** Searches for active Problem List Items for a patient. @agentic @benchmark/core */ async function searchProblemListConditions(medplum, patientId) {
    return searchConditions(medplum, patientId, {
        category: "problem-list-item",
        clinicalStatus: "active",
        _sort: "-recorded-date"
    });
}

/** Creates a Coverage resource. @agentic @benchmark/core */ async function createCoverage(medplum, coverage) {
    return await createResource(medplum, coverage);
}
/** Updates an existing Coverage resource. @agentic @benchmark/core */ async function updateCoverage(medplum, coverage) {
    return await updateResource(medplum, coverage);
}
/** Reads a Coverage resource by ID. @agentic @benchmark/core */ async function getCoverage(medplum, id) {
    return await readResource(medplum, "Coverage", id);
}
/** Searches for Coverage resources by query. @agentic @benchmark/core */ async function searchCoverages(medplum, query) {
    const bundle = await searchResources(medplum, "Coverage", query);
    return bundle.entry?.map((e)=>e.resource).filter(Boolean) || [];
}
/** Deletes a Coverage resource. @agentic @benchmark/core */ async function deleteCoverage(medplum, id) {
    return await deleteResource(medplum, "Coverage", id);
}

/**
 * Builds the canonical `Location` resource for a supported coverage
 * area state. Pure helper; the resource is not persisted by this call.
 * @agentic @benchmark/core
 */ function buildCanonicalCoverageAreaLocation(stateCode) {
    return {
        resourceType: "Location",
        status: "active",
        mode: "instance",
        name: getCoverageAreaLocationName(stateCode),
        identifier: [
            {
                system: COVERAGE_AREA_STATE_IDENTIFIER_SYSTEM,
                value: stateCode
            }
        ],
        physicalType: {
            coding: [
                {
                    system: "http://terminology.hl7.org/CodeSystem/location-physical-type",
                    code: "jdn",
                    display: "Jurisdiction"
                }
            ]
        },
        address: {
            state: stateCode,
            country: "US"
        }
    };
}
/**
 * Builds a typed Reference<Location> with display text for a
 * canonical coverage-area Location.
 * @agentic @benchmark/core
 */ function getCoverageAreaLocationReference(location, stateCode) {
    return {
        reference: `Location/${location.id}`,
        display: location.name ?? getCoverageAreaLocationName(stateCode)
    };
}
/**
 * Resolves the canonical state-coverage `Location` for a single
 * state, creating it if it doesn't exist.
 * @agentic @benchmark/core
 */ async function ensureCoverageAreaLocation(medplum, stateCode) {
    const existing = await searchResources(medplum, "Location", {
        identifier: `${COVERAGE_AREA_STATE_IDENTIFIER_SYSTEM}|${stateCode}`,
        _count: "1"
    });
    const found = existing.entry?.[0]?.resource;
    if (found?.id) {
        return found;
    }
    const created = await createResource(medplum, buildCanonicalCoverageAreaLocation(stateCode));
    return created;
}
/**
 * Resolves canonical state-coverage `Location` resources for many
 * states in parallel. Returns a Map keyed by state code.
 * @agentic @benchmark/core
 */ async function ensureCoverageAreaLocations(medplum, stateCodes) {
    const unique = Array.from(new Set(stateCodes));
    const resolved = await Promise.all(unique.map(async (code)=>{
        const location = await ensureCoverageAreaLocation(medplum, code);
        return [
            code,
            location
        ];
    }));
    return new Map(resolved);
}
/**
 * Returns a copy of the form data with every coverageAreas[] entry's
 * locationId/locationDisplay populated. Resolves any missing canonical
 * Locations via ensureCoverageAreaLocations. No-op when the form has
 * no coverageAreas or every entry is already resolved.
 * @agentic @benchmark/core
 */ async function enrichCoverageAreasWithLocations(medplum, formData) {
    const coverageAreas = formData.coverageAreas;
    if (!coverageAreas || coverageAreas.length === 0) {
        return formData;
    }
    const missing = coverageAreas.filter((area)=>!area.locationId).map((area)=>area.state);
    if (missing.length === 0) {
        return formData;
    }
    const resolved = await ensureCoverageAreaLocations(medplum, missing);
    const enriched = coverageAreas.map((area)=>{
        if (area.locationId) {
            return area;
        }
        const location = resolved.get(area.state);
        if (!location?.id) {
            return area;
        }
        return {
            state: area.state,
            locationId: location.id,
            locationDisplay: location.name ?? getCoverageAreaLocationName(area.state)
        };
    });
    return {
        ...formData,
        coverageAreas: enriched
    };
}

/**
 * Find the EARLIEST finished intake encounter of any intake track (clinical or
 * CoCM). Server-`_tag`-filters by the intake type codes and sorts ascending so
 * an older intake behind many later encounters (or a CoCM-only intake) is never
 * missed. Returns a discriminated result so the caller can distinguish "no
 * intake on record" from "the lookup failed".
 * @agentic @benchmark/core
 */ async function findEarliestIntakeEncounter(medplum, patientId) {
    try {
        const tagQuery = INTAKE_ENCOUNTER_CODES.map((code)=>`${VALUE_SET.ENCOUNTER_TYPE}|${code}`).join(",");
        const encounters = await medplum.searchResources("Encounter", {
            subject: `Patient/${patientId}`,
            status: "finished",
            _tag: tagQuery,
            _sort: "date",
            _count: "1"
        });
        const encounter = encounters[0];
        return encounter ? {
            status: "found",
            encounter
        } : {
            status: "absent"
        };
    } catch (error) {
        console.error("Error finding earliest intake encounter:", error);
        return {
            status: "failed"
        };
    }
}
/** Active care-time statuses (excludes soft-deleted `entered-in-error`). */ const CARE_TIME_ACTIVE_STATUS_TOKEN = "final,amended,corrected,preliminary";
/**
 * Page size for encounter-related resource reads. These MUST be fully paginated:
 * a completed universal intake creates ~26 ClinicalImpressions (one per note
 * subsection), which exceeds the server's default page size (20) — so a single
 * unpaginated page silently drops the last-created subsections (living situation,
 * social supports, occupation, substance treatment history, chronic conditions,
 * other physical symptoms), and they fail to prefill on edit. 100/page keeps this
 * to a single request for any realistic encounter while the pager still drains
 * every page for pathological cases.
 * @agentic @benchmark/core
 */ const ENCOUNTER_RELATED_PAGE_COUNT = "100";
/**
 * Collects every page of a search (the Medplum SDK returns one page per call).
 * @agentic @benchmark/core
 */ async function collectAllPages(medplum, resourceType, query) {
    const results = [];
    for await (const page of medplum.searchResourcePages(resourceType, {
        ...query,
        _count: ENCOUNTER_RELATED_PAGE_COUNT
    })){
        results.push(...page);
    }
    return results;
}
/**
 * Loads all resources needed for encounter form data.
 *
 * On a related-resource read failure the default is to log and return empty
 * arrays (callers that only enrich existing state rely on this). Pass
 * `{ throwOnError: true }` when an empty result would be indistinguishable from a
 * genuinely empty encounter and must instead surface as a load error — e.g. the
 * intake edit hydrator, which would otherwise present a populated encounter as a
 * blank form.
 * @agentic @benchmark/core
 */ async function loadEncounterFormDataContext(medplum, encounter, patient, options = {}) {
    try {
        // Fetch all related resources in parallel, fully paginated (see
        // ENCOUNTER_RELATED_PAGE_COUNT: an intake's impression count exceeds the
        // default page size, so an unpaginated read drops note subsections).
        const encounterRef = `Encounter/${encounter.id}`;
        const [clinicalImpressions, questionnaireResponses, observations, careTimeObservations] = await Promise.all([
            collectAllPages(medplum, "ClinicalImpression", {
                encounter: encounterRef
            }),
            collectAllPages(medplum, "QuestionnaireResponse", {
                encounter: encounterRef
            }),
            collectAllPages(medplum, "Observation", {
                encounter: encounterRef,
                category: "survey"
            }),
            collectAllPages(medplum, "Observation", {
                encounter: encounterRef,
                category: CARE_TIME_CATEGORY_TOKEN,
                status: CARE_TIME_ACTIVE_STATUS_TOKEN
            })
        ]);
        return {
            encounter,
            patient,
            clinicalImpressions,
            questionnaireResponses,
            observations,
            careTimeObservations
        };
    } catch (error) {
        console.error("Error loading encounter form data context:", error);
        // Surface the failure to callers that must not mistake it for an empty
        // encounter (e.g. intake edit hydration shows a load error instead of a
        // blank form). Default callers keep the empty-arrays fallback.
        if (options.throwOnError) throw error;
        return {
            encounter,
            patient,
            clinicalImpressions: [],
            questionnaireResponses: [],
            observations: [],
            careTimeObservations: []
        };
    }
}
/** Finds an in-progress draft encounter for a patient matching one of the given encounter types. @agentic @benchmark/core */ async function findDraftEncounterForPatient(medplum, patientId, encounterTypes) {
    try {
        const encounters = await medplum.searchResources("Encounter", {
            subject: `Patient/${patientId}`,
            status: "in-progress",
            _sort: "-date",
            _count: "10"
        });
        return encounters.find((enc)=>(enc.meta?.tag || []).some((tag)=>tag.system === VALUE_SET.ENCOUNTER_TYPE && encounterTypes.includes(tag.code || "")));
    } catch (error) {
        console.error("Error finding draft encounter for patient:", error);
        return undefined;
    }
}
/** Finds the most recent Initial Clinical Intake encounter for a patient. @agentic @benchmark/core */ async function findMostRecentClinicalIntakeEncounter(medplum, patientId) {
    try {
        const encounters = await medplum.searchResources("Encounter", {
            subject: `Patient/${patientId}`,
            _sort: "-date",
            _count: ENCOUNTER_BATCH_SMALL_COUNT.toString()
        });
        // Find the most recent finished encounter with clinical-intake-interaction tag
        const clinicalIntake = encounters.find((encounter)=>{
            if (encounter.status !== "finished") return false;
            return (encounter.meta?.tag || []).some((tag)=>tag.system === VALUE_SET.ENCOUNTER_TYPE && tag.code === CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE$1);
        });
        return clinicalIntake;
    } catch (error) {
        console.error("Error finding most recent clinical intake encounter:", error);
        return undefined;
    }
}
/**
 * Finds the most recent finished intake encounter of ANY intake track
 * (Clinical, CoCM, Unbilled). Used by the unified follow-up form to mirror the
 * originating intake's billing track onto the follow-up code.
 * @agentic @benchmark/core
 */ async function findMostRecentIntakeEncounter(medplum, patientId) {
    try {
        // Filter server-side by the intake type tags so the lookup can't miss an
        // older intake behind a window of more recent non-intake encounters (a
        // member accrues many follow-ups after intake). `_tag` OR-matches the
        // three intake codes on the encounter-type system.
        const tagQuery = INTAKE_ENCOUNTER_CODES.map((code)=>`${VALUE_SET.ENCOUNTER_TYPE}|${code}`).join(",");
        const encounters = await medplum.searchResources("Encounter", {
            subject: `Patient/${patientId}`,
            status: "finished",
            _tag: tagQuery,
            _sort: "-date",
            _count: 1
        });
        return encounters[0];
    } catch (error) {
        console.error("Error finding most recent intake encounter:", error);
        return undefined;
    }
}
/**
 * Finds a recently finished intake encounter (any track) for the patient
 * within the last `withinHours` (default 24), excluding `excludeEncounterId`
 * (the encounter currently being edited). Powers the duplicate-intake
 * guardrail (FR-C4): documenting a second intake for the same patient within a
 * day is almost always accidental.
 * @agentic @benchmark/core
 */ async function findRecentDuplicateIntakeEncounter(medplum, patientId, options) {
    const withinHours = options?.withinHours ?? 24;
    try {
        const tagQuery = INTAKE_ENCOUNTER_CODES.map((code)=>`${VALUE_SET.ENCOUNTER_TYPE}|${code}`).join(",");
        const encounters = await medplum.searchResources("Encounter", {
            subject: `Patient/${patientId}`,
            status: "finished",
            _tag: tagQuery,
            _sort: "-date",
            _count: ENCOUNTER_BATCH_SMALL_COUNT.toString()
        });
        const cutoffMs = Date.now() - withinHours * 60 * 60 * 1000;
        return encounters.find((encounter)=>{
            if (encounter.id === options?.excludeEncounterId) return false;
            const when = encounter.period?.end ?? encounter.period?.start ?? encounter.meta?.lastUpdated;
            if (!when) return false;
            return new Date(when).getTime() >= cutoffMs;
        });
    } catch  {
        // Deliberately do not log the error object — a patient-scoped search error
        // can carry identifiers/clinical detail. The guardrail degrades to "no
        // duplicate found" on failure.
        console.error("Error finding recent duplicate intake encounter");
        return undefined;
    }
}

/** Creates an Encounter resource. @agentic @benchmark/core */ async function createEncounter(medplum, encounter) {
    return await createResource(medplum, encounter);
}
/** Updates an existing Encounter resource. @agentic @benchmark/core */ async function updateEncounter(medplum, encounter) {
    return await updateResource(medplum, encounter);
}
/** Reads an Encounter resource by ID. @agentic @benchmark/core */ async function getEncounter(medplum, id) {
    return await readResource(medplum, "Encounter", id);
}
/** Searches for Encounter resources by query. @agentic @benchmark/core */ async function searchEncounters(medplum, query) {
    const bundle = await searchResources(medplum, "Encounter", query);
    return bundle.entry?.map((e)=>e.resource).filter(Boolean) || [];
}
/** Deletes an Encounter resource. @agentic @benchmark/core */ async function deleteEncounter(medplum, id) {
    return await deleteResource(medplum, "Encounter", id);
}

/**
 * Returns true if flag represents patient pause (code "patient-paused").
 * @agentic @benchmark/core
 */ function isPauseFlag(flag) {
    const codings = flag.code?.coding || [];
    return codings.some((coding)=>coding.code === "patient-paused");
}
/**
 * Returns true if flag represents CoCM enrollment.
 * @agentic @benchmark/core
 */ function isCoCMFlag(flag) {
    const code = flag.code?.coding?.[0];
    return code?.system === FLAG_LOCAL_CODE_SYSTEM$1 && code?.code === "cocm-enrolled";
}
/**
 * Filters flags to CoCM enrollment flags only.
 * @agentic @benchmark/core
 */ function filterCoCMFlags(flags) {
    return flags.filter(isCoCMFlag);
}
/**
 * Returns true if flag represents minor (under 18) patient.
 * @agentic @benchmark/core
 */ function isMinorFlag(flag) {
    const code = flag.code?.coding?.[0];
    return code?.system === FLAG_LOCAL_CODE_SYSTEM$1 && code?.code === "patient-under-18";
}
/**
 * Filters flags to minor (under 18) flags only.
 * @agentic @benchmark/core
 */ function filterMinorFlags(flags) {
    return flags.filter(isMinorFlag);
}

/** Page size for project-wide Flag sweeps. */ const FLAG_SWEEP_PAGE_COUNT = "500";
/**
 * Fetches a patient's currently active pause Flags.
 *
 * Unlike {@link fetchExistingActiveFlags}, a failed search **throws**. That
 * function is a duplicate-creation guard where returning nothing degrades
 * gracefully; this one answers "is this patient paused right now?" for callers
 * that are about to un-pause them, and a swallowed error there reads as "not
 * paused" and unpauses somebody who is.
 * @agentic @benchmark/core
 */ async function findPatientActivePauseFlags(medplum, patientId) {
    const flags = await medplum.searchResources("Flag", {
        subject: `Patient/${patientId}`,
        status: "active"
    });
    return flags.filter(isPauseFlag);
}
/**
 * Fetches a patient's pause Flags regardless of status, including deactivated
 * ones.
 *
 * Used as provenance evidence: a care plan sitting on hold is only attributable
 * to a pause if the patient was in fact paused at some point. An inactive pause
 * Flag is the durable record of that.
 *
 * Pages until a pause Flag is found rather than reading one default-sized page:
 * patients accumulate Flags (there is a whole duplicate-cleanup command for it),
 * and missing the evidence would wrongly leave a repairable plan on hold.
 * @agentic @benchmark/core
 */ async function findPatientPauseFlagHistory(medplum, patientId) {
    const pauseFlags = [];
    for await (const page of medplum.searchResourcePages("Flag", {
        subject: `Patient/${patientId}`,
        _count: FLAG_SWEEP_PAGE_COUNT
    })){
        pauseFlags.push(...page.filter(isPauseFlag));
        if (pauseFlags.length > 0) {
            break; // presence is the signal; the full list is not needed
        }
    }
    return pauseFlags;
}
/**
 * Fetches every active pause Flag in the project, across all patients.
 *
 * `Flag.code` is not a Medplum search parameter, so the code filter is applied
 * in memory. Pages exhaustively — a partial sweep would silently leave paused
 * patients unreconciled.
 * @agentic @benchmark/core
 */ async function findAllActivePauseFlags(medplum) {
    const flags = [];
    for await (const page of medplum.searchResourcePages("Flag", {
        status: "active",
        _count: FLAG_SWEEP_PAGE_COUNT
    })){
        flags.push(...page.filter(isPauseFlag));
    }
    return flags;
}
/** Fetches existing active flags for a patient. Used to prevent duplicate flag creation. @agentic @benchmark/core */ async function fetchExistingActiveFlags(medplum, patientId) {
    try {
        return await medplum.searchResources("Flag", {
            subject: `Patient/${patientId}`,
            status: "active"
        });
    } catch (error) {
        console.error("Error fetching existing flags:", error);
        // Return empty array on error - will rely on server-side validation
        return [];
    }
}
/** Finds existing CoCM flag for patient. Filters by code in memory since FHIR R4 has no code search. @agentic @benchmark/core */ async function findExistingCoCMFlag(medplum, patientId) {
    try {
        // Search by subject only (code is not a standard search parameter for Flag)
        const flags = await medplum.searchResources("Flag", {
            subject: `Patient/${patientId}`
        });
        // Filter for CoCM flags in memory using resolver
        const cocmFlags = filterCoCMFlags(flags);
        // Return the most recent flag (if multiple exist)
        if (cocmFlags.length > 0) {
            return cocmFlags[0] || null;
        }
        return null;
    } catch (error) {
        // Log error but don't fail - flag search is a side effect
        console.error("Error searching for CoCM flag:", error);
        return null;
    }
}
/** Finds existing minor flag for patient. Filters by code in memory since FHIR R4 has no code search. @agentic @benchmark/core */ async function findExistingMinorFlag(medplum, patientId) {
    try {
        // Search by subject only (code is not a standard search parameter for Flag)
        const flags = await medplum.searchResources("Flag", {
            subject: `Patient/${patientId}`
        });
        // Filter for minor flags in memory using resolver
        const minorFlags = filterMinorFlags(flags);
        // Return the most recent flag (if multiple exist)
        if (minorFlags.length > 0) {
            return minorFlags[0] || null;
        }
        return null;
    } catch (error) {
        // Log error but don't fail - flag search is a side effect
        console.error("Error searching for minor flag:", error);
        return null;
    }
}

/** Creates a Goal Progress Observation resource. @agentic @benchmark/core */ async function createGoalProgress(medplum, observation) {
    return await createResource(medplum, observation);
}
/** Searches for Goal Progress Observations for a specific goal using GAS code system. @agentic @benchmark/core */ async function searchGoalProgress(medplum, goalId, options) {
    const query = {
        focus: `Goal/${goalId}`,
        _sort: options?._sort || "-date"
    };
    // Add count parameter if provided
    if (options?._count) {
        query._count = options._count.toString();
    }
    const bundle = await searchResources(medplum, "Observation", query);
    const allObservations = bundle.entry?.map((e)=>e.resource).filter(Boolean) || [];
    // Filter to only include GAS score observations (client-side filter)
    // The code parameter in FHIR search expects a code value, not a system URL,
    // so we filter client-side instead
    // Note: filter() preserves order, so server-side sort is maintained
    const gasObservations = allObservations.filter((obs)=>{
        const coding = obs.code?.coding?.[0];
        if (!coding || !coding.code || !coding.system) return false;
        // GAS assessment code structure
        return coding.code === GAS_ASSESSMENT_CODE.code && coding.system === GAS_ASSESSMENT_CODE.system;
    });
    // Server-side sort by -date (effectiveDateTime) is already applied and preserved by filter()
    return gasObservations;
}

/** Creates a Goal resource. @agentic @benchmark/core */ async function createGoal(medplum, goal) {
    return await createResource(medplum, goal);
}
/** Updates an existing Goal resource. @agentic @benchmark/core */ async function updateGoal(medplum, goal) {
    return await updateResource(medplum, goal);
}
/** Reads a Goal resource by ID. @agentic @benchmark/core */ async function getGoal(medplum, id) {
    return await readResource(medplum, "Goal", id);
}
/** Searches for Goal resources for a patient. @agentic @benchmark/core */ async function searchGoals(medplum, patientId, options) {
    const query = {
        subject: `Patient/${patientId}`
    };
    // Map status to lifecycle-status search parameter
    if (options?.status) {
        query["lifecycle-status"] = options.status;
    }
    // Map category to category search parameter with system
    if (options?.category) {
        query.category = `${GOAL_CATEGORY_SYSTEM}|${options.category}`;
    }
    // Add include parameters
    if (options?._include) {
        options._include.forEach((include, index)=>{
            query[`_include:${index}`] = include;
        });
    }
    // Add sort parameter
    if (options?._sort) {
        query._sort = options._sort;
    }
    // Add count parameter
    if (options?._count) {
        query._count = options._count.toString();
    }
    const bundle = await searchResources(medplum, "Goal", query);
    return bundle.entry?.map((e)=>e.resource).filter(Boolean) || [];
}
/** Deletes a Goal resource. @agentic @benchmark/core */ async function deleteGoal(medplum, id) {
    return await deleteResource(medplum, "Goal", id);
}

/** Creates an Observation resource. @agentic @benchmark/core */ async function createObservation(medplum, observation) {
    return await createResource(medplum, observation);
}
/** Updates an existing Observation resource. @agentic @benchmark/core */ async function updateObservation(medplum, observation) {
    return await updateResource(medplum, observation);
}
/** Reads an Observation resource by ID. @agentic @benchmark/core */ async function getObservation(medplum, id) {
    return await readResource(medplum, "Observation", id);
}
/** Searches for Observation resources by query. @agentic @benchmark/core */ async function searchObservations(medplum, query) {
    const bundle = await searchResources(medplum, "Observation", query);
    return bundle.entry?.map((e)=>e.resource).filter(Boolean) || [];
}
/** Deletes an Observation resource. @agentic @benchmark/core */ async function deleteObservation(medplum, id) {
    return await deleteResource(medplum, "Observation", id);
}
/**
 * Fetch ALL final survey score Observations for a patient on/before the given
 * ISO instant, paginated to exhaustion. Correctness-critical for baseline
 * (earliest-per-instrument) computation: a single page can truncate a
 * per-instrument extreme when one instrument is high-volume, so this follows the
 * search pages instead of trusting a single `_count` page.
 * @agentic @benchmark/core
 */ async function searchAllFinalSurveyObservations(medplum, patientId, onOrBeforeIso) {
    const results = [];
    for await (const page of medplum.searchResourcePages("Observation", {
        subject: `Patient/${patientId}`,
        category: "survey",
        status: "final",
        date: `le${onOrBeforeIso}`,
        _sort: "date",
        _count: "100"
    })){
        results.push(...page);
    }
    return results;
}

/**
 * Patient Service. Patient resource CRUD operations. Wraps generic FHIR service with Patient-specific logic.
 * @agentic @benchmark/core
 */ // Constants
/** Creates a Patient resource. @agentic @benchmark/core */ async function createPatient(medplum, patient) {
    return await createResource(medplum, patient);
}
/** Updates an existing Patient resource. @agentic @benchmark/core */ async function updatePatient(medplum, patient) {
    return await updateResource(medplum, patient);
}
/** Reads a Patient resource by ID. @agentic @benchmark/core */ async function getPatient(medplum, id) {
    return await readResource(medplum, "Patient", id);
}
/** Searches for Patient resources by query. @agentic @benchmark/core */ async function searchPatients(medplum, query) {
    const bundle = await searchResources(medplum, "Patient", query);
    return bundle.entry?.map((e)=>e.resource).filter(Boolean) || [];
}
/** Deletes a Patient resource. @agentic @benchmark/core */ async function deletePatient(medplum, id) {
    return await deleteResource(medplum, "Patient", id);
}
/** Gets patient service request date from ServiceRequest. @agentic @benchmark/core */ async function getPatientServiceRequestDate(medplum, patientId) {
    try {
        const serviceRequests = await medplum.searchResources("ServiceRequest", {
            subject: `Patient/${patientId}`,
            _sort: "authored",
            _count: QUERY_LIMIT_SINGLE
        });
        return serviceRequests[0]?.authoredOn;
    } catch  {
        return localCalendarDateTodayISO();
    }
}
const REFERRAL_SERVICE_REQUEST_COUNT = "100";
/**
 * Oldest ServiceRequest authored date and requester (same ordering as patient table referral column).
 * @agentic @benchmark/core
 */ async function getPatientReferralInfo(medplum, patientId) {
    try {
        const serviceRequests = await medplum.searchResources("ServiceRequest", {
            subject: `Patient/${patientId}`,
            _sort: "authored",
            _count: REFERRAL_SERVICE_REQUEST_COUNT
        });
        if (serviceRequests.length > 0) {
            const withAuthored = serviceRequests.filter((sr)=>!!sr.authoredOn);
            if (withAuthored.length === 0) {
                return {};
            }
            const oldestServiceRequest = [
                ...withAuthored
            ].sort((a, b)=>new Date(a.authoredOn).getTime() - new Date(b.authoredOn).getTime())[0];
            return {
                date: oldestServiceRequest.authoredOn,
                requesterDisplay: oldestServiceRequest.requester?.display
            };
        }
        try {
            const history = await medplum.readHistory("Patient", patientId);
            if (history.entry && history.entry.length > 0) {
                const originalVersion = history.entry[history.entry.length - 1];
                if (originalVersion.resource?.meta?.lastUpdated) {
                    return {
                        date: originalVersion.resource.meta.lastUpdated
                    };
                }
            }
        } catch  {
        // Ignore history errors
        }
        return {};
    } catch  {
        return {};
    }
}

/**
 * Patient Form Data Service. Loads patient-related resources for form data. Pure data access layer.
 * @agentic @benchmark/core
 */ // Constants
/** Loads all resources needed for patient form data. @agentic @benchmark/core */ async function loadPatientFormDataContext(medplum, patient) {
    try {
        // Fetch all related resources in parallel
        const [coverages, relatedPersons, careTeams, conditions, referralDate] = await Promise.all([
            medplum.searchResources("Coverage", {
                beneficiary: `Patient/${patient.id}`
            }),
            medplum.searchResources("RelatedPerson", {
                patient: `Patient/${patient.id}`
            }),
            medplum.searchResources("CareTeam", {
                subject: `Patient/${patient.id}`
            }),
            patient.id ? searchProblemListConditions(medplum, patient.id).catch(()=>[]) : Promise.resolve([]),
            patient.id ? getPatientServiceRequestDate(medplum, patient.id).catch(()=>localCalendarDateTodayISO()) : Promise.resolve(localCalendarDateTodayISO())
        ]);
        // Resolve primary care provider if needed
        let practitionerRole;
        const pcpRef = getPatientReferringProvider(patient);
        if (pcpRef?.reference?.startsWith("Practitioner/")) {
            try {
                const roles = await medplum.searchResources("PractitionerRole", {
                    practitioner: pcpRef.reference,
                    _count: QUERY_LIMIT_SINGLE.toString()
                });
                if (roles.length > 0) {
                    practitionerRole = roles[0];
                }
            } catch (error) {
                console.error("Error searching for PractitionerRole:", error);
            }
        } else if (pcpRef?.reference?.startsWith("PractitionerRole/")) {
            try {
                const roleId = pcpRef.reference.replace("PractitionerRole/", "");
                practitionerRole = await medplum.readResource("PractitionerRole", roleId);
            } catch (error) {
                console.error("Error reading PractitionerRole:", error);
            }
        }
        return {
            patient,
            coverages,
            relatedPersons,
            careTeams,
            conditions,
            practitionerRole,
            referralDate
        };
    } catch (error) {
        console.error("Error loading patient form data context:", error);
        // Return context with empty arrays on error
        return {
            patient,
            coverages: [],
            relatedPersons: [],
            careTeams: [],
            conditions: [],
            referralDate: localCalendarDateTodayISO()
        };
    }
}

/** Fetches practitioner role for a given practitioner. Optionally filters by organization. @agentic @benchmark/core */ async function fetchPractitionerRole(medplum, practitionerId, options) {
    const searchParams = {
        practitioner: `Practitioner/${practitionerId}`,
        active: "true",
        _count: QUERY_LIMIT_SINGLE.toString(),
        _sort: "-_lastUpdated"
    };
    if (options?.organizationId) {
        searchParams.organization = `Organization/${options.organizationId}`;
    }
    const roles = await medplum.searchResources("PractitionerRole", searchParams);
    if (!roles || roles.length === 0) {
        throw new Error(`No active PractitionerRole found for practitioner ${practitionerId}`);
    }
    return roles[0];
}
/**
 * Fetches all active practitioner roles for a given practitioner, most recently
 * updated first. Unlike {@link fetchPractitionerRole}, this returns the full set
 * (not just the primary) and resolves to an empty array when none are found
 * rather than throwing. Optionally filters by organization.
 * @agentic @benchmark/core
 */ async function fetchPractitionerRoles(medplum, practitionerId, options) {
    const count = options?.count ?? PRACTITIONER_ROLE_CURRENT_USER_COUNT;
    const searchParams = {
        practitioner: `Practitioner/${practitionerId}`,
        active: "true",
        _count: count.toString(),
        _sort: "-_lastUpdated"
    };
    if (options?.organizationId) {
        searchParams.organization = `Organization/${options.organizationId}`;
    }
    return medplum.searchResources("PractitionerRole", searchParams);
}
/** Fetches the current user's Benchmark Health practitioner role. @agentic @benchmark/core */ async function fetchBenchmarkPractitionerRole(medplum, practitionerId, options) {
    const benchmarkOrgId = options.benchmarkOrganizationId;
    if (!benchmarkOrgId) {
        throw new Error("fetchBenchmarkPractitionerRole: benchmarkOrganizationId is required");
    }
    return fetchPractitionerRole(medplum, practitionerId, {
        organizationId: benchmarkOrgId
    });
}
/**
 * Fetches all of the current user's active Benchmark Health practitioner roles,
 * most recently updated first. Returns an empty array when
 * `benchmarkOrganizationId` is missing/empty or no roles exist.
 * @agentic @benchmark/core
 */ async function fetchBenchmarkPractitionerRoles(medplum, practitionerId, options) {
    const benchmarkOrgId = options.benchmarkOrganizationId;
    if (!benchmarkOrgId) {
        return [];
    }
    return fetchPractitionerRoles(medplum, practitionerId, {
        organizationId: benchmarkOrgId,
        count: options.count
    });
}
/** Finds practitioner role by reference. Compatibility wrapper for old signature. @agentic @benchmark/core */ async function findPractitionerRole(medplum, options) {
    const match = options.reference.match(/^Practitioner\/(.+)$/);
    if (!match) {
        return undefined;
    }
    const practitionerId = match[1];
    try {
        return await fetchPractitionerRole(medplum, practitionerId, {
            organizationId: options.organizationId
        });
    } catch  {
        return undefined;
    }
}
/**
 * Finds the current user's Benchmark Health practitioner role by reference.
 * Returns undefined when `benchmarkOrganizationId` is missing or empty.
 * @agentic @benchmark/core
 */ async function findBenchmarkPractitionerRole(medplum, options) {
    const benchmarkOrgId = options.benchmarkOrganizationId;
    if (!benchmarkOrgId) {
        return undefined;
    }
    return findPractitionerRole(medplum, {
        reference: options.reference,
        organizationId: benchmarkOrgId
    });
}

/**
 * Get the Program Selection Observation for a given patient + intake care plan.
 * Uses the idempotency identifier for lookup.
 * @agentic @benchmark/core
 */ async function getProgramSelectionObservation(medplum, patientId, intakeCarePlanId) {
    const identifierValue = `${patientId}|${intakeCarePlanId}`;
    const results = await medplum.searchResources("Observation", {
        identifier: `${PROGRAM_SELECTION_IDENTIFIER_SYSTEM}|${identifierValue}`,
        _count: 1
    });
    return results.length > 0 ? results[0] : undefined;
}
/**
 * Operational care plan types that count toward the "one active plan" constraint.
 * @agentic @benchmark/core
 */ const OPERATIONAL_PLAN_TYPES = [
    "triage",
    "cocm"
];
/**
 * Get all active operational care plans for a patient.
 * Operational plans are triage or cocm (not intake).
 * @agentic @benchmark/core
 */ async function getActiveOperationalCarePlans(medplum, patientId) {
    const results = await medplum.searchResources("CarePlan", {
        subject: `Patient/${patientId}`,
        status: "active,draft",
        _sort: "-date",
        _count: 100
    });
    return results.filter((plan)=>{
        const categoryCode = plan.category?.[0]?.coding?.find((c)=>c.system === CARE_PLAN_CATEGORY_SYSTEM$1)?.code;
        return categoryCode !== undefined && OPERATIONAL_PLAN_TYPES.includes(categoryCode);
    });
}

/**
 * Fetch progress log Communications for a Task, newest first.
 * @agentic @benchmark/core
 */ async function getProgressLogs(medplum, taskId) {
    return medplum.searchResources("Communication", {
        "based-on": `Task/${taskId}`,
        category: `${PROGRESS_LOG_CATEGORY_SYSTEM}|`,
        // Medplum returns entered-in-error resources unless excluded; retired
        // admin-note Communications must never render as progress history.
        "status:not": "entered-in-error",
        _sort: "-sent",
        _count: "50"
    });
}
/**
 * Count progress log Communications for a Task.
 * @agentic @benchmark/core
 */ async function getProgressLogCount(medplum, taskId) {
    const logs = await medplum.searchResources("Communication", {
        "based-on": `Task/${taskId}`,
        category: `${PROGRESS_LOG_CATEGORY_SYSTEM}|`,
        "status:not": "entered-in-error",
        _count: "0",
        _total: "accurate"
    });
    return logs.length;
}

/** Gets questionnaire configuration by URL. @agentic @benchmark/core */ const getQuestionnaireConfig = (url)=>Object.values(STANDARD_QUESTIONNAIRES).find((config)=>config.url === url);
/** Type guard to check if a string is a valid questionnaire key. @agentic @benchmark/core */ const isQuestionnaireKey = (key)=>key in STANDARD_QUESTIONNAIRES;
/** Gets enhanced questionnaire configuration with questions included. @agentic @benchmark/core */ const getEnhancedQuestionnaireConfig = (url)=>{
    const baseConfig = getQuestionnaireConfig(url);
    if (!baseConfig) return null;
    // Items come from the single canonical registry source (no per-call map).
    return {
        ...baseConfig,
        questions: getQuestionnaireItems(url)
    };
};
/** Gets set of non-scorable linkIds for a questionnaire. @agentic @benchmark/core */ const getNonScorableLinkIds = (questionnaireUrl)=>{
    const config = getEnhancedQuestionnaireConfig(questionnaireUrl);
    if (!config?.questions) return new Set();
    return new Set(config.questions.filter((q)=>q.scorable === false).map((q)=>q.linkId));
};
/** Filters responses to only include scorable items. @agentic @benchmark/core */ const filterScorableResponses = (responses, questionnaireUrl)=>{
    const nonScorableLinkIds = getNonScorableLinkIds(questionnaireUrl);
    return responses.filter((r)=>!nonScorableLinkIds.has(r.linkId));
};
/** Generates enhanced select options for questionnaires. @agentic @benchmark/core */ const generateQuestionnaireSelectOptions = ()=>Object.values(STANDARD_QUESTIONNAIRES).map((config)=>({
            value: config.url,
            label: config.title,
            description: config.name
        }));
/** Converts FHIR QuestionnaireResponse items to internal format. Flattens nested structure. @agentic @benchmark/core */ function flattenResponseItems(items) {
    const result = [];
    for (const item of items){
        // Handle nested items recursively
        if (item.item && item.item.length > 0) {
            result.push(...flattenResponseItems(item.item));
        }
        // Handle answers
        if (item.answer && item.answer.length > 0) {
            const answer = item.answer[0];
            if (answer.valueString !== undefined) {
                result.push({
                    linkId: item.linkId,
                    valueString: answer.valueString
                });
            } else if (answer.valueInteger !== undefined) {
                result.push({
                    linkId: item.linkId,
                    value: answer.valueInteger,
                    coding: answer.valueCoding ? {
                        code: answer.valueCoding.code || "",
                        display: answer.valueCoding.display || ""
                    } : undefined
                });
            }
        }
    }
    return result;
}
/** Extracts LOINC codes dynamically from a FHIR Questionnaire resource. @agentic @benchmark/core */ const extractQuestionnaireLoincCodes = (questionnaire)=>{
    // Extract panel LOINC code from questionnaire.code
    // questionnaire.code is CodeableConcept[], each with coding: Coding[]
    const panelCodeableConcept = questionnaire.code?.find((codeableConcept)=>codeableConcept.coding?.some((coding)=>coding.system === "http://loinc.org"));
    const panelCode = panelCodeableConcept?.coding?.find((coding)=>coding.system === "http://loinc.org")?.code;
    // Extract score LOINC code from questionnaire items (look for score items)
    const scoreItem = questionnaire.item?.find((item)=>item.type === "decimal" && (item.text?.toLowerCase().includes("score") || item.text?.toLowerCase().includes("total")));
    // scoreItem.code is also CodeableConcept[]
    const scoreCodeableConcept = scoreItem?.code?.find((codeableConcept)=>codeableConcept.coding?.some((coding)=>coding.system === "http://loinc.org"));
    const scoreCode = scoreCodeableConcept?.coding?.find((coding)=>coding.system === "http://loinc.org")?.code;
    return {
        panelCode,
        scoreCode
    };
};

/**
 * ServiceRequest Service
 *
 * Query functions for need-based ServiceRequests linked to operational CarePlans.
 * @agentic @benchmark/core
 */ /**
 * Fetch need-based ServiceRequests for a CarePlan.
 * @agentic @benchmark/core
 */ async function getCarePlanServiceRequests(medplum, carePlanId) {
    return medplum.searchResources("ServiceRequest", {
        "based-on": `CarePlan/${carePlanId}`,
        _sort: "-_lastUpdated"
    });
}

/**
 * Medplum Query Count Constants
 *
 * Centralized documentation of all `_count` parameters used in Medplum FHIR queries.
 * This provides visibility into query limits that may unintentionally restrict
 * options or lists in the UI.
 *
 * Every query with a `_count` parameter should reference a constant from this file.
 *
 * Convention:
 * - Purpose-based constants: QUERY_LIMIT_SINGLE
 * - Resource-specific constants: <RESOURCE>_<PURPOSE>_COUNT
 *   - OPTIONS: dropdowns/selects
 *   - FORM: form searches
 *   - PAGINATION: paginated queries
 *   - BATCH_SMALL: small batch operations
 */ // ============================================================================
// Purpose-Based Limits
// ============================================================================
/** Paginated outbound referrals linked to a HealthcareService performer. @agentic @benchmark/core */ const HEALTHCARE_SERVICE_REFERRAL_HISTORY_COUNT = 25;

/** Slim ServiceRequest fields returned for directory referral history lists. @agentic @benchmark/core */ const HEALTHCARE_SERVICE_REFERRAL_HISTORY_ELEMENTS = "id,status,authoredOn,subject,code,performer,basedOn,meta";
/**
 * Builds FHIR search parameters for referral history on a HealthcareService.
 * @agentic @benchmark/core
 */ const buildHealthcareServiceReferralHistorySearchParams = (healthcareServiceId, options = {})=>{
    const pageSize = options.pageSize ?? HEALTHCARE_SERVICE_REFERRAL_HISTORY_COUNT;
    const page = options.page ?? 1;
    const offset = (page - 1) * pageSize;
    return {
        performer: `HealthcareService/${healthcareServiceId}`,
        category: `${SERVICE_REQUEST_CATEGORY.OUTBOUND_REFERRAL.system}|${SERVICE_REQUEST_CATEGORY.OUTBOUND_REFERRAL.code}`,
        _sort: "-authored",
        _count: String(pageSize),
        _offset: String(offset),
        _total: "accurate",
        _elements: HEALTHCARE_SERVICE_REFERRAL_HISTORY_ELEMENTS
    };
};
/**
 * Extracts ServiceRequest resources from a search bundle.
 * @agentic @benchmark/core
 */ const extractServiceRequestsFromBundle = (bundle)=>(bundle.entry ?? []).map((entry)=>entry.resource).filter((resource)=>resource?.resourceType === "ServiceRequest");
/**
 * Searches outbound ServiceRequests where this HealthcareService is the performer.
 * @agentic @benchmark/core
 */ const searchHealthcareServiceReferralHistory = async (medplum, healthcareServiceId, options)=>{
    const bundle = await medplum.search("ServiceRequest", buildHealthcareServiceReferralHistorySearchParams(healthcareServiceId, options));
    return {
        serviceRequests: extractServiceRequestsFromBundle(bundle),
        total: bundle.total ?? 0
    };
};

/**
 * CareTeam Participant Resolver
 *
 * Pure functions for extracting participant references from CareTeam resources
 * by SNOMED role code. Used by the assignment resolver and available for
 * general CareTeam lookups.
 * @agentic @benchmark/core
 */ /** SNOMED codes for CareTeam participant roles. @agentic @benchmark/core */ const CARE_TEAM_SNOMED_ROLES = {
    LCSW: "224598009"};
/**
 * Resolve a CareTeam participant's member reference by SNOMED role code.
 * @agentic @benchmark/core
 */ const resolveCareTeamPractitionerRole = (careTeam, snomedRoleCode)=>{
    const participant = careTeam.participant?.find((p)=>p.role?.some((role)=>role.coding?.some((c)=>c.code === snomedRoleCode)));
    if (!participant?.member?.reference) return undefined;
    return {
        reference: participant.member.reference,
        display: participant.member.display ?? ""
    };
};
/**
 * Resolve the LCSW participant from a CareTeam.
 * @agentic @benchmark/core
 */ const resolveCareTeamLCSW = (careTeam)=>resolveCareTeamPractitionerRole(careTeam, CARE_TEAM_SNOMED_ROLES.LCSW);

/**
 * Find a PractitionerRole in the registry that matches the given business role code.
 */ const findRoleInRegistry = (registry, roleCode)=>registry.find((pr)=>pr.code?.some((cc)=>cc.coding?.some((c)=>c.code === roleCode)));
/**
 * Extract PractitionerRole ID from resource. Returns the raw ID, not a full reference.
 */ const extractRoleId = (role)=>role.id;
/**
 * Build a display name for a PractitionerRole, falling back to the role code display.
 */ const extractRoleDisplay = (role)=>{
    const practitionerDisplay = role.practitioner?.display;
    if (practitionerDisplay) return practitionerDisplay;
    const roleCodeDisplay = role.code?.[0]?.coding?.[0]?.display;
    if (roleCodeDisplay) return roleCodeDisplay;
    return "";
};
/**
 * Resolve Task.owner for a given activity based on its assignment mode.
 *
 * Returns `ownerRef` as a bare PractitionerRole ID (not a full FHIR reference path).
 * Callers should construct the full reference as `PractitionerRole/${ownerRef}`.
 * When `ownerRef` is undefined, the task should be created without an owner (queue task).
 * @agentic @benchmark/core
 */ const resolveTaskAssignment = (context)=>{
    const mode = getActivityAssignmentMode(context.activityCode);
    switch(mode){
        case "unassigned-queue":
            return {
                mode
            };
        case "role-restricted":
            {
                const roleCode = getActivityAssignmentRoleCode(context.activityCode);
                if (!roleCode || !context.roleRegistry?.length) {
                    return {
                        mode
                    };
                }
                const matchedRole = findRoleInRegistry(context.roleRegistry, roleCode);
                if (!matchedRole) return {
                    mode
                };
                const id = extractRoleId(matchedRole);
                if (!id) return {
                    mode
                };
                return {
                    mode,
                    ownerRef: id,
                    ownerDisplay: extractRoleDisplay(matchedRole)
                };
            }
        case "care-team-assigned":
            {
                if (!context.patientCareTeam) return {
                    mode
                };
                const lcswRef = resolveCareTeamLCSW(context.patientCareTeam);
                if (!lcswRef) return {
                    mode
                };
                const practRoleId = lcswRef.reference.replace("PractitionerRole/", "");
                return {
                    mode,
                    ownerRef: practRoleId,
                    ownerDisplay: lcswRef.display
                };
            }
        case "primary-advocate":
            {
                const advocate = context.patientPrimaryAdvocate;
                if (!advocate?.ref) return {
                    mode
                };
                return {
                    mode,
                    ownerRef: advocate.ref,
                    ownerDisplay: advocate.display
                };
            }
        case "current-user":
            {
                if (!context.currentUserRole?.id) return {
                    mode
                };
                return {
                    mode,
                    ownerRef: context.currentUserRole.id,
                    ownerDisplay: extractRoleDisplay(context.currentUserRole)
                };
            }
        default:
            return {
                mode: "current-user"
            };
    }
};

/**
 * Task Owner Business Rules
 *
 * Pure functions that apply business-rule overrides to the assignment-mode
 * default. Rules are evaluated in order; first match wins.
 * @agentic @benchmark/core
 */ /**
 * Applies business-rule overrides to the resolved assignment result.
 *
 * Current rules:
 * - clinician-contact-only flag redirects unassigned-queue → primary-advocate
 *   (only when a primary advocate is available).
 * @agentic @benchmark/core
 */ const applyOwnerBusinessRules = (result, context)=>{
    if (result.mode === "unassigned-queue" && context.patientFlagCodes.includes("clinician-contact-only") && context.patientPrimaryAdvocate?.ref) {
        return {
            mode: "primary-advocate",
            ownerRef: context.patientPrimaryAdvocate.ref,
            ownerDisplay: context.patientPrimaryAdvocate.display,
            appliedRule: {
                rule: "clinician-contact-only",
                originalMode: result.mode
            }
        };
    }
    return result;
};

/**
 * Builds the primary advocate reference from patient data.
 * Prefers the typed extension match, falls back to GP index.
 * @agentic @benchmark/core
 */ const buildPrimaryAdvocate = (patient, activityCode)=>{
    const typedAdvocate = getPatientBenchmarkLCSW(patient);
    const gpIndex = getActivityAssignmentGPIndex(activityCode) ?? 1;
    const gpFallback = patient.generalPractitioner?.[gpIndex];
    const advocateRef = typedAdvocate ?? gpFallback;
    if (!advocateRef?.reference) return undefined;
    const rawRef = advocateRef.reference.replace(/^PractitionerRole\//, "");
    if (!rawRef) return undefined;
    return {
        ref: rawRef,
        display: advocateRef.display || "Primary Advocate"
    };
};
/** Benchmark Care Team identifiers. @agentic @benchmark/core */ const BENCHMARK_CARE_TEAM_NAME = "Benchmark Care Team";
const BENCHMARK_CARE_TEAM_CATEGORY_CODE = "LA28865-6";
/**
 * Fetches shared assignment context (care team + flag codes) for a patient.
 * @agentic @benchmark/core
 */ const fetchAssignmentContext = async (medplum, patientId)=>{
    const [careTeams, flags] = await Promise.all([
        medplum.searchResources("CareTeam", {
            subject: `Patient/${patientId}`,
            status: "active"
        }),
        medplum.searchResources("Flag", {
            subject: `Patient/${patientId}`,
            status: "active"
        })
    ]);
    const benchmarkCareTeam = careTeams.find((ct)=>ct.name === BENCHMARK_CARE_TEAM_NAME || ct.category?.[0]?.coding?.[0]?.code === BENCHMARK_CARE_TEAM_CATEGORY_CODE);
    const flagCodes = flags.map((f)=>f.code?.coding?.[0]?.code).filter((c)=>!!c);
    return {
        benchmarkCareTeam,
        flagCodes
    };
};
/**
 * Resolves Task.owner for an activity through the full waterfall:
 * 1. Assignment mode default (via resolveTaskAssignment)
 * 2. Business rule overrides (clinician-contact-only, etc.)
 *
 * Returns OwnerRef or undefined for unassigned tasks.
 * @agentic @benchmark/core
 */ const resolveActivityOwner = async (medplum, input)=>{
    const { activityCode, patient, currentUserRole } = input;
    const patientPrimaryAdvocate = buildPrimaryAdvocate(patient, activityCode);
    const { benchmarkCareTeam, flagCodes } = patient.id ? await fetchAssignmentContext(medplum, patient.id) : {
        benchmarkCareTeam: undefined,
        flagCodes: []
    };
    const assignmentResult = resolveTaskAssignment({
        activityCode,
        currentUserRole,
        patientCareTeam: benchmarkCareTeam,
        patientPrimaryAdvocate
    });
    const adjustedResult = applyOwnerBusinessRules(assignmentResult, {
        patientFlagCodes: flagCodes,
        patientPrimaryAdvocate
    });
    if (!adjustedResult.ownerRef) return undefined;
    return {
        ref: adjustedResult.ownerRef,
        display: adjustedResult.ownerDisplay || "",
        appliedRule: adjustedResult.appliedRule
    };
};
/**
 * Resolves TaskOwnerOverrides for all activities in a care plan.
 * Fetches patient context once, then resolves each activity in a single pass.
 * @agentic @benchmark/core
 */ const resolveOwnerOverridesForPlan = async (medplum, input)=>{
    const { activityCodes, patient, currentUserRole } = input;
    const { benchmarkCareTeam, flagCodes } = patient.id ? await fetchAssignmentContext(medplum, patient.id) : {
        benchmarkCareTeam: undefined,
        flagCodes: []
    };
    const overrides = {};
    for (const code of activityCodes){
        const patientPrimaryAdvocate = buildPrimaryAdvocate(patient, code);
        const assignmentResult = resolveTaskAssignment({
            activityCode: code,
            currentUserRole,
            patientCareTeam: benchmarkCareTeam,
            patientPrimaryAdvocate
        });
        const adjustedResult = applyOwnerBusinessRules(assignmentResult, {
            patientFlagCodes: flagCodes,
            patientPrimaryAdvocate
        });
        if (adjustedResult.ownerRef) {
            overrides[code] = {
                ref: adjustedResult.ownerRef,
                display: adjustedResult.ownerDisplay || ""
            };
        }
    }
    return overrides;
};

export { ADAPTIVE_SCREENER_ACK_AT_EXTENSION, ADAPTIVE_SCREENER_ACK_BY_EXTENSION, ADAPTIVE_SCREENER_BASELINE_EXTENSION, ADAPTIVE_SCREENER_COMPOSITION_EXTENSION, ADAPTIVE_SCREENER_INSTRUMENT_ORDER, ADAPTIVE_SCREENER_QUESTIONNAIRE_ORDER, ADAPTIVE_SCREENER_REASON_EXTENSION, ADAPTIVE_SCREENER_SESSION_SYSTEM, ADAPTIVE_SCREENER_SETTLEMENT_EXTENSION, ADAPTIVE_SCREENER_SKIPPED_ITEMS_EXTENSION, ADAPTIVE_SECTION_OUTCOMES, ADAPTIVE_SETTLEMENT_READBACK_LABELS, ANXIETY_SHORT_CHECK_CUTOFF, AdaptiveScreenerIntegrityError, CARE_TIME_MAX_PER_MONTH, CARE_TIME_PAGE_SIZE, GATED_SECTION_KEYS, HEALTHCARE_SERVICE_REFERRAL_HISTORY_ELEMENTS, PATIENT_SCREENER_LINK_TAG_CODE, SAFETY_ITEM_LINK_ID, SCREENER_OBSERVATION_ROLE, SCREENER_OBSERVATION_ROLE_SYSTEM, SUBMISSION_SOURCE_SYSTEM, TRIGGER_ADDED_SECTIONS, adaptiveCompletionScoreObservationQuery, answersByLinkIdFromItems, buildAdaptiveCompletionScoreObservation, buildCanonicalCoverageAreaLocation, buildCarePlanTasksMap, buildDueTodayTaskSearchParams, buildHealthcareServiceReferralHistorySearchParams, buildNotificationTaskSearchParams, buildOperationalTaskExclusionParams, buildScreenerScoreObservation, buildSelectionRecord, buildTaskDueDateFilterSearchParams, buildUnreadNotificationTaskSearchParams, canRecordAdaptiveScreener, canRecordScreening, collectBundleResourceTypes, composeAdaptiveScreener, composePersistedAdaptiveSession, computeCoCMBillingPacket, computeCoCMEligibility, createAppointment, createCommunication, createCondition, createCoverage, createEncounter, createGoal, createGoalProgress, createObservation, createPatient, createResource, createResourceIfNoneExist, deleteAppointment, deleteCommunication, deleteCoverage, deleteEncounter, deleteGoal, deleteObservation, deletePatient, deleteResource, deriveAdaptiveSectionOutcome, deriveFollowUpSelectionMemory, deriveSafetySignalFired, deriveSafetySignalState, deriveScreenerMemoryFromResponses, enrichCoverageAreasWithLocations, ensureCoverageAreaLocation, ensureCoverageAreaLocations, evaluateQuestionnaireCompletion, extractQuestionnaireLoincCodes, extractServiceRequestsFromBundle, fetchBenchmarkPractitionerRole, fetchBenchmarkPractitionerRoles, fetchExistingActiveFlags, fetchPatientActivePlanLabels, fetchPatientLatestAssessments, fetchPatientNextOpenTask, fetchPractitionerRole, fetchPractitionerRoles, fetchTranscriptForComposition, filterScorableResponses, findAllActivePauseFlags, findAllOnHoldCarePlans, findBenchmarkPractitionerRole, findCareTimeForCommunication, findCoCMBillingPacket, findCoCMBillingSummary, findCoCMChargeItemsForMonth, findDefaultCarePlanForCareTime, findDraftEncounterForPatient, findDroppedOpenedSections, findEarliestIntakeEncounter, findExistingCoCMFlag, findExistingMinorFlag, findMostRecentClinicalIntakeEncounter, findMostRecentIntakeEncounter, findOpenTasksForPatient, findPatientActivePauseFlags, findPatientPauseFlagHistory, findPractitionerRole, findRecentDuplicateIntakeEncounter, findUnOpenedRegressions, flattenResponseItems, formatTaskDueDateSearchDate, generateQuestionnaireSelectOptions, getActiveCarePlans, getActiveOperationalCarePlans, getActivityTasks, getAppointment, getCarePlanByType, getCarePlanServiceRequests, getCareTimeForTask, getCommunication, getCoverage, getCoverageAreaLocationReference, getEarliestCoCMEpisodeStart, getEncounter, getEnhancedQuestionnaireConfig, getGoal, getNonScorableLinkIds, getObservation, getOnHoldCarePlans, getPatient, getPatientEncounterBillingSignals, getPatientReferralInfo, getPatientServiceRequestDate, getPractitionerRoleAttribution, getPriorBilledCodeHistory, getProgramSelectionObservation, getProgressLogCount, getProgressLogs, getQuestionnaireConfig, getTaskDueDateDayToken, getTaskDueDateRelativePosition, getTaskDueDateSearchRange, hasActiveTriage, hasBillable90791, hasCompletedIntake, hasGeneratedCadencedFollowups, insertTriggeredSections, isAdaptiveSafetyObligationSatisfied, isExplicitSettlement, isGatedSection, isQuestionnaireKey, isSafetyItemPositive, isScoredOutcome, isScreenerOffLocked, isSectionSettled, isSelfHarmPositive, loadEncounterFormDataContext, loadPatientFormDataContext, mapResponsesToPriorSittings, markCompositionUsed, numericAnswerValue, observationScreenerRole, outcomeFromSettlementStatus, outcomeHasAnsweredContent, outcomeOpensSection, patientDeclineOption, performerDisplayForRole, readAdaptiveScreenerMarker, readResource, recordBlockedReason, resolveActivityOwner, resolveCoCMBillingSummaryAttribution, resolveCoCMConsent, resolveFollowUpSelection, resolveIntakeCallCareTimeLink, resolveOwnerOverridesForPlan, restoreAdaptiveScreenerState, screenerScoreObservationQuery, searchAllFinalSurveyObservations, searchAllIntakeCompositions, searchAppointments, searchCareTimeByIds, searchCareTimeByPatient, searchCareTimeByPatientAndMonth, searchCoCMBillingSummariesByPatient, searchCommunications, searchConditions, searchCoverages, searchEncounters, searchGoalProgress, searchGoals, searchHealthcareServiceReferralHistory, searchLatestIntakeComposition, searchObservations, searchPatients, searchProblemListConditions, searchResources, shouldRevealAnxietyFullSet, shouldRevealAnxietyFullSetFromState, sortByAdaptiveScreenerAdministrationOrder, submitBundle, sumBillableCareTimeMinutes, tagAdaptiveScreenerResponse, updateAppointment, updateCarePlan, updateCommunication, updateCondition, updateCoverage, updateEncounter, updateGoal, updateObservation, updatePatient, updateResource, upsertResource };
